/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- Firestore batch operations require dynamic typing; loop indices kept for clarity */
import { z } from 'zod'
import { format } from 'date-fns'
import { saveFiscalReturn, loadLKTokensFromCache, checkLKTokens, setNewLKTokens, saveFiscalOperation } from '../lightkassa'
import * as Sentry from "@sentry/react-native"
import { saveReturn, refreshCredentials, saveSale, getActiveShift, extractCash, closeCashboxShift } from '../wipon'
import { functions as Functions, auth as Auth, rootDb as RootDb, fieldValue as FieldValue, fieldPath as FieldPath, storage as Storage, emailAuthProvider as EmailAuthProvider, getRemoteConfigValue as GetRemoteConfigValue, FetchSignInMethodsForEmail as fetchSignInMethodsForEmail } from './firebaseFirestore'
import { planAggregations } from './aggregationPlan'
// Cycle-safe sanitizer for debug JSON.stringify of dual-write payloads — entities carry a live
// native doc.ref (cyclic on iOS) that the local sanitizeForFirestoreDeep keeps and can't guard.
import { sanitizeForFirestore } from './productSanitize'
import type {
  FirestoreDb,
  FirestoreDocumentRef,
  FirestoreQuery,
  Organization,
  Product,
  Store,
  Transaction,
  Spending,
  CashboxTransaction,
  ProductMovement,
  ProductCategory,
  WhereCondition,
  AggregationConfig,
  Return,
  GroupedData,
} from './firestore.types'
import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import {
  toDate as convertToDate,
  filterUndefined,
  shouldUseSupabase,
  createMockRef,
  convertTimestamp,
  transformApiItem,
  snapshotExists,
  type TimestampLike,
} from './types'
import { getFirestoreRef, commitBatchTolerant, runIdempotentDelete } from './firestoreRefUtils'
import { timestampLikeToFirestore } from './timestampLikeToFirestore'
import { bestEffortDualWrite, bestEffortDualWriteDelete } from './dualWrite'
import { constantsItemLocation, userActiveLocation } from './deletedEntityLocation'
import i18n from 'i18next'
import { dateRangeWithTimezoneAndAutoclose, getLocalStoreDate, getDailyStatID, dailyStatIDForRecord, dailyStatIDForRecordOrNull, type DateLike } from '../../utils/timezoneFunctions'
import { parseDecimalValue } from '../../utils/ingredientQuantity'
import { parseMoneyInput } from '../../utils/cartPricing'
import { sendBulkStockMirror } from './stockMirror'
import { manufactureIngredientPayload } from './manufacturePayload'
import {
  cumulativeRefundExceededError,
  cumulativeRefundExceedsSale,
  priorReturnedTotal,
  refundableSaleTotal,
  type PriorReturnFields,
  type RefundableSaleFields,
} from './returnLineAmount'
import { dbAtom, organizationAtom, productsDictAtom, storesDictAtom, userIdAtom, constantsAtom } from '../AtomsProvider'
import { getDefaultStore } from 'jotai'
import { reportFirestoreOnlyWriterUsage } from '../analytics/firestoreOnlyWriterUsage'
import { ComplexSalesBusinessTypes, ONBOARDING_CHECKLIST } from '../../utils/onboardingConfig'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { DEFAULT_UNITS, MODULES } = require('../../utils/organizationTypes') as {
  DEFAULT_UNITS: Array<{ id: string; name?: string; shortName?: string }>
  MODULES: Array<{ id: string }>
}
import * as Localization from 'expo-localization'
import { WORKING_HOURS } from '../../utils/workingHoursConstants'
import timezones from '../../utils/timezones'
import { ALL_PERMISSIONS } from '../../utils/permissionsConstants'
import { STATIC_FIELDS } from '../../utils/funnelConstants'
import { WHATSAPP_OPT_IN_AT_FIELD, WHATSAPP_OPT_IN_FIELD, buildWhatsappOnboardingConsent } from '../../utils/whatsappOnboardingConsent'
import { deleteWorkingHourPaymentTransaction, saveNewWorkingHourPaymentTransaction } from './workingHours'
import { dualWriteUpdate } from './updates'
import { api } from '../API/apiClient'
import { readWholeList, newestFirst } from '../API/readWholeList'
import {
  createCashboxTransactionsApi,
  createSalesApi,
  type CreateCashboxTransaction,
  type IdWindowPage,
  type SupplyOrderResponse,
  type UpdateCashboxTransaction,
} from '@atlas/api-client'
import { assertWWriterAvailable, writeW, type WAckDisposition } from '../Write/writeW'
import { buildOutboxRequest } from '../Write/outboxRequest'
import {
  LOCAL_EFFECTS_VERSION,
  assertLocalEffects,
  businessDayOfDailyStatId,
  isoInstantOf,
  rawIncrements,
  toDisplayJson,
  toDisplayObject,
  type DailyStatEffect,
  type LocalEffects,
} from '../Write/localEffects'
import type { JsonPrimitive } from '../Write/outboxEntry'
import {
  salesApi,
  productsApi,
  spendingsApi,
  transactionsApi,
  storesApi,
  productCategoriesApi,
  suppliersApi,
  walletsApi,
  paymentMethodsApi,
  rolesApi,
  productUnitsApi,
  cashboxTransactionsApi,
  usersApi,
  auditLogApi,
  savedCartsApi,
  checklistsApi,
  complexSalesApi,
  complexSaleTemplatesApi,
  supplyOrdersApi,
  manufacturesApi,
  printTemplatesApi,
  productTagsApi,
  settingsApi,
  ordersApi,
  priceTypesApi,
  organizationsApi,
  spendingCategoriesApi,
  discountOptionsApi,
  writeOffReasonsApi,
  returnReasonsApi,
  purchaseMethodsApi,
  saleSettingsApi,
  productModifiersApi,
  recurringSpendingsApi,
  typedClient,
} from '../API/typedApiClient'
import { supplyOrderIsCompleted } from '../../utils/orderStatus'
import { readSupplyOrderDate } from '../../utils/supplyOrderDate'
import { withSupplyOrderSupplierKey } from '../../utils/supplyOrderLine'
import { buildWriteBackDelta } from './writeBackDelta'
import { localizedDefaultCashName } from '../../utils/defaultPaymentMethod'

// Import from typed domain modules (these replace implementations below)
import {
  getSalesOnDateRange as getSalesOnDateRangeTyped,
  getClientStatisticsAggregated as getClientStatisticsAggregatedTyped,
  getClientTopProductsAggregated as getClientTopProductsAggregatedTyped,
  getClientSalesSummary as getClientSalesSummaryTyped,
  dailyStatIncrementsForSales as dailyStatIncrementsForSalesTyped,
  dailyStatIncrementsForReturns as dailyStatIncrementsForReturnsTyped,
  saveNewSale as saveNewSaleTyped,
  updateSale as updateSaleTyped,
  mirrorSaleFiscalInfo as mirrorSaleFiscalInfoTyped,
  deleteSale as deleteSaleTyped,
  coerceCartItemForApi,
  type PaymentMethod,
  type Sale,
  type ReturnInfo,
  type CartItem,
  type FiscalInfo,
} from './sales'

import { isCustomLine, isDeliveryLine } from './cartLineType'

import {
  getSuppliesOnDateRange as getSuppliesOnDateRangeTyped,
  getTransfersOnDateRange as getTransfersOnDateRangeTyped,
  getWriteoffsOnDateRange as getWriteoffsOnDateRangeTyped,
  getRevisionsOnDateRange as getRevisionsOnDateRangeTyped,
  saveNewSupply as saveNewSupplyTyped,
  updateSupply as updateSupplyTyped,
  deleteSupply as deleteSupplyTyped,
  saveNewWriteOff as saveNewWriteOffTyped,
  deleteWriteOff as deleteWriteOffTyped,
  saveNewRevision as saveNewRevisionTyped,
  updateRevision as updateRevisionTyped,
  deleteRevision as deleteRevisionTyped,
  saveNewTransfer as saveNewTransferTyped,
  deleteTransfer as deleteTransferTyped,
  uploadSupplyInvoice as uploadSupplyInvoiceTyped,
  deleteSupplyInvoice as deleteSupplyInvoiceTyped,
} from './inventory'

import {
  saveNewProduct as saveNewProductTyped,
  deleteProduct as deleteProductTyped,
  uploadProductImage as uploadProductImageTyped,
  deleteProductImage as deleteProductImageTyped,
  getProductMovementOnDateRange as getProductMovementOnDateRangeTyped,
  batchUpdateProducts as batchUpdateProductsTyped,
  updateProduct as updateProductTyped,
} from './products'

import {
  findClientByPhone as findClientByPhoneTyped,
  getClientById as getClientByIdTyped,
  getClientsRegisteredOnDateRange as getClientsRegisteredOnDateRangeTyped,
  getClientsActiveOnDateRange as getClientsActiveOnDateRangeTyped,
  addNewClient as addNewClientTyped,
  deleteClient as deleteClientTyped,
  updateClient as updateClientTyped,
  updateBonusForClient as updateBonusForClientTyped,
  getClientBonusValue as getClientBonusValueTyped,
  getClientOperations as getClientOperationsTyped,
} from './clients'

// Re-export Firestore types for use in other modules
export type { FirestoreDb, FirestoreDocumentRef as FirestoreRef } from './firestore.types'

// Internal type alias for backward compatibility
type FirestoreRef = FirestoreDocumentRef

import {
  getStatsForDays as getStatsForDaysTyped,
  getMonthlyStatsOnDateRange as getMonthlyStatsOnDateRangeTyped,
  getProductStatsForDays as getProductStatsForDaysTyped,
  getProductReportForDays as getProductReportForDaysTyped,
} from './stats'
import { commitGuardedCreate } from './writeClaims'

// Re-export typed versions for backward compatibility
export const getSalesOnDateRange = getSalesOnDateRangeTyped
export const getClientStatisticsAggregated = getClientStatisticsAggregatedTyped
export const getClientTopProductsAggregated = getClientTopProductsAggregatedTyped
export const getClientSalesSummary = getClientSalesSummaryTyped
export const dailyStatIncrementsForSales = dailyStatIncrementsForSalesTyped
export const dailyStatIncrementsForReturns = dailyStatIncrementsForReturnsTyped
export const saveNewSale = saveNewSaleTyped
export const updateSale = updateSaleTyped
export const mirrorSaleFiscalInfo = mirrorSaleFiscalInfoTyped
export const deleteSale = deleteSaleTyped

export const getSuppliesOnDateRange = getSuppliesOnDateRangeTyped
export const getTransfersOnDateRange = getTransfersOnDateRangeTyped
export const getWriteOffsOnDateRange = getWriteoffsOnDateRangeTyped
export const getRevisionsOnDateRange = getRevisionsOnDateRangeTyped
export const saveNewSupply = saveNewSupplyTyped
export const updateSupply = updateSupplyTyped
export const deleteSupply = deleteSupplyTyped
export const saveNewWriteOff = saveNewWriteOffTyped
export const deleteWriteOff = deleteWriteOffTyped
export const saveNewRevision = saveNewRevisionTyped
export const updateRevision = updateRevisionTyped
export const deleteRevision = deleteRevisionTyped
export const saveNewTransfer = saveNewTransferTyped
export const deleteTransfer = deleteTransferTyped
export const uploadSupplyInvoice = uploadSupplyInvoiceTyped
export const deleteSupplyInvoice = deleteSupplyInvoiceTyped

export const saveNewProduct = saveNewProductTyped
export const deleteProduct = deleteProductTyped
export const uploadProductImage = uploadProductImageTyped
export const deleteProductImage = deleteProductImageTyped
export const getProductMovementOnDateRange = getProductMovementOnDateRangeTyped
export const batchUpdateProducts = batchUpdateProductsTyped
export const updateProduct = updateProductTyped

export const findClientByPhone = findClientByPhoneTyped
export const getClientById = getClientByIdTyped
export const getClientsRegisteredOnDateRange = getClientsRegisteredOnDateRangeTyped
export const getClientsActiveOnDateRange = getClientsActiveOnDateRangeTyped
export const addNewClient = addNewClientTyped
export const deleteClient = deleteClientTyped
export const updateClient = updateClientTyped
export const updateBonusForClient = updateBonusForClientTyped
export const getClientBonusValue = getClientBonusValueTyped
export const getClientOperations = getClientOperationsTyped

export const getStatsForDays = getStatsForDaysTyped
export const getMonthlyStatsOnDateRange = getMonthlyStatsOnDateRangeTyped
export const getProductStatsForDays = getProductStatsForDaysTyped
export const getProductReportForDays = getProductReportForDaysTyped

/** A list row as the untyped client delivers it (raw JSON, dates as ISO text). */
type RawListRow = Record<string, unknown> & { supabaseId: string }

/**
 * ATL-1477: every row of a backend list read through the untyped client, via the list's snapshot
 * window route (`readWholeList`). A bare `api.get(path, { params })` returns one page (50 rows by
 * default) with nothing to say so. `readWholeList` validates every response — shape, bounds, row
 * ids, count — before a row is accepted, so the untyped body is checked, not trusted.
 */
const getWholeList = (
  path: string,
  params: Record<string, unknown>,
  order: (a: RawListRow, b: RawListRow) => number,
): Promise<RawListRow[]> =>
  readWholeList<RawListRow>(async (window) => {
    const response = await api.get(`${path}/window`, { params: { ...params, ...window } })
    const page: IdWindowPage<RawListRow> = response.data
    return page
  }, { label: path, order })

export const functions = Functions
export const auth = Auth
export const storage = Storage
export const rootDb = RootDb
export const fieldValue = FieldValue
export const fieldPath = FieldPath
export const emailAuthProvider = EmailAuthProvider
export const getRemoteConfigValue = GetRemoteConfigValue

// ============================================================================
// Type Definitions for Legacy Code
// ============================================================================

/** Date range for queries */
export interface DateRange {
  startDate: Date
  endDate: Date
}

/** Permissions object type */
type Permissions = Record<string, boolean>

/**
 * Platform-aware universal aggregation function
 * Handles count, sum, and average aggregations across web and native platforms
 * @param {string} collectionPath - The collection path (e.g., 'sales', 'clients')
 * @param {Array} whereConditions - Array of where conditions [{ field, operator, value }]
 * @param {Array} aggregations - Array of aggregation configs [{ type, field?, alias }]
 */
/**
 * Count unprocessed client orders using universal aggregation
 * @param orgId - Organization ID
 * @returns Promise<number> Count of unprocessed orders
 */
export const countClientOrdersFromServer = async (orgId: string): Promise<number> => {
  if (shouldUseSupabase()) {
    try {
      const response = await api.get('/orders/count/unprocessed')
      return (response.data as { count?: number })?.count || 0
    } catch (error: unknown) {
      console.error('Error fetching unprocessed orders count from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'orders' },
        extra: {
          organizationId: orgId,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      })
      throw error
    }
  }

  const collectionPath = `organizations/${orgId}/orders`
  const whereConditions: WhereCondition[] = [
    { field: 'isProcessed', operator: '==', value: false }
  ]
  const aggregations: AggregationConfig[] = [
    { type: 'count', alias: 'orderCount' }
  ]

  const results = await getUniversalAggregation(collectionPath, whereConditions, aggregations)
  return results.orderCount || 0
}

export const getUniversalAggregation = async (
  collectionPath: string,
  whereConditions: WhereCondition[],
  aggregations: AggregationConfig[]
): Promise<Record<string, number>> => {
  if (Platform.OS === 'web') {
    // Web platform: use modular SDK
    try {
      const { collection, query, where, getCountFromServer, getAggregateFromServer, count, sum, average } = await import('firebase/firestore')
      const { rootDbModular } = await import('./firebaseFirestore.web')
      
      const results: Record<string, number> = {}

      // Get organization ID from the database context
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id
      
      // Construct full collection path for web platform
      // If organizationId is available and collectionPath doesn't start with 'organizations/',
      // prepend the organization path
      let fullCollectionPath = collectionPath
      if (organizationId && !collectionPath.startsWith('organizations/')) {
        fullCollectionPath = `organizations/${organizationId}/${collectionPath}`
      }
      
      // Create collection reference
      const collectionRef = collection(rootDbModular, fullCollectionPath)
      
      // Build query constraints from where conditions
      const queryConstraints = whereConditions.map(condition =>
        where(condition.field, condition.operator as '<' | '<=' | '==' | '!=' | '>=' | '>' | 'array-contains' | 'in' | 'array-contains-any' | 'not-in', condition.value)
      )
      const firestoreQuery = query(collectionRef, ...queryConstraints)
      
      // ATL-1101 — one shared decision for both platform branches, unit-tested in
      // `aggregationPlan.test.js`. A count asked for ALONGSIDE sums/averages goes in the SAME
      // aggregate query: two round trips describe two snapshots, and the daily-stats sweep runs
      // against TODAY on an actively selling till, so a sale landing between them makes the count
      // and the sum disagree by construction. See the docblock on `planAggregations`.
      const plan = planAggregations(aggregations)

      if (plan.standaloneCountAlias) {
        const countSnapshot = await getCountFromServer(firestoreQuery)
        results[plan.standaloneCountAlias] = countSnapshot.data().count
      }
      
      // Handle sum and average aggregations
      if (plan.needsAggregateQuery) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aggregationObject: Record<string, any> = {}

        if (plan.countInSpecAlias) aggregationObject[plan.countInSpecAlias] = count()
        plan.sums.forEach(agg => {
          aggregationObject[agg.alias] = sum(agg.field!)
        })
        plan.averages.forEach(agg => {
          aggregationObject[agg.alias] = average(agg.field!)
        })

        // Execute aggregation query
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aggregateSnapshot = await getAggregateFromServer(firestoreQuery as any, aggregationObject)
        const aggregateData = aggregateSnapshot.data()
        
        // Extract results
        Object.keys(aggregationObject).forEach(alias => {
          results[alias] = (aggregateData as Record<string, number>)[alias] || 0
        })
      }
      
      return results
    } catch (error: unknown) {
      console.error('Web aggregation error:', error)
      throw error
    }
  } else {
    // Native platform: use React Native Firebase
    try {
      const jotaiStore = getDefaultStore()
      const db = jotaiStore.get(dbAtom)

      const results: Record<string, number> = {}

      // Build query reference
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let queryRef: any
      if (collectionPath.startsWith('organizations/') && collectionPath.includes('/')) {
        // Handle organization subcollections (e.g., organizations/123/orders)
        const pathParts = collectionPath.split('/')
        const orgId = pathParts[1]
        const subcollection = pathParts[2]
        
        // Check if db is already scoped to an organization
        if (db.path && db.path.includes('organizations/')) {
          // db is already scoped to organization, just use the subcollection
          queryRef = db.collection(subcollection)
        } else {
          // db is root level, need to navigate to organization subcollection
          queryRef = db.collection('organizations').doc(orgId).collection(subcollection)
        }
      } else {
        // Handle regular collections
        queryRef = db.collection(collectionPath)
      }
      
      whereConditions.forEach(condition => {
        queryRef = queryRef.where(condition.field, condition.operator, condition.value)
      })
      
      // Same shared decision as the web branch — see `planAggregations`.
      const plan = planAggregations(aggregations)

      // Use RN Firebase aggregate helpers
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { getAggregateFromServer, getCountFromServer, count, sum, average } = require('@react-native-firebase/firestore')

      // Handle count aggregation using getCountFromServer
      if (plan.standaloneCountAlias) {
        const countSnapshot = await getCountFromServer(queryRef)
        results[plan.standaloneCountAlias] = countSnapshot.data().count
      }

      // Handle sum/average using getAggregateFromServer with aliases
      if (plan.needsAggregateQuery) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const spec: Record<string, any> = {}
        if (plan.countInSpecAlias) spec[plan.countInSpecAlias] = count()
        for (const agg of plan.sums) {
          spec[agg.alias] = sum(agg.field)
        }
        for (const agg of plan.averages) {
          spec[agg.alias] = average(agg.field)
        }
        const aggregateSnapshot = await getAggregateFromServer(queryRef, spec)
        const aggregateData = aggregateSnapshot.data() as Record<string, number>
        Object.keys(spec).forEach(alias => {
          results[alias] = aggregateData[alias] || 0
        })
      }
      
      return results
    } catch (error: unknown) {
      console.error('Native aggregation error:', error)
      throw error
    }
  }
}

export const sendMessageToClient = functions.httpsCallable('sendMessageToClient')
export const createNewInternalUser = functions.httpsCallable('createNewInternalUser')
export const updateUserInAuth = functions.httpsCallable('updateUserInAuth')
export const disableUser = functions.httpsCallable('disableUser')
export const enableUser = functions.httpsCallable('enableUser')
export const deleteUsersFromAuth = functions.httpsCallable('deleteUsersFromAuth')

/**
 * Create internal user with dual-write to Supabase
 * After Firebase Cloud Function succeeds, write to Supabase users table
 */
export const createNewInternalUserWithDualWrite = async (info: {
  name: string
  email: string
  password: string
  active: boolean
  role: string
  store: { id: string; name: string }
  organizationId: string
  phone?: string
  locale?: string
}) => {
  const response = await createNewInternalUser(info)

  // If successful (status === 0), dual-write to Supabase
  if (response?.data?.status === 0 && response?.data?.uid) {
    await bestEffortDualWrite('users.create', () => usersApi.createUser({
      firestoreId: response.data.uid,
      uid: response.data.uid,
      name: info.name,
      email: info.email,
      organizationId: info.organizationId,
      role: info.role,
      storeId: info.store?.id,
      active: info.active,
      ...(info.phone ? { phone: info.phone } : {}),
      ...(info.locale ? { locale: info.locale } : {}),
    }))
  }

  return response
}

/**
 * Enable user with dual-write to Supabase
 */
export const enableUserWithDualWrite = async (data: { uid: string; id: string }) => {
  const response = await enableUser(data)

  if (response?.data?.status === 0) {
    // ATL-1209: a replay of this enable must not undo a disable made since, on any device.
    await bestEffortDualWrite('users.enable', () => usersApi.enableUser(data.uid), {
      deletedFrom: userActiveLocation(data.id, true),
    })
  }

  return response
}

/**
 * Soft-delete user with dual-write to Supabase
 * Disables in Firebase Auth via Cloud Function, then soft-deletes in Supabase
 */
export const softDeleteUserWithDualWrite = async (data: { uid: string; id: string }) => {
  const response = await disableUser(data)

  if (response?.data?.status === 0) {
    // ATL-1209: a replay of this disable must not undo a re-enable made since, on any device.
    await bestEffortDualWrite('users.softDelete', () => usersApi.softDeleteUser(data.uid), {
      deletedFrom: userActiveLocation(data.id, false),
    })
  }

  return response
}

/**
 * Update user email with dual-write to Supabase
 * Updates Firebase Auth via Cloud Function, then syncs email to Supabase
 */
export const updateUserEmailWithDualWrite = async (data: {
  email: string
  uid: string
}) => {
  // Update Firebase Auth via Cloud Function (also updates Firestore email)
  const response = await updateUserInAuth({ email: data.email, uid: data.uid })

  // Dual-write to Supabase via backend API
  if (response?.data?.status === 0) {
    await bestEffortDualWrite('users.updateEmail', () => usersApi.updateEmail(data.uid, data.email))
  }

  return response
}

/**
 * Update user password with dual-write to Supabase
 * Updates Firebase Auth via Cloud Function, then records audit trail in Supabase
 *
 * ATL-1951: the callable is the ONLY credential write. The Supabase leg is an audit stamp and must
 * never carry the password: a failed mirror is persisted to AsyncStorage and replayed later, and a
 * replayed password was both plaintext at rest and a late credential write (it signed a till out
 * 11.5 h after the change, and would have reverted any newer password). The outbox also refuses to
 * persist this label at all (`isCredentialWrite` in dualWriteOutbox.ts).
 */
export const updateUserPasswordWithDualWrite = async (data: {
  password: string
  uid: string
}) => {
  // Update Firebase Auth via Cloud Function
  const response = await updateUserInAuth({ password: data.password, uid: data.uid })

  // Dual-write to Supabase (audit trail only — no password)
  if (response?.data?.status === 0) {
    await bestEffortDualWrite('users.updatePassword', () => usersApi.recordPasswordChange(data.uid))
  }

  return response
}

export const loginWithEmail = (email: string, password: string) =>
  auth.signInWithEmailAndPassword(email, password)

export const logout = () => auth.signOut()

export const passwordReset = (email: string) => auth.sendPasswordResetEmail(email)

interface FunnelAnswers {
  email: string
  password: string
  userName: string
  phone: string
  orgName: string
  currency: { id: string; symbol?: string }
  businessType: { id: string }
  trafficSource: { id: string }
  setupOption: { id: string }
  wantClientTracking: { id: string }
  loyaltyType: { id: string }
  defaultBonusLevel: { id: string }
  punchCardCapacity: { id: string }
  wantOnlineCatalog: { id: string }
  inventoryTrackingNeeds: { id: string }
  inventoryOperationNeeds: Array<{ id: string }>
  [key: string]: unknown
}

export const createNewUserAndOrganization = async (funnelAnswers: FunnelAnswers) => {
  const { 
    email, 
    password, 
    userName, 
    phone, 
    orgName, 
    currency, 
    businessType, 
    trafficSource, 
    setupOption, 
    wantClientTracking, 
    loyaltyType, 
    defaultBonusLevel,
    punchCardCapacity,
    wantOnlineCatalog,
    inventoryTrackingNeeds,
    inventoryOperationNeeds,
  } = funnelAnswers

  const updatedFunnelAnswers = {...funnelAnswers}
  STATIC_FIELDS.forEach(field => {
    delete updatedFunnelAnswers[field]
  })
  // WA-ONBOARD-001 — the WhatsApp consent travels in its own `whatsappOnboarding` block and is
  // stripped from the funnel-answers blob. `funnelAnswers` is copied verbatim into the organization
  // document and mirrored to Supabase, so leaving it here would create a SECOND copy of the consent
  // that can drift from the ledger an audit actually reads — and, unlike the ledger, one that gets
  // rewritten by any later edit of the org document.
  delete updatedFunnelAnswers[WHATSAPP_OPT_IN_FIELD]
  // ...and the instant it was ticked, for the same reason plus two of its own. The checkbox writes
  // this one with `setValue`, so a merchant who ticks the box and then UNTICKS it leaves the key
  // present with the value `undefined` — and Firestore (ignoreUndefinedProperties is unset ⇒ false)
  // rejects the whole `batch.set` with "Unsupported field value: undefined", failing a signup whose
  // only sin was changing its mind. It would also diverge the two stores even when set: the field
  // carries no decorator on `FunnelAnswersDto`, so the global pipe's `whitelist: true` strips it
  // from the Supabase copy while Firestore keeps it.
  delete updatedFunnelAnswers[WHATSAPP_OPT_IN_AT_FIELD]

  const localizedDefaultUnits = DEFAULT_UNITS.map(item => ({...item, 
    name: i18n.t(item.id + 'Long'),
    shortName: i18n.t(item.id),
  }))
  const localizedOnboardingChecklist = ONBOARDING_CHECKLIST.map(item => {
    const newItem = {...item, 
      title: i18n.t('onboarding.' + item.id),
      description: i18n.t('onboarding.' + item.id + 'Description'),
    }
    delete newItem.condition
    if (item.condition) { 
      if (item.condition(businessType)) {
        return newItem
      } else {
        return null
      }
    } else {
      return newItem
    }
  }).filter(item => item !== null)

  // ATL-920: this used to read Remote Config `active_pricing_model`, and on native
  // `getRemoteConfigValue` returned RNFirebase's Value OBJECT rather than a string — so the org
  // document was written with `{_source, _value}` where a pricing-model string belongs. The
  // commented-out call is deleted rather than left as a hint: `getRemoteConfigValue` now returns a
  // string on both platforms, but the value here is deliberately a constant and reviving a
  // Remote-Config read is a product decision, not a cleanup.
  const activePricingModel = 'paywall_2024_07_18'

  // Initialize enabled modules
  const enabledModules: Record<string, boolean> = {}
  for (const module of MODULES as Array<{ id: string }>) {
    enabledModules[module.id] = false
  }

  // Enable core modules
  enabledModules.sales = true
  enabledModules.spendings = true
  // Enable complex sales for specific business types
  if (ComplexSalesBusinessTypes.includes(businessType?.id)) {
    enabledModules.complexSales = true
  }
  if (inventoryTrackingNeeds?.id !== 'none') {
    enabledModules.stock = true
  }
  if (inventoryOperationNeeds.some(need => need.id === 'supplies')) {
    enabledModules.supplies = true
  }
  if (inventoryOperationNeeds.some(need => need.id === 'writeOffs')) {
    enabledModules.writeOffs = true
  }
  if (inventoryOperationNeeds.some(need => need.id === 'revisions')) {
    enabledModules.revisions = true
  }
  if (inventoryOperationNeeds.some(need => need.id === 'transfers')) {
    enabledModules.transfers = true
  }
  if (inventoryOperationNeeds.some(need => need.id === 'manufactures')) {
    enabledModules.manufactures = true
  }
  if (wantClientTracking?.id === 'yes') {
    enabledModules.clients = true
  }
  if (wantOnlineCatalog?.id === 'yes') {
    enabledModules.ecommerce = true
  }

  const orgInfo = {
    name: orgName, 
    createdDate: new Date(), 
    pricingModel: activePricingModel,
    currency: currency,
    moduleEnabled: enabledModules,
    onboardingChecklist: localizedOnboardingChecklist,
    onboardingTasksLeft: localizedOnboardingChecklist.length-1,

    funnelAnswers: updatedFunnelAnswers,
    platform: Platform.OS,
    businessType,
    trafficSource,
    setupOption,
  }

  const batch = rootDb.batch()

  // register user in firebase auth (handle retry: user may already exist)
  let authUserCred
  try {
    authUserCred = await auth.createUserWithEmailAndPassword(email, password)
  } catch (e: any) {
    if (e.code === 'auth/email-already-in-use') {
      authUserCred = await auth.signInWithEmailAndPassword(email, password)
    } else {
      throw e
    }
  }

  // Persist generated Firestore IDs for retry idempotency
  let orgId = await AsyncStorage.getItem('pending_org_id')
  if (!orgId) {
    orgId = rootDb.collection('organizations').doc().id
    await AsyncStorage.setItem('pending_org_id', orgId as string)
  }
  let storeId = await AsyncStorage.getItem('pending_store_id')
  if (!storeId) {
    storeId = rootDb.collection('organizations').doc().collection('stores').doc().id
    await AsyncStorage.setItem('pending_store_id', storeId as string)
  }

  // create org (using persisted ID for retry idempotency)
  const orgRef = rootDb.collection('organizations').doc(orgId)
  batch.set(orgRef, orgInfo)
  const db = orgRef

  // create store (using persisted ID for retry idempotency)
  const storeRef = db.collection('stores').doc(storeId)
  const defaultStoreName = i18n.t('store') + ' 1'
  //find timezone
  const deviceTimezone = Localization.getCalendars()[0].timeZone
  let timezone = timezones[77]
  for (const value of timezones) {
    for (const utcValue of value.utc) {
      if (utcValue === deviceTimezone) {
        timezone = value
      }
    }
  }
  batch.set(storeRef, {
    id: storeRef.id,
    name: defaultStoreName,
    timezone,
    autoCloseShiftTime: WORKING_HOURS[0],
    organizationId: orgRef.id,
    // ATL-1098 — signup seeds the SCALAR shape, deliberately, and this is not an oversight left in
    // place. `showOnTop` is also legally `{ admin, cashier }` (2,627 of the fleet's 5,814 stores
    // hold that), and the column is jsonb now, so a map here would be storable. It would still be
    // wrong: nothing has written a new map in years, the only reader consults the `admin` half, and
    // seeding `cashier` would be inventing a setting this merchant never made — the same
    // manufacture-a-value mistake as the coercion, pointed the other way. Scalar `true` means
    // "shown, no per-role split", which is exactly what a brand-new store's first branch is.
    showOnTop: true,
    fiscalOperator: {id: 'none', text: i18n.t('settings.withoutFiscalization')},
  })

  // create admin user in firestore
  const userRef = rootDb.collection('users').doc(authUserCred.user.uid)
  batch.set(userRef, {
    name: userName,
    active: true,
    email: email,
    phone: phone,
    organizationId: orgRef.id,
    uid: authUserCred.user.uid,
    role: 'admin',
    store: {
      id: storeRef.id,
      name: defaultStoreName
    }
  })

  // create wallets
  const cashWalletInfo = {
    id: storeRef.id + '-cash',
    text: i18n.t('cashFromCashbox') + ' ' + defaultStoreName,
    storeId: storeRef.id,
  }
  const bankWalletInfo = {
    id: 'bank-wallet',
    text: i18n.t('bankAccount'),
    storeId: '',
  }
  batch.set(db.collection('constants').doc('wallets'), {
    data: [cashWalletInfo, {
      id: storeRef.id + '-extracted-cash',
      text: i18n.t('extractedCash') + ' ' + defaultStoreName,
      storeId: storeRef.id,
    }, bankWalletInfo],
    organizationId: orgRef.id
  })

  // ATL-946 — the seeded payment methods, defined ONCE and written to both stores from this one
  // object. They used to be written twice: this Firestore batch carried `destinationWalletForStore`
  // and the Supabase `createOrgPayload.paymentMethods` below simply did not have the key. That map
  // is what `resolve_payment_method_wallet()` reads, so every org created this way booked every
  // sale's income with `destination_wallet = NULL` — 1,099 transactions across 10 active orgs,
  // invisible while reads come from Firestore and lost at the read cutover.
  //
  // This is the ATL-938 shape on a different field, and ATL-938's own write-up records that its
  // first draft kept two copies of the seed "with a comment claiming they could not drift". So
  // there is one copy, and `seededPaymentMethods.test.js` pins that both legs carry the same map.
  const defaultCashName = localizedDefaultCashName(i18n.language, (key: string) => i18n.t(key))
  const seededPaymentMethods = [{
    id: 'cash',
    name: defaultCashName,
    activeInStore: { [storeRef.id]: true },
    comission: 0,
    icon: 'money-bill',
    iconText: defaultCashName,
    isCash: true,
    destinationWalletForStore: {
      [storeRef.id]: cashWalletInfo
    }
  }, {
    id: 'qr',
    name: 'QR',
    activeInStore: { [storeRef.id]: true },
    comission: 0.95,
    icon: 'qrcode',
    iconText: 'QR',
    isCash: false,
    destinationWalletForStore: {
      [storeRef.id]: bankWalletInfo
    }
  }, {
    id: 'terminal',
    name: i18n.t('paymentTerminal'),
    activeInStore: { [storeRef.id]: true },
    comission: 2.3,
    icon: 'credit-card',
    iconText: i18n.t('paymentTerminal'),
    isCash: false,
    destinationWalletForStore: {
      [storeRef.id]: bankWalletInfo
    }
  }]

  // create payment methods
  batch.set(db.collection('constants').doc('paymentMethods'), {
    data: seededPaymentMethods,
    organizationId: orgRef.id
  })

  // create purchase methods
  batch.set(db.collection('constants').doc('purchaseMethods'), {
    data: [{
      id: 'shop-offline',
      name: i18n.t('login.shopOffline'),
      activeInStore: { [storeRef.id]: true },
      icon: 'house-user',
      iconText: i18n.t('login.shopOffline')
    }, {
      id: 'delivery',
      name: i18n.t('login.delivery'),
      activeInStore: { [storeRef.id]: true },
      icon: 'truck',
      iconText: i18n.t('login.delivery')
    }],
    organizationId: orgRef.id
  })

  // create roles
  const adminPermissions: Record<string, boolean> = {}
  ;(ALL_PERMISSIONS as string[]).forEach((item) => {
    adminPermissions[item] = true
  })
  const sellerPermissions: Record<string, boolean> = {...adminPermissions}
  sellerPermissions.Spendings = false
  sellerPermissions.viewAdminSpendingCategories = false
  sellerPermissions.Stats = false
  sellerPermissions.Clients = false
  sellerPermissions.ClientDetails = false
  sellerPermissions.Settings = false
  sellerPermissions.deleteSale = false
  sellerPermissions.deleteSupply = false
  sellerPermissions.deleteWriteOff = false
  sellerPermissions.deleteComplexSale = false
  sellerPermissions.deleteProduct = false
  sellerPermissions.deleteManufacture = false
  sellerPermissions.deleteSpending = false
  sellerPermissions.deleteRevision = false
  sellerPermissions.deleteTransfer = false
  sellerPermissions.editChecklistItems = false
  sellerPermissions.viewOthersWorkingHours = false
  sellerPermissions.viewOtherStores = false
  sellerPermissions.billing = false
  sellerPermissions.newSaleSettings = false
  batch.set(db.collection('constants').doc('roles'), {
    data: [{
      id: 'admin',
      name: i18n.t('login.admin'),
      salaryType: 'hour',
      salary: 500,
      permissions: adminPermissions
    }, {
      id: 'cashier',
      name: i18n.t('login.cashier'),
      salaryType: 'hour',
      salary: 500,
      permissions: sellerPermissions
    }],
    organizationId: orgRef.id
  })

  // create discount options
  batch.set(db.collection('constants').doc('discountOptions'), {
    data: [
      { id: '5%', text: '5' },
      { id: '10%', text: '10' },
      { id: '15%', text: '15' },
      { id: '20%', text: '20' },
      { id: '25%', text: '25' },
      { id: '30%', text: '30' },
      { id: '50%', text: '50' },
    ],
    organizationId: orgRef.id
  })

  // create default units
  batch.set(db.collection('constants').doc('productUnits'), { 
    data: localizedDefaultUnits,
    organizationId: orgRef.id
  })

  // product category
  const productCategoryRef = db.collection('productCategories').doc()
  batch.set(productCategoryRef, {name: i18n.t('other'), order: 1})

  // product modifier
  batch.set(db.collection('constants').doc('productModifiers'), {
    data: [{
      id: 'universal',
      name: i18n.t('login.universalModifiers'),
      activeInStore: { [storeRef.id]: true },
      data: []
    }],
    organizationId: orgRef.id
  })

  // product supplier
  const supplierRef = db.collection('suppliers').doc()
  batch.set(supplierRef, {name: i18n.t('other'), active: true})

  // products
  const productRef = db.collection('products').doc()
  batch.set(productRef, {
    activeInStore: {
      [storeRef.id]: true
    }, 
    barcode: 200001,
    categoryID: productCategoryRef.id,
    supplierID: supplierRef.id,
    name: i18n.t('login.testProduct'), 
    purchasePrice: 1000, 
    isHiddenInNewSale: false,
    salePrices: {
      [storeRef.id]: 1500
    }, 
    storeStock: {
      [storeRef.id]: 0
    }, 
    type: false
  })

  // spending categories
  batch.set(db.collection('constants').doc('adminSpendingCategories'), {
    data: [{
      id: 'adminSpendingCategory1',
      text: i18n.t('login.adminSpendingCategory1'),
    }, {
      id: 'adminSpendingCategory2',
      text: i18n.t('login.adminSpendingCategory2'),
    }, {
      id: 'adminSpendingCategoryOther',
      text: i18n.t('other'),
    }],
    organizationId: orgRef.id
  })
  batch.set(db.collection('constants').doc('spendingCategories'), {
    data: [{
      id: 'spendingCategory1',
      text: i18n.t('login.spendingCategory1'),
    }, {
      id: 'spendingCategory2',
      text: i18n.t('login.spendingCategory2'),
    }, {
      id: 'spendingCategoryOther',
      text: i18n.t('other'),
    }],
    organizationId: orgRef.id
  })
  // ATL-802: the default recurring spending (the rent line) is defined ONCE and used twice — written
  // to Firestore here, and sent to the backend as `createOrgPayload.recurringSpendings` below.
  //
  // The literal `id` is LOAD-BEARING, for two independent reasons:
  //
  //  1. It is the handle the EDIT and DELETE paths use. `saveRecurringSpendings` resolves the row to
  //     update as `info.id || info.firestoreId || info.supabaseId`, and `deleteRecurringSpending`
  //     sends `deletedItem.id`. Seeded without an id, this line was write-only in Supabase: an edit
  //     was silently skipped and a delete 404'd, so a rent line the owner had DELETED would come back
  //     at the Phase-5 read cutover — 100 000/month of spend the books never agreed to.
  //  2. It keeps BOTH writers on `item.id` verbatim. `migrateConstants` only mints a content-hash key
  //     for id-LESS items (ATL-799); an id-bearing item keeps its plain id, so re-migrating this org
  //     upserts onto THIS row instead of inserting a second rent line. No hash runs on this path at
  //     all, which is why nothing here is coupled to the hashing scheme.
  //
  // `recurringSpending1` follows the convention of every other signup-seeded constant item
  // (`spendingCategory1`, `adminSpendingCategory1`, `admin`, `wasted`, …) — this was the only one
  // without an id. Keep the two copies below in lockstep: the payload must carry the same id and the
  // same values, or Firestore and Supabase describe different money.
  const defaultRecurringSpending = {
    id: 'recurringSpending1',
    name: i18n.t('login.recurringSpending1'),
    amount: 100000,
    date: new Date(),
    store: {
      id: storeRef.id,
      name: defaultStoreName
    },
    type: 'monthly'
  }
  batch.set(db.collection('constants').doc('recurringSpendings'), {
    data: [defaultRecurringSpending],
    organizationId: orgRef.id
  })

  // writeoff reasons
  batch.set(db.collection('constants').doc('writeOffReasons'), {
    data: [{
      id: 'wasted',
      text: i18n.t('login.writeOffReasonWasted'),
    }, {
      id: 'internal-use',
      text: i18n.t('login.writeOffReasonInternalUse'),
    }, {
      id: 'gift',
      text: i18n.t('login.writeOffReasonGift'),
    }, {
      id: 'other',
      text: i18n.t('other'),
    }],
    organizationId: orgRef.id
  })

  // return reasons
  batch.set(db.collection('constants').doc('returnReasons'), {
    data: [{
      id: 'mistake',
      text: i18n.t('login.returnReasonMistake'),
    }, {
      id: 'not-like',
      text: i18n.t('login.returnReasonNotLike'),
    }, {
      id: 'bad-good',
      text: i18n.t('login.returnReasonBadGood'),
    }, {
      id: 'other',
      text: i18n.t('other'),
    }],
    organizationId: orgRef.id
  })

  /**
   * ATL-938 — the flat `constants/<name>` documents signup seeds. ONE definition, written to BOTH
   * stores below: the Firestore batch here, and `organizationConstants` in the Supabase payload.
   *
   * They used to be Firestore-only, so every org created after dual-write went fleet-wide held them
   * in Firestore and nowhere else and failed the cutover gate's `constants-parity` clause. Defined
   * once rather than listed twice, because two copies of a seed is how the two stores drift apart
   * one edit at a time.
   */
  const seededOrganizationConstants = {
    printChecks: { name: 'printChecks', value: false },
    stickerHeight: { name: 'stickerHeight', value: 113 },
    floatingMenu: { isVisible: true },
  }
  for (const [name, body] of Object.entries(seededOrganizationConstants)) {
    batch.set(db.collection('constants').doc(name), body)
  }

  // loyalty settings
  batch.set(db.collection('constants').doc('loyaltySettings'), {
    cashbackEnabled: loyaltyType?.id === 'cashback',
    punchCardEnabled: loyaltyType?.id === 'punchCard',
    defaultBonusLevel: loyaltyType?.id === 'cashback' ? parseInt(defaultBonusLevel?.id) : 1,
    punchCardCapacity: loyaltyType?.id === 'punchCard' ? parseInt(punchCardCapacity?.id) : 7,
  })

  // sale settings - store-specific settings for delivery and other sale configurations
  batch.set(db.collection('constants').doc('saleSettings'), {
    data: {
      [storeRef.id]: {
        deliveryPrice: 0,
        deliveryPaidByCustomer: true,
        deliveryIncludeInReceipt: true,
        deliveryPaymentSourceWallet: null,
        defaultDeliverySpendingCategory: null,
        showSaveCartForLater: true,
        allowMultipleProductSelection: true,
        dateFieldVisible: false,
        storeFieldVisible: false,
        tableNumberFieldVisible: false,
        commentFieldVisible: false,
        showTotalPurchasePriceInCart: false,
        showPurchasePriceInEditCartItem: false,
        mergeSimilarItems: true
      }
    },
    organizationId: orgRef.id
  })

  // ATL-1225 — ONE clock read for the whole day-keyed group below (the same defect ATL-1216 fixed
  // in `saveNewStore`, plus one that site never had because it had no mirror).
  //
  // This block used to read the wall clock five separate times: the checklist id, the checklist's
  // `date`, `today`, `tomorrow`, and again in the Supabase mirror far below. Two of those reads
  // have to agree with something outside this function:
  //
  //  ① The mirror's `date` and the Firestore `date` are compared. `checklists.service.ts` diffs
  //     them at SECOND granularity — deliberately, because comparing only the UTC day masked a
  //     real truncation bug — and marks every difference `severity: 'critical'`;
  //     `organizations.service.ts` stores whatever the client sent, verbatim. The two reads were
  //     separated by `await batch.commit()`, a network round trip, so a signup whose reads landed
  //     in different seconds shipped an organization that was ALREADY refused a read cutover on
  //     the blocking `compare-spotcheck` clause. A live race, not a boundary case.
  //
  //  ② `checklistId` and `dailyStatId` are DOCUMENT IDS that have to name the same day:
  //     `updateChecklist` addresses the daily stat by stripping `-seller` off the checklist id. A
  //     signup across local midnight named day D on one and D+1 on the other, so the first ticked
  //     checkbox ran `batch.update` against a `dailyStats` document that does not exist, the whole
  //     batch failed, and `markChecked` (`SellerChecklistScreen.js`) neither awaits nor catches it.
  //     Silently un-tickable.
  //
  // So: one instant, `checklistId` derived FROM `dailyStatId` rather than re-formatted, and
  // `checklistContent` hoisted so the mirror sends the array this batch wrote instead of a second
  // copy of the literal that can drift.
  //
  // ⚠️ Consistent is not the same as agreeing with the READER. `SellerChecklistScreen` derives the
  // id from `format(getLocalStoreDate(), 'd-M-y')` — a STORE-timezone date — while this mints it
  // from the DEVICE's local date, so for a device in another timezone the screen can look up an id
  // this function never minted. Note the screen passes NO storeId, so its date resolves to the
  // LOGGED-IN user's store even while its `store` picker addresses another one: two ways for the
  // reader to miss. Pre-existing, shared with `saveNewStore`, out of scope here; ATL-1224.
  //
  // ⚠️ Also unfixed here: the daily stat below hardcodes `incompleteChecklistItems: 1` while this
  // checklist carries THREE incomplete tasks, so the home badge goes UP after the first tick
  // (`updateChecklist` recomputes it correctly). A user-visible product change, not this clock
  // defect; ATL-1227.
  const signupCreatedAt = new Date()

  // daily stat ids — the checklist's id is built from today's, never re-formatted
  const dailyStatId = format(signupCreatedAt, 'd-M-y') + '-' + storeRef.id

  // checklist defaults
  const checklistId = dailyStatId + '-seller'
  const checklistRef = db.collection('checklists').doc(checklistId)
  const checklistContent = [{
    type: 'header',
    text: i18n.t('login.checklistHeader1')
  }, {
    type: 'task',
    text: i18n.t('login.checklistTask1'),
    done: false,
  }, {
    type: 'task',
    text: i18n.t('login.checklistTask2'),
    done: false,
  }, {
    type: 'header',
    text: i18n.t('login.checklistHeader2')
  }, {
    type: 'task',
    text: i18n.t('login.checklistTask3'),
    done: false,
  }]
  batch.set(checklistRef, {
    date: signupCreatedAt,
    store: storeRef.id,
    organizationId: orgRef.id,
    content: checklistContent,
  })

  // daily stat
  const dailyStatRef = db.collection('dailyStats').doc(dailyStatId)
  batch.set(dailyStatRef, {
    date: signupCreatedAt,
    store: storeRef.id, 
    incompleteChecklistItems: 1, 
    organizationId: orgRef.id
  })
  const tomorrow = new Date(signupCreatedAt.getTime())
  tomorrow.setDate(signupCreatedAt.getDate() + 1)
  tomorrow.setHours(0,0,0,0)
  const dailyStatIdTomorrow = format(tomorrow, 'd-M-y') + '-' + storeRef.id
  const dailyStatTomorrowRef = db.collection('dailyStats').doc(dailyStatIdTomorrow)
  batch.set(dailyStatTomorrowRef, {
    date: tomorrow, 
    store: storeRef.id, 
    organizationId: orgRef.id
  })

  await batch.commit()

  // [SIGNUP] Emit the new org/store/user ids once the Firestore docs exist (before the Supabase
  // dual-write). Powers the signup→dual-write parity e2e (ATL-595) and is a useful prod breadcrumb
  // for tracing a new signup across Firestore/Supabase/Auth. NOTE: no email/PII — this line becomes
  // a Sentry console breadcrumb, so it carries ids only.
  // eslint-disable-next-line no-console
  console.log(
    '[SIGNUP] org created',
    JSON.stringify({ orgId: orgRef.id, storeId: storeRef.id, uid: authUserCred.user.uid }),
  )

  // Dual-write to Supabase via backend API
  const createOrgPayload = {
    firestoreId: orgRef.id,
    name: orgName,
    // INAPP-ONBOARD-001 — the language this merchant actually signed up in, sent so the in-app
    // welcome a minute from now can be written in it. It has to travel WITH the registration:
    // `users.locale` is not written by the batch above and `create_organization_atomic` has no
    // parameter for it, so every new user's `locale` is NULL and the language is not recoverable
    // later. `i18n.language` is the value the funnel screens were just rendered in.
    locale: i18n.language,
    // ADS-FIX-2026-002: scopes backend replay repair to create requests emitted by fixed clients.
    // Old queued create payloads have no marker and therefore cannot backfill historical orgs.
    defaultPaymentMethodSeedVersion: 1,
    currency: currency,
    businessType: businessType,
    pricingModel: activePricingModel,
    moduleEnabled: enabledModules,
    onboardingChecklist: localizedOnboardingChecklist,
    onboardingTasksLeft: localizedOnboardingChecklist.length - 1,
    funnelAnswers: updatedFunnelAnswers,
    platform: Platform.OS,
    trafficSource: trafficSource,
    setupOption: setupOption,
    // WA-ONBOARD-001 — the merchant's answer to the WhatsApp activation opt-in, always present
    // (an unticked box is sent as an explicit `false`, never omitted: the backend reads an ABSENT
    // block as "this app never asked" and records nothing). `i18n.language` is the language the
    // disclosure was actually rendered in.
    whatsappOnboarding: buildWhatsappOnboardingConsent(funnelAnswers, { locale: i18n.language }),
    store: {
      firestoreId: storeRef.id,
      name: defaultStoreName,
      timezone: timezone,
      autoCloseShiftTime: WORKING_HOURS[0],
      fiscalOperator: { id: 'none', text: i18n.t('settings.withoutFiscalization') },
    },
    user: {
      firestoreUid: authUserCred.user.uid,
      name: userName,
      email: email,
      phone: phone,
      role: 'admin',
      storeFirestoreId: storeRef.id,
    },
    wallets: [
      { firestoreId: storeRef.id + '-cash', text: i18n.t('cashFromCashbox') + ' ' + defaultStoreName, storeFirestoreId: storeRef.id },
      { firestoreId: storeRef.id + '-extracted-cash', text: i18n.t('extractedCash') + ' ' + defaultStoreName, storeFirestoreId: storeRef.id },
      { firestoreId: 'bank-wallet', text: i18n.t('bankAccount'), storeFirestoreId: '' },
    ],
    // ATL-946: the SAME objects the Firestore batch wrote, re-keyed `id` -> `firestoreId` for the
    // DTO. Nothing is restated here, so the wallet map cannot be present on one side only.
    paymentMethods: seededPaymentMethods.map(({ id, ...rest }) => ({ firestoreId: id, ...rest })),
    purchaseMethods: [
      { firestoreId: 'shop-offline', name: i18n.t('login.shopOffline'), activeInStore: { [storeRef.id]: true }, icon: 'house-user', iconText: i18n.t('login.shopOffline') },
      { firestoreId: 'delivery', name: i18n.t('login.delivery'), activeInStore: { [storeRef.id]: true }, icon: 'truck', iconText: i18n.t('login.delivery') },
    ],
    roles: [
      { firestoreId: 'admin', name: i18n.t('login.admin'), salaryType: 'hour', salary: 500, permissions: adminPermissions },
      { firestoreId: 'cashier', name: i18n.t('login.cashier'), salaryType: 'hour', salary: 500, permissions: sellerPermissions },
    ],
    productUnits: localizedDefaultUnits.map(u => ({ firestoreId: u.id, name: u.name, shortName: u.shortName })),
    discountOptions: [
      { firestoreId: '5%', text: '5' },
      { firestoreId: '10%', text: '10' },
      { firestoreId: '15%', text: '15' },
      { firestoreId: '20%', text: '20' },
      { firestoreId: '25%', text: '25' },
      { firestoreId: '30%', text: '30' },
      { firestoreId: '50%', text: '50' },
    ],
    spendingCategories: [
      { firestoreId: 'adminSpendingCategory1', text: i18n.t('login.adminSpendingCategory1'), isAdmin: true },
      { firestoreId: 'adminSpendingCategory2', text: i18n.t('login.adminSpendingCategory2'), isAdmin: true },
      { firestoreId: 'adminSpendingCategoryOther', text: i18n.t('other'), isAdmin: true },
      { firestoreId: 'spendingCategory1', text: i18n.t('login.spendingCategory1'), isAdmin: false },
      { firestoreId: 'spendingCategory2', text: i18n.t('login.spendingCategory2'), isAdmin: false },
      { firestoreId: 'spendingCategoryOther', text: i18n.t('other'), isAdmin: false },
    ],
    writeOffReasons: [
      { firestoreId: 'wasted', text: i18n.t('login.writeOffReasonWasted') },
      { firestoreId: 'internal-use', text: i18n.t('login.writeOffReasonInternalUse') },
      { firestoreId: 'gift', text: i18n.t('login.writeOffReasonGift') },
      { firestoreId: 'other', text: i18n.t('other') },
    ],
    returnReasons: [
      { firestoreId: 'mistake', text: i18n.t('login.returnReasonMistake') },
      { firestoreId: 'not-like', text: i18n.t('login.returnReasonNotLike') },
      { firestoreId: 'bad-good', text: i18n.t('login.returnReasonBadGood') },
      { firestoreId: 'other', text: i18n.t('other') },
    ],
    productModifiers: [
      { firestoreId: 'universal', name: i18n.t('login.universalModifiers'), activeInStore: { [storeRef.id]: true }, data: [] },
    ],
    loyaltySettings: {
      cashbackEnabled: loyaltyType?.id === 'cashback',
      punchCardEnabled: loyaltyType?.id === 'punchCard',
      defaultBonusLevel: loyaltyType?.id === 'cashback' ? parseInt(defaultBonusLevel?.id) : 1,
      punchCardCapacity: loyaltyType?.id === 'punchCard' ? parseInt(punchCardCapacity?.id) : 7,
    },
    // ATL-938 — the same object the Firestore batch wrote, not a second copy of it.
    organizationConstants: seededOrganizationConstants,
    saleSettings: [
      {
        storeFirestoreId: storeRef.id,
        settings: {
          deliveryPrice: 0,
          deliveryPaidByCustomer: true,
          deliveryIncludeInReceipt: true,
          deliveryPaymentSourceWallet: null,
          defaultDeliverySpendingCategory: null,
          showSaveCartForLater: true,
          allowMultipleProductSelection: true,
          dateFieldVisible: false,
          storeFieldVisible: false,
          tableNumberFieldVisible: false,
          commentFieldVisible: false,
          showTotalPurchasePriceInCart: false,
          showPurchasePriceInEditCartItem: false,
          mergeSimilarItems: true,
        },
      },
    ],
    // ATL-802: mirror of the Firestore `constants/recurringSpendings` seed above — same id, same
    // values, same instant. `firestoreId` is the Firestore item's literal `id`, exactly as every other
    // seeded constant here does it (`spendingCategory1`, `admin`, `5%`, …); it is what a later
    // re-migration will key this row on, and what the edit/delete paths address it by. `date` goes
    // over the wire as the ISO form of the very Date stored in Firestore.
    recurringSpendings: [
      {
        firestoreId: defaultRecurringSpending.id,
        name: defaultRecurringSpending.name,
        amount: defaultRecurringSpending.amount,
        date: defaultRecurringSpending.date.toISOString(),
        store: defaultRecurringSpending.store,
        type: defaultRecurringSpending.type,
      },
    ],
    productCategories: [
      { firestoreId: productCategoryRef.id, name: i18n.t('other'), order: 1 },
    ],
    suppliers: [
      { firestoreId: supplierRef.id, name: i18n.t('other'), active: true },
    ],
    products: [
      {
        firestoreId: productRef.id,
        name: i18n.t('login.testProduct'),
        activeInStore: { [storeRef.id]: true },
        barcode: '200001',
        categoryFirestoreId: productCategoryRef.id,
        supplierFirestoreId: supplierRef.id,
        purchasePrice: 1000,
        isHiddenInNewSale: false,
        salePrices: { [storeRef.id]: 1500 },
        storeStock: { [storeRef.id]: 0 },
        type: false,
      },
    ],
    checklists: [
      {
        firestoreId: checklistId,
        storeFirestoreId: storeRef.id,
        checklistType: 'seller',
        // ATL-1225: the SAME array and the SAME instant the Firestore batch above wrote. This
        // used to be a second copy of the literal plus a fresh `new Date()`, taken after
        // `await batch.commit()` — so the mirrored `date` was a different instant from
        // Firestore's, and the second-granularity compare in `checklists.service.ts` reported it
        // as a `critical` difference on any signup whose two reads straddled a second boundary.
        content: checklistContent,
        date: signupCreatedAt.toISOString(),
      },
    ],
  };
  // ATL-1077: the context is what makes this write RECOVERABLE, and it has to be passed explicitly.
  // On failure `bestEffortDualWrite` falls back to `getOrganization()?.id` for the org, which is
  // null here by construction — the org being created is the one that would populate the atom — so
  // the outbox refused every failed signup under its `no-organization` rule and the organization was
  // lost with no retry anywhere. Measured: 12 of 60 orgs created in the 30 days to 2026-09-01 (20%)
  // have no Supabase row; 4 of them trade. `POST /organizations` is `FirebaseTokenOnlyGuard`, not
  // `FirebaseAuthGuard`, so the replay does not need a Supabase user row to exist first.
  const response = await bestEffortDualWrite(
    'organizations.create',
    () => api.post('/organizations', createOrgPayload),
    { firestoreId: orgRef.id, orgFirestoreId: orgRef.id },
  );
  if (response?.data?.warnings?.length) {
    console.warn('Organization created with warnings:', response.data.warnings);
    Sentry.captureMessage('Organization created with warnings', {
      level: 'warning',
      extra: { warnings: response.data.warnings, organizationId: response.data.organizationId },
    });
  }

  // Clear persisted IDs after successful creation
  await AsyncStorage.multiRemove(['pending_org_id', 'pending_store_id']);

  // ASO-AN-001: hand the caller the identity this signup just minted. The funnel screen has to
  // establish the PostHog person BEFORE it captures `user_signed_up`, and returning the ids is what
  // lets it do that from the value it already awaited rather than by re-reading global auth state
  // and hoping the right user is on it. `authUserCred.user.uid` is the stable Firebase UID; it is
  // present on both the create and the email-already-in-use sign-in branch above.
  return { uid: authUserCred?.user?.uid ?? null, organizationId: orgRef.id };
}

export const checkEmailAvailability = async (email: string) => {
  try {
    let signInMethods
    if (Platform.OS === 'web') {
      signInMethods = await fetchSignInMethodsForEmail(auth, email);
    } else {
      signInMethods = await auth.fetchSignInMethodsForEmail(email);
    }
    return signInMethods.length === 0; // Email is available if there are no sign-in methods
  } catch (error: unknown) {
    console.error('Error checking email availability:', error);
    return false; // Assume email is taken in case of error
  }
}

export const getClientSalesPaginated = async (db: FirestoreDb, clientID: string, previousLastDocument: string | null = null, pageSize = 10) => {
  if (shouldUseSupabase()) {
    try {
      // The cursor is the absolute offset of the next page (ATL-1469). It used to be the size of
      // the page just read, so every page after the second re-requested offset `pageSize`.
      const offset = previousLastDocument ? parseInt(previousLastDocument) || 0 : 0
      const params: Record<string, any> = {
        clientId: clientID,
        limit: pageSize,
        offset,
      }

      // Using typed API - returns SaleResponse[] directly (validated)
      const rawSales = await salesApi.getSales(params)
      const sales = rawSales.map(sale => transformApiItem(sale, 'sales'))

      // Calculate hasMore based on response length
      const hasMore = sales.length === pageSize

      return {
        docs: sales,
        lastDocument: hasMore && sales.length > 0 ? String(offset + sales.length) : null,
        hasMore,
      }
    } catch (error: unknown) {
      console.error('Error fetching client sales from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales', function: 'getClientSalesPaginated' },
        extra: {
          clientID,
          pageSize,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  let querySnapshot = db.collection('sales')
    .where('clientId', '==', clientID)
    .orderBy('saleDate', 'desc')

  if (previousLastDocument) {
    querySnapshot = querySnapshot.startAfter(previousLastDocument as any)
  }
  
  querySnapshot = querySnapshot.limit(pageSize)
  
  const result = await querySnapshot.get()
  
  

  const docs: any[] = []
  result.forEach(function (doc: any) {
    const snap = doc.data() as any
    if (snap) {
      snap.id = doc.id
      snap.ref = doc.ref
      docs.push(snap)
    }
  })

  return {
    docs,
    lastDocument: result.docs.length > 0 ? result.docs[result.docs.length - 1] : null,
    hasMore: result.docs.length === pageSize
  }
}

export const getClientSalesDateRange = async (db: FirestoreDb, clientID: string) => {
  if (shouldUseSupabase()) {
    try {
      // Fetch all sales for the client to find date range. ATL-1477: ALL of them — a single
      // `limit: 1000` request reported the 1,000th-newest sale as the client's first purchase.
      const sales = await getWholeList('/sales', { clientId: clientID }, newestFirst('saleDate'))

      if (sales.length === 0) {
        return { earliestDate: null, latestDate: null }
      }

      // Find earliest and latest dates
      let earliestDate: Date | null = null
      let latestDate: Date | null = null

      sales.forEach((sale) => {
        const saleDate = typeof sale.saleDate === 'string' ? new Date(sale.saleDate) : null
        if (saleDate) {
          if (!earliestDate || saleDate < earliestDate) {
            earliestDate = saleDate
          }
          if (!latestDate || saleDate > latestDate) {
            latestDate = saleDate
          }
        }
      })

      return { earliestDate, latestDate }
    } catch (error: unknown) {
      console.error('Error fetching client sales date range from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'sales', function: 'getClientSalesDateRange' },
        extra: {
          clientID,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const collectionPath = 'sales'
  const whereConditions: WhereCondition[] = [
    { field: 'clientId', operator: '==', value: clientID }
  ]

  // First check if we have any sales
  const aggregations: AggregationConfig[] = [{ type: 'count', alias: 'totalSales' }]
  const countResult = await getUniversalAggregation(collectionPath, whereConditions, aggregations)

  if (countResult.totalSales === 0) {
    return { earliestDate: null, latestDate: null }
  }
  
  // Get the earliest and latest sale dates for frequency calculation
  const salesRef = db.collection(collectionPath).where('clientId', '==', clientID)
  const [earliestSnapshot, latestSnapshot] = await Promise.all([
    salesRef.orderBy('saleDate', 'asc').limit(1).get(),
    salesRef.orderBy('saleDate', 'desc').limit(1).get()
  ])
  

  
  let earliestDate = null
  let latestDate = null
  
  if (!earliestSnapshot.empty) {
    const data = earliestSnapshot.docs[0]?.data() as any
    earliestDate = data?.saleDate?.toDate() ?? null
  }

  if (!latestSnapshot.empty) {
    const data = latestSnapshot.docs[0]?.data() as any
    latestDate = data?.saleDate?.toDate() ?? null
  }
  
  return { earliestDate, latestDate }
}

export const dailyStatIncrementsForComplexSales = (sales: any[], forFirebase: boolean, negative: boolean) => {
  const saleStatFields: Record<string, any> = {}
  for (const info of sales) {
    saleStatFields['saleAmount'] = (saleStatFields['saleAmount'] || 0) + info.finalPrice
    saleStatFields['saleQuantity'] = (saleStatFields['saleQuantity'] || 0) + 1
    saleStatFields['salesPurchaseAmount'] = (saleStatFields['salesPurchaseAmount'] || 0) + info.finalExpensesPrice
    if (info.client != null && info.paymentMethod) {
      saleStatFields['bonusClients'] = (saleStatFields['bonusClients'] || 0) + 1
    }
    // payment methods, wallets and bank percent
    if (info.payments) {
      for (const payment of info.payments) {
        if (!payment.paymentMethod) continue
        saleStatFields[payment.paymentMethod.id + 'SalesAmountPaid'] = (saleStatFields[payment.paymentMethod.id + 'SalesAmountPaid'] || 0) + (payment.amount || 0)
        saleStatFields[payment.paymentMethod.id + 'SalesQuantityPaid'] = (saleStatFields[payment.paymentMethod.id + 'SalesQuantityPaid'] || 0) + 1
        const walletId = payment.paymentMethod.destinationWalletForStore?.[info.store]?.id
        if (walletId) {
          saleStatFields[walletId + 'SalesAmountPaid'] = (saleStatFields[walletId + 'SalesAmountPaid'] || 0) + (payment.amount || 0)
          saleStatFields[walletId + 'SalesQuantityPaid'] = (saleStatFields[walletId + 'SalesQuantityPaid'] || 0) + 1
        }
        // bank percent / commission
        if (payment.paymentMethod.comission && payment.paymentMethod.comission > 0) {
          saleStatFields['bankPercent'] = (saleStatFields['bankPercent'] || 0) + ((payment.amount || 0) * payment.paymentMethod.comission/100)
        }
      }
    }
    // by cashier
    saleStatFields[info.createdBy + 'SalesAmount'] = (saleStatFields[info.createdBy + 'SalesAmount'] || 0) + info.finalPrice
    saleStatFields[info.createdBy + 'SalesQuantity'] = (saleStatFields[info.createdBy + 'SalesQuantity'] || 0) + 1
  }
  if (negative) {
    for (const key in saleStatFields) {
      saleStatFields[key] = 0 - saleStatFields[key]
    }
  }
  if (forFirebase) {
    for (const key in saleStatFields) {
      saleStatFields[key] = fieldValue.increment(saleStatFields[key])
    }
  }
  return saleStatFields
}

/**
 * A pay-later sale is paid.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam. Firestore mode runs master's body verbatim
 * (`firestoreFirst`). Supabase mode (unreachable while any W writer is unconverted) fiscalizes exactly as
 * the body does, then queues the `POST /sales/:id/payment` the body's dual-write sends (`markSalePaidDto`,
 * the same function), identified by the payment's TRANSACTION document as the body's outbox entry is
 * (stage 2b), with the day's payment totals (`paidSaleStatIncrements`, the batch's own numbers) and the
 * sale row's payment fields as local effects. ATL-1542, AWAITING BEKS: `mark_sale_paid` applies no client
 * effects (the backend applied them when the unpaid sale was created), while this body applies the
 * client's purchase totals, bonus and punch card now — P9 changes neither side (§17 "ATL-1542").
 */
export const markSalePaid = (
  db: FirestoreDb,
  info: Sale,
  paymentInfo: { paymentMethod: PaymentMethod; dailyStatID: string; paymentDate: Date; fiscalInfo?: FiscalInfo; transactionId?: string },
  fiscalOperator: unknown = null,
  OFDCredentials: unknown = null
): Promise<void> =>
  writeW({
    label: 'sales.markSalePaid',
    kind: 'create',
    offline: 'queue',
    firestoreFirst: async () => {
      const batch = rootDb.batch()
      const transactionRef = db.collection('transactions').doc()
      // One instant for the income document, the day's stats and the Supabase mirror.
      const paymentDate = paymentInfo.paymentDate || new Date()

      // update bonuses for client
      if (info.client != null) {
        // ATL-911 — the PURCHASE fields are written once, for every client sale. They used to sit
        // inside the two loyalty branches, which meant:
        //
        //   neither flag set  → NOTHING was written at all. No total, no lastPurchaseDate, and no
        //                       lastPurchaseSource — for a perfectly ordinary paid sale.
        //   both flags set    → totalPurchasedValue incremented TWICE.
        //
        // The first case is the explanation for the client-aggregate samples reading fs=0 against
        // sb=139/125/139 — Firestore simply never recorded those purchases against the client. An
        // earlier reading blamed a NULL payment_method_id; that premise was falsified.
        //
        // Loyalty balances stay in their own branches below: those genuinely only move when the
        // corresponding programme is enabled.
        batch.update(db.collection('clients').doc(info.client.id), {
          totalPurchasedValue: fieldValue.increment(info.finalTotalCartPrice),
          lastPurchaseDate: convertToDate(info.saleDate),
          lastPurchaseSource: info.store,
        })
        if (info.cashbackEnabled) {
          batch.update(db.collection('clients').doc(info.client.id), {
            bonusValue: fieldValue.increment((info.bonusAccumulation || 0) - (info.bonusReduction || 0)),
          })
        }
        if (info.punchCardEnabled) {
          // find items that have punchcard enabled
          let punchCardValueAccumulation = 0
          info.cartItems.forEach((item) => {
            if (item.punchCardEnabled) {
              punchCardValueAccumulation += item.quantity
            }
          })
          if ((info.punchCardReduction || 0) > 0) { // if already getting free item with this sale
            punchCardValueAccumulation = punchCardValueAccumulation - 1 - ((info.punchCardCapacity || 1) - 1)
          }
          batch.update(db.collection('clients').doc(info.client.id), {
            punchCardValue: fieldValue.increment(punchCardValueAccumulation),
          })
        }
      }

      // update dailyStats — P9: the numbers are `paidSaleStatIncrements` (the Supabase-mode plan's local
      // effects are the same function), wrapped here in Firestore increments in the same field order.
      const statDocName = paymentInfo.dailyStatID
      const saleStatFields: Record<string, unknown> = {}
      for (const [field, value] of Object.entries(paidSaleStatIncrements(info, paymentInfo))) {
        saleStatFields[field] = fieldValue.increment(value)
      }
      // payment methods, wallets and save payment transaction
      const walletId = paymentInfo.paymentMethod?.destinationWalletForStore?.[info.store]?.id
      batch.set(transactionRef, {
        linkedDocId: info.ref?.id || info.id,
        linkedDocType: 'sale',
        type: 'income',
        destinationWallet: walletId || '',
        value: info.finalTotalCartPrice,
        date: paymentDate,
        store: info.store,
        organizationId: info.organizationId,
        createdDate: new Date(),
      })
      batch.set(db.collection('dailyStats').doc(statDocName), {...saleStatFields,
        date: paymentDate,
        store: info.store,
        organizationId: info.organizationId},
      {merge: true})

      // save to OFD
      let check: Record<string, unknown> | undefined
      const typedFiscalOperator = fiscalOperator as { id: string } | null
      const typedOFDCredentials = OFDCredentials as Record<string, unknown>
      if (typedFiscalOperator?.id === 'light-kassa') {
        await loadLKTokensFromCache(info.store)
        await checkLKTokens(typedOFDCredentials, info.store)
        check = await saveFiscalOperation(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>, info.store)
        if (!check) {
          await setNewLKTokens(typedOFDCredentials, info.store)
          // ATL-1170 review: the retry's result used to be discarded, so a sale the OFD signed on the
          // SECOND attempt was still recorded with no `fiscalInfo` — the merchant then pressed retry in
          // Sales History and punched a DUPLICATE fiscal receipt for one sale.
          check = await saveFiscalOperation(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>, info.store)
        }
      } else if (typedFiscalOperator?.id === 'wipon-kassa') {
        check = await saveSale(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>) as Record<string, unknown> | undefined
        if (!check) {
          // ATL-1170 review: this used to retry with `{...credentials, accessToken: undefined}` when the
          // login failed — a request that can only 401 — and then throw the retry's receipt away. Both
          // halves are fixed: no login, no retry; a successful retry is the sale's `fiscalInfo`.
          const refreshed = await refreshCredentials(typedOFDCredentials)
          if (refreshed) {
            check = await saveSale(refreshed, {...info, ...paymentInfo} as Record<string, unknown>) as Record<string, unknown> | undefined
          }
        }
      }
      const updatedSaleInfo = {...paymentInfo, transactionId: transactionRef.id}
      if (check) {
        updatedSaleInfo.fiscalInfo = check
      }
      batch.update(info.ref!, updatedSaleInfo)
      await batch.commit()

      // ATL-1445 stage 2b — mirror the PAYMENT, not a sale edit. A PATCH /sales/:id used to set the
      // payment method with a `transactionId` that resolved to no row (the income was never written to
      // Supabase), so the sale was stored paid with no income behind it. POST /sales/:id/payment books
      // the income keyed by this transaction document's id, once, and links it to the sale; client
      // effects are not re-applied (the sale create already applied them, ATL-1542).
      //
      // The outbox identity is the TRANSACTION document, not the sale: a live write for the same
      // document id purges a queued replay, and a later sale edit (a fiscal retry) must not purge this
      // payment. `replaySafe`: a replay of a booked payment is a server no-op, and a skipped one that is
      // never replayed leaves the sale unpaid in Supabase for good.
      if (info.ref) {
        const saleId = info.ref.id
        await bestEffortDualWrite(
          'sales.markSalePaid',
          () => salesApi.markSalePaid(saleId, markSalePaidDto(transactionRef.id, info, paymentInfo, paymentDate, check)),
          { firestoreId: transactionRef.id, replaySafe: true },
        )
      }
    },
    // Supabase mode only (P9). The fiscal receipt is issued exactly as the body issues it
    // (`fiscalizeSalePayment`, held to the body by `firebaseSaleWritersSupabase.p9.test.js`) — but only in
    // `beforeAppend` (review 1, S2-c): the plan builds the payment WITHOUT the receipt, so every refusal
    // that does not depend on it (the reference, the local effects, the session pin, the v1-migration
    // policy, the store's rules for the request) runs first, while no receipt exists. The branch then
    // calls `beforeAppend` once, immediately before the durable append, and queues the payment it
    // returns, carrying the receipt; it is never sent here.
    plan: async () => {
      // The body cannot book a payment for a sale with no document either (`batch.update(info.ref!)`
      // throws); here it is refused BEFORE a receipt is issued for it.
      if (!info.ref) throw new Error('markSalePaid: the sale has no document reference')
      const saleId = info.ref.id
      const transactionRef = db.collection('transactions').doc()
      const paymentDate = paymentInfo.paymentDate || new Date()
      const build = async (check: Record<string, unknown> | undefined) => ({
        request: await buildOutboxRequest((client) =>
          createSalesApi(client).markSalePaid(saleId, markSalePaidDto(transactionRef.id, info, paymentInfo, paymentDate, check))),
        // A payment the local view could not show (a NaN total) is refused.
        effects: assertLocalEffects(paidSaleLocalEffects(saleId, info, paymentInfo, paymentDate, transactionRef.id, check)),
      })
      const unfiscalized = await build(undefined)
      return {
        documentId: transactionRef.id,
        relatedIds: info.client?.id ? [saleId, info.client.id] : [saleId],
        request: unfiscalized.request,
        effects: unfiscalized.effects,
        beforeAppend: async () => build(await fiscalizeSalePayment(info, paymentInfo, fiscalOperator, OFDCredentials)),
        fromAck: () => undefined,
      }
    },
  })

/**
 * The `POST /sales/:id/payment` body of a pay-later payment (stage 2b): `markSalePaid`'s dual-write and its
 * Supabase-mode plan both send it.
 */
const markSalePaidDto = (
  transactionId: string,
  info: Sale,
  paymentInfo: { paymentMethod: PaymentMethod; dailyStatID: string },
  paymentDate: Date,
  check: Record<string, unknown> | undefined,
): Parameters<typeof salesApi.markSalePaid>[1] => ({
  transactionId,
  paymentMethod: { ...paymentInfo.paymentMethod },
  paymentDate: paymentDate.toISOString(),
  dailyStatID: paymentInfo.dailyStatID,
  ...(check && { fiscalInfo: check }),
  // A string, always: this request books MONEY, and a legacy numeric phone must not be the
  // 400 that drops it (the DTO validates clientPhone as a string).
  ...(info.client?.phoneNumber && { clientPhone: String(info.client.phoneNumber) }),
})

/**
 * The day's totals a payment adds (raw numbers, in the batch's field order): the client's loyalty counters
 * when the sale has a client, and the payment method's and its wallet's amount and count. `markSalePaid`'s
 * batch wraps each in a Firestore increment; its Supabase-mode plan books them as local effects. The sale's
 * own amount and count were added when it was created.
 */
const paidSaleStatIncrements = (
  info: Sale,
  paymentInfo: { paymentMethod: PaymentMethod },
): Record<string, number> => {
  const fields: Record<string, number> = {}
  if (info.client != null) {
    fields['bonusClients'] = 1
    fields['bonusAccumulation'] = info.bonusAccumulation || 0
    fields['bonusReduction'] = info.bonusReduction || 0
    fields['punchCardReduction'] = info.punchCardReduction || 0
  }
  const walletId = paymentInfo.paymentMethod?.destinationWalletForStore?.[info.store]?.id
  fields[paymentInfo.paymentMethod.id + 'SalesAmount'] = info.finalTotalCartPrice
  fields[paymentInfo.paymentMethod.id + 'SalesQuantity'] = 1
  if (walletId) {
    fields[walletId + 'SalesAmount'] = info.finalTotalCartPrice
    fields[walletId + 'SalesQuantity'] = 1
  }
  return fields
}

/**
 * The local effects of a payment (P9): the day's payment totals on the payment's `dailyStatID` (the body
 * books the same increments there), and the sale row's payment fields. A `dailyStatID` that is not a
 * `d-M-y-<store>` id contributes no totals rather than a guessed day.
 */
const paidSaleLocalEffects = (
  saleId: string,
  info: Sale,
  paymentInfo: { paymentMethod: PaymentMethod; dailyStatID: string },
  paymentDate: Date,
  transactionId: string,
  check: Record<string, unknown> | undefined,
): LocalEffects => {
  const businessDay = businessDayOfDailyStatId(paymentInfo.dailyStatID)
  const dailyStats: DailyStatEffect[] =
    businessDay !== null && typeof info.store === 'string' && info.store.length > 0
      ? [{
          storeId: info.store,
          dailyStatId: paymentInfo.dailyStatID,
          businessDay,
          increments: rawIncrements(paidSaleStatIncrements(info, paymentInfo)),
          assign: {},
        }]
      : []
  return {
    v: LOCAL_EFFECTS_VERSION,
    stock: [],
    dailyStats,
    records: [{
      collection: 'sales',
      id: saleId,
      op: 'patch',
      store: typeof info.store === 'string' && info.store.length > 0 ? info.store : null,
      date: isoInstantOf(info.saleDate),
      fields: toDisplayObject({
        paymentMethod: paymentInfo.paymentMethod,
        dailyStatID: paymentInfo.dailyStatID,
        paymentDate,
        transactionId,
        ...(check && { fiscalInfo: check }),
      }),
    }],
  }
}

/**
 * The fiscal receipt of a pay-later payment, exactly as `markSalePaid`'s Firestore body issues it (the body
 * keeps its own copy inline, so Firestore mode is byte- and microtask-identical to master;
 * `firebaseSaleWritersSupabase.p9.test.js` holds this copy to it call by call). Supabase mode only.
 */
const fiscalizeSalePayment = async (
  info: Sale,
  paymentInfo: { paymentMethod: PaymentMethod; dailyStatID: string; paymentDate: Date; fiscalInfo?: FiscalInfo; transactionId?: string },
  fiscalOperator: unknown,
  OFDCredentials: unknown,
): Promise<Record<string, unknown> | undefined> => {
  let check: Record<string, unknown> | undefined
  const typedFiscalOperator = fiscalOperator as { id: string } | null
  const typedOFDCredentials = OFDCredentials as Record<string, unknown>
  if (typedFiscalOperator?.id === 'light-kassa') {
    await loadLKTokensFromCache(info.store)
    await checkLKTokens(typedOFDCredentials, info.store)
    check = await saveFiscalOperation(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>, info.store)
    if (!check) {
      await setNewLKTokens(typedOFDCredentials, info.store)
      check = await saveFiscalOperation(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>, info.store)
    }
  } else if (typedFiscalOperator?.id === 'wipon-kassa') {
    check = await saveSale(typedOFDCredentials, {...info, ...paymentInfo} as Record<string, unknown>) as Record<string, unknown> | undefined
    if (!check) {
      const refreshed = await refreshCredentials(typedOFDCredentials)
      if (refreshed) {
        check = await saveSale(refreshed, {...info, ...paymentInfo} as Record<string, unknown>) as Record<string, unknown> | undefined
      }
    }
  }
  return check
}

/**
 * Edit a sale's payment or purchase method (and the fields that come with it), moving its day's totals.
 *
 * ATL-1445 stage 3 P9 — through the `writeW` seam. Firestore mode runs master's body verbatim
 * (`firestoreFirst`); the totals are `saleEditStatIncrements`, wrapped in Firestore increments in the same
 * field order. Supabase mode queues the `PATCH /sales/:id` the body's dual-write sends (label
 * `sales.updateSale`, `saleWithStatsUpdatePayload`, the same function), with the moved totals and the
 * row's patch as local effects.
 */
export const updateSaleWithStats = (
  db: FirestoreDb,
  sale: Sale,
  info: Partial<Sale> & { store?: string; totalPrice?: number }
): Promise<void> =>
  writeW({
    label: 'sales.updateSale',
    kind: 'update',
    offline: 'queue',
    firestoreFirst: async () => {
      const batch = rootDb.batch()
      batch.update(sale.ref, info)

      // update dailyStats
      //
      // ATL-1013: this had NO `dailyStatID` fallback at all — it always recomputed the doc id
      // from the device's calendar day, so it could re-key a sale's payment-method counters
      // onto a day other than the one saveNewSale incremented (`doc(info.dailyStatID)`).
      // Prefer the stored id; otherwise recompute it on the store's business day.
      // ATL-1013: raw, not `?? new Date()` — same reason as deleteSaleReturn. An earlier round kept
      // the today-fallback to preserve pre-diff behaviour exactly; that preserved a silent
      // wrong-day write, so the guard wins.
      const statDocName = dailyStatIDForRecord(sale.dailyStatID, sale.store, sale.saleDate as DateLike)
      // P9: the numbers are `saleEditStatIncrements` (the Supabase-mode plan's local effects are the same
      // function), wrapped in Firestore increments in the same field order.
      const saleStatFields: Record<string, unknown> = {}
      for (const [field, value] of Object.entries(saleEditStatIncrements(sale, info))) {
        saleStatFields[field] = fieldValue.increment(value)
      }
      if (info.paymentMethod != sale.paymentMethod) {
        const saleWalletId = sale.paymentMethod?.destinationWalletForStore?.[sale.store]?.id
        if (saleWalletId) {
          batch.update(db.collection('transactions').doc(sale.transactionId), { destinationWallet: saleWalletId })
        }
      }

      batch.update(db.collection('dailyStats').doc(statDocName), saleStatFields)

      await batch.commit()

      // Dual write update to Supabase
      if (!sale.ref) return
      const updatePayload = saleWithStatsUpdatePayload(info)
      await bestEffortDualWrite('sales.updateSale', () => salesApi.updateSale(sale.ref!.id, updatePayload as Parameters<typeof salesApi.updateSale>[1]));
    },
    plan: async () => {
      // The body cannot edit a sale with no document either (`batch.update(sale.ref, …)` throws).
      if (!sale.ref) throw new Error('updateSaleWithStats: the sale has no document reference')
      const saleId = sale.ref.id
      const statDocName = dailyStatIDForRecord(sale.dailyStatID, sale.store, sale.saleDate as DateLike)
      const increments = saleEditStatIncrements(sale, info)
      const businessDay = businessDayOfDailyStatId(statDocName)
      const updatePayload = saleWithStatsUpdatePayload(info)
      const effects = assertLocalEffects({
        v: LOCAL_EFFECTS_VERSION,
        stock: [],
        dailyStats:
          businessDay !== null && typeof sale.store === 'string' && sale.store.length > 0
            ? [{ storeId: sale.store, dailyStatId: statDocName, businessDay, increments: rawIncrements(increments), assign: {} }]
            : [],
        records: [{
          collection: 'sales',
          id: saleId,
          op: 'patch',
          store: typeof sale.store === 'string' && sale.store.length > 0 ? sale.store : null,
          date: isoInstantOf(sale.saleDate),
          fields: toDisplayObject(updatePayload),
        }],
      })
      const request = await buildOutboxRequest((client) =>
        createSalesApi(client).updateSale(saleId, updatePayload as Parameters<typeof salesApi.updateSale>[1]))
      return { documentId: saleId, request, effects, fromAck: () => undefined }
    },
  })

/**
 * The day's totals a sale edit moves (raw numbers, in the batch's field order): the amount and count from
 * the old payment method (and its wallet) to the new one, and likewise between purchase methods.
 * `updateSaleWithStats`' batch wraps each in a Firestore increment; its Supabase-mode plan books them as
 * local effects. The methods are compared by reference (`!=`), as the batch always has.
 */
const saleEditStatIncrements = (
  sale: Sale,
  info: Partial<Sale> & { store?: string },
): Record<string, number> => {
  const fields: Record<string, number> = {}
  if (info.paymentMethod != sale.paymentMethod) {
    const infoStore = info.store || sale.store
    const infoWalletId = info.paymentMethod?.destinationWalletForStore?.[infoStore]?.id
    const saleWalletId = sale.paymentMethod?.destinationWalletForStore?.[sale.store]?.id

    fields[info.paymentMethod!.id + 'SalesAmount'] = sale.finalTotalCartPrice
    fields[info.paymentMethod!.id + 'SalesQuantity'] = 1
    fields[sale.paymentMethod!.id + 'SalesAmount'] = -sale.finalTotalCartPrice
    fields[sale.paymentMethod!.id + 'SalesQuantity'] = -1
    if (infoWalletId) {
      fields[infoWalletId + 'SalesAmount'] = sale.finalTotalCartPrice
      fields[infoWalletId + 'SalesQuantity'] = 1
    }
    if (saleWalletId) {
      fields[saleWalletId + 'SalesAmount'] = -sale.finalTotalCartPrice
      fields[saleWalletId + 'SalesQuantity'] = -1
    }
  }
  if (info.purchaseMethod != sale.purchaseMethod && info.purchaseMethod && sale.purchaseMethod) {
    fields[info.purchaseMethod.id + 'SalesAmount'] = sale.finalTotalCartPrice
    fields[info.purchaseMethod.id + 'SalesQuantity'] = 1
    fields[sale.purchaseMethod.id + 'SalesAmount'] = -sale.finalTotalCartPrice
    fields[sale.purchaseMethod.id + 'SalesQuantity'] = -1
  }
  return fields
}

/** The `PATCH /sales/:id` body of a sale edit with totals: `updateSaleWithStats`' dual-write and its plan. */
const saleWithStatsUpdatePayload = (info: Partial<Sale> & { totalPrice?: number }): Record<string, unknown> => ({
  ...(info.paymentMethod && { paymentMethod: info.paymentMethod }),
  ...(info.purchaseMethod && { purchaseMethod: info.purchaseMethod }),
  ...(info.cartItems && { cartItems: info.cartItems }),
  ...(info.totalPrice !== undefined && { totalPrice: info.totalPrice }),
  ...(info.totalPurchasePrice !== undefined && { totalPurchasePrice: info.totalPurchasePrice }),
  ...(info.finalTotalCartPrice !== undefined && { finalTotalCartPrice: info.finalTotalCartPrice }),
  ...(info.comment !== undefined && { comment: info.comment }),
  ...(info.tableNumber !== undefined && { tableNumber: info.tableNumber }),
  ...(info.clientId !== undefined && { clientId: info.clientId }),
  ...(info.client !== undefined && { client: info.client }),
  ...(info.clientPhone !== undefined && { clientPhone: info.clientPhone }),
})

export const saveSaleReturn = async (
  db: FirestoreDb,
  saleRef: FirestoreRef,
  info: Sale & { returnInfo: ReturnInfo },
  saleID: string,
  saleCashierUID: string,
  fiscalOperator: unknown = null,
  OFDCredentials: unknown = null
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveSaleReturn')

  // ATL-1798: the ONE instant the refund is booked on, in both stores. The Supabase leg is dated by
  // `create_sale_return_atomic` from `returnDto.returnDate` below; the Firestore leg used to take a
  // second `new Date()` after the awaits below, so the two ledgers disagreed by the length of the
  // save (655 ms on Кэри Лэнд's HyegJMJ8Op39TdOM5hM0, which the read-cutover gate blocks on) — and a
  // return saved across Almaty midnight would land on different days. The leg's `date` is the
  // return's own date, as every other leg's is (sale → saleDate, spending → info.date); its
  // `createdDate` stays the save time.
  // No `?? new Date()`: the return document spreads `returnInfo`, so a return without a usable date
  // would be dateless in Firestore while both legs and Supabase's `return_date` took the save time —
  // refused here, before anything is read or written (same reasoning as ATL-1013 in deleteSaleReturn).
  const returnInstant = convertToDate(info.returnInfo.date as unknown as Date | null)
  if (!returnInstant || Number.isNaN(returnInstant.getTime())) {
    throw new Error(
      `saveSaleReturn: returnInfo.date is missing or not a valid date (${String(info.returnInfo.date)}) — refusing to book a refund on an unknown day`
    )
  }
  const returnInstantIso = returnInstant.toISOString()

  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)

  // Resolve saleRef to a real Firestore DocumentReference (may be a plain {id} object from Supabase)
  const resolvedSaleRef = getFirestoreRef({ id: saleID, ref: saleRef }, 'sales')

  // ── ATL-959: the cumulative refund cap, on the FIRESTORE leg ───────────────────────────────────
  // Supabase has refused an over-refund since ATL-573 ("Fix G" in create_sale_return_atomic).
  // Firestore refused nothing, and Firestore commits FIRST and cannot be rolled back — so a return
  // re-entered a second time wrote a second full refund document here while the mirror correctly
  // refused it, and the dual-write being non-fatal meant the till showed success. Sales
  // `CB6GHF0ddCUUTjLJFHK7` (3.00) and `fhpb0q4V5KL3ysMt9tFV` (9.00) each carry two full refunds in
  // Firestore for this reason.
  //
  // Both reads are deliberately fresh rather than taken from the screen's `route.params.sale`:
  // that object is a snapshot from the sales list and is exactly what a double submit re-uses.
  // Firestore HARD-deletes a return document (`deleteSaleReturn` below), so the query result is
  // already the live set — the same set Supabase means by `deleted_at IS NULL`.
  //
  // Errors are NOT caught. A read failure here is not a state in which it is safe to commit an
  // unbounded refund, and the batch commit that follows needs the same connection anyway.
  //
  // MEASURED 2026-08-25 — the `returns` LIST below is permitted by the deployed Firestore rules.
  // It is worth stating because this is in practice the FIRST production LIST of
  // `organizations/{org}/returns`: `getReturnsOnDateRange` has no callers, and SaleDetailScreen's
  // read is a `.doc().get()`, which rules gate as `get`, separately from `list`. The rules are not
  // in this repo, so it was settled by probing prod read-only with the real Firebase JS client
  // (rules ARE enforced for an ID token; the admin SDK would have bypassed them), signed in via a
  // minted custom token as a real CASHIER (`4fD4D…YjHeoyEoZ03`) of a real trading org, ИП Смак
  // (`ZE8mrbJAPVyzLttjtg47`), and repeated as an admin of the same org:
  //   (a) LIST `returns` where saleID == '<sale with a return>'  → ALLOW, 1 doc, correct id
  //   (a') LIST `returns` where saleID == '<sale with none>'     → ALLOW, 0 docs (the common path)
  //   (b) GET  `returns/<id>` (control)                          → ALLOW
  // Corroborated on a second, independent channel (raw Firestore REST, no SDK, no auth token —
  // HTTP 200). The harness was proved able to report a refusal: the same query with an
  // `orderBy` on an unindexed field returned a real server-side `failed-precondition`, so a
  // `permission-denied` would have surfaced the same way. That also confirms the query needs no
  // composite index — the automatic single-field index on `saleID` serves it.
  // Read the result narrowly: the LIST is allowed because the deployed ruleset denies nothing at
  // all — the probe's own negative control (an UNAUTHENTICATED read of the same document) also
  // succeeded, which is filed as ATL-965. So if those rules are ever tightened, this call is one
  // of the ones to re-probe before shipping the change.
  const [saleSnapshot, priorReturnsSnapshot] = await Promise.all([
    resolvedSaleRef.get(),
    db.collection('returns').where('saleID', '==', saleID).get(),
  ])
  const priorReturns: PriorReturnFields[] = []
  priorReturnsSnapshot.forEach((doc) => {
    const data = doc.data()
    // A document with no readable payload contributes nothing; `priorReturnedTotal` treats a
    // missing amount as 0 rather than NaN for the same reason — NaN would make the comparison
    // below always false and silently disable the whole rule.
    if (data) priorReturns.push(data as PriorReturnFields)
  })
  // The basis is `refundableSaleTotal` — the LARGER of the sale's declared total and its charged
  // line total — and NOT `finalTotalCartPrice` alone. A full return requests
  // `Σ round(chargedLineAmount(cartItems))`, which is higher than `finalTotalCartPrice` by exactly
  // `discountAmount + bonusReduction + punchCardReduction`; bounding by the declared total alone
  // refuses a complete, correct return of every loyalty-bonus sale. See the measurements in
  // `returnLineAmount.ts`. The live sale document is preferred over `info.cartItems` so the basis
  // is the stored record rather than the screen's editable copy.
  //
  // The line candidate is rounded PER LINE for the same reason the request is: `totalReturnedAmount`
  // is `Σ returnedLineAmount()`, and `returnedLineAmount` ends in `Math.round(...)`. Bounding a
  // per-line-rounded ask with an unrounded sum lets each line round UP by as much as 0.5 against a
  // 0.01 tolerance — and THIS leg throws BEFORE `batch.commit()`, so on prod sale
  // `OKtOKPKAZAWb9QILgZhc` (asks 286, unrounded basis 285.60) the cashier could not make the refund
  // at all. 453 sales in the last 30 days are that shape; the rounded basis refuses 0 of them and
  // still refuses both double submits.
  const refundableTotal = refundableSaleTotal(
    (saleSnapshot.data() as RefundableSaleFields | undefined) ?? { cartItems: info.cartItems },
  )
  const priorTotal = priorReturnedTotal(priorReturns)
  const requestedRefund = Number(info.returnInfo.totalReturnedAmount)
  // The `refundableTotal !== null` half is redundant with the rule's own handling of a missing
  // basis and is written out anyway: it is what makes the refusal's `refundableTotal` a real number
  // rather than a nullable one, so the message can never read "already refunded 100 of null".
  if (
    refundableTotal !== null &&
    cumulativeRefundExceedsSale({
      refundableTotal,
      priorReturnedTotal: priorTotal,
      totalReturnedAmount: requestedRefund,
    })
  ) {
    const refusal = i18n.t('salesHistory.cumulativeRefundExceedsSale', {
      alreadyReturned: priorTotal,
      requested: requestedRefund,
      refundable: refundableTotal,
    })
    // `ReturnSaleScreen` renders `error.message` verbatim. `i18n.t` yields '' when the resource
    // bundle has not loaded, and an EMPTY refusal reads to the cashier exactly like the Return
    // button doing nothing — the same silent failure ATL-930's NaN produced. So the numbers are
    // always in the message, translated or not.
    throw cumulativeRefundExceededError(
      refusal ||
        `Cumulative refund exceeds sale total: ${priorTotal} of ${refundableTotal} already ` +
          `refunded, ${requestedRefund} requested.`,
      { refundableTotal, priorReturnedTotal: priorTotal, requestedRefund },
    )
  }

  // save return
  const newRef = db.collection('returns').doc()
  const returnInfo: ReturnInfo & { transactionId?: string; cartItems: CartItem[]; saleID: string } = {
    ...info.returnInfo,
    cartItems: info.cartItems,
    saleID: saleID,
  }

  // update sale with return info.
  // In Supabase-read mode info.cartItems arrive API-shaped — pseudo lines carry a unique id
  // + lineType instead of the legacy sentinel. The Firestore sale doc is the legacy source of
  // truth (and the Phase-5 read-rollback target), so restore the sentinel shape before writing
  // it back: never let the unique id / lineType leak into the doc where sentinel consumers read.
  // (Pre-existing: API-shaped items also lose categoryID/supplierID here — tracked separately.)
  const firestoreCartItems = (info.cartItems || []).map((item: any) => {
    if (isCustomLine(item) && item.id !== 'customProduct') {
      const restored = { ...item, id: 'customProduct', cartItemId: item.cartItemId || item.id }
      delete restored.lineType
      return restored
    }
    if (isDeliveryLine(item) && item.id !== 'delivery') {
      const restored = { ...item, id: 'delivery', cartItemId: item.cartItemId || item.id }
      delete restored.lineType
      return restored
    }
    return item
  })
  // ATL-2021: in Supabase read mode `info` is the typed API's read model, whose nested dates are
  // `{ toDate: fn, seconds, nanoseconds }` objects. Web Firestore refuses the function (every web
  // return of a post-cutover sale failed); native silently wrote them as maps. Both writes that carry
  // the read model — this one and the `returns` document below — store real Timestamps instead.
  batch.update(resolvedSaleRef, timestampLikeToFirestore({...info, cartItems: firestoreCartItems, returnId: newRef.id}))
  // update dailyStats
  const statDocName = info.returnInfo.dailyStatID
  const saleWithReturn = {
    ...info,
    returnInfo: info.returnInfo,
    cashierUID: saleCashierUID,
    store: info.store
  }
  const statFields = dailyStatIncrementsForReturns([saleWithReturn], true, false)

  // return money - update transaction and daily stat
  if (info.returnInfo.returnType == 1) {
    // save transaction
    const transactionRef = db.collection('transactions').doc()
    returnInfo.transactionId = transactionRef.id
    const walletId = info.returnInfo.returnMethod?.destinationWalletForStore?.[info.store]?.id
    if (!walletId) {
      console.warn('saveSaleReturn: No wallet ID found for return method')
    }
    batch.set(transactionRef, {
      linkedDocId: newRef.id,
      linkedDocType: 'return',
      type: 'expense',
      sourceWallet: walletId || '',
      value: info.returnInfo.totalReturnedAmount,
      date: returnInstant,
      store: info.store,
      organizationId: info.organizationId,
      createdDate: new Date(),
    })
  }
  // update stock if needed
  const storeStockField = 'storeStock.' + info.store
  if (info.returnInfo.returnType == 0 && !info.returnInfo.updateStock) {
    // exchange product and don't add the returned product back to stock
    for (const [_index, value] of info.cartItems.entries()) { // take products from stock
      if (value.isReturned) {
        if (value.isComposite && !value.manufacturedBeforeSale && value.ingredients) {
          for (const [_idx, ingredient] of value.ingredients.entries()) {
            const increment = 0 - ((value.returnedQuantity || 0) * ingredient.quantity)
            if (ingredient.ref || allProductsDict[ingredient.id]) {
              const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
              batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
              batch.set(ingredientRef.collection('movement').doc(), {
                productID: ingredient.id,
                type: 'return',
                previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
                increment,
                date: fieldValue.serverTimestamp(),
                linkedDocId: newRef.id,
                linkedDocDate: info.returnInfo.date,
                clientSaveDate: new Date(),
                store: info.store,
                organizationId: info.organizationId
              })
            }
          }
        } else if (value.ref || allProductsDict[value.id]) {
          const increment = 0 - (value.returnedQuantity || 0)
          const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
          batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
          batch.set(valueRef.collection('movement').doc(), {
            productID: value.id,
            type: 'return',
            previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: newRef.id,
            linkedDocDate: info.returnInfo.date,
            clientSaveDate: new Date(),
            store: info.store,
            organizationId: info.organizationId
          })
        }
      }
    }
  } else if (info.returnInfo.returnType == 0 && info.returnInfo.updateStock) {
    // exchange product and add the returned product back to stock - stock doesn't change
  } else if (info.returnInfo.returnType == 1 && info.returnInfo.updateStock) {
    // return money and add the returned product back to stock
    for (const [_index, value] of info.cartItems.entries()) {
      if (value.isReturned) {
        if (value.isComposite && !value.manufacturedBeforeSale && value.ingredients) {
          for (const [_idx, ingredient] of value.ingredients.entries()) {
            const increment = (value.returnedQuantity || 0) * ingredient.quantity
            if (ingredient.ref || allProductsDict[ingredient.id]) {
              const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
              batch.update(ingredientRef, {[storeStockField]: fieldValue.increment(increment)})
              batch.set(ingredientRef.collection('movement').doc(), {
                productID: ingredient.id,
                type: 'return',
                previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
                increment,
                date: fieldValue.serverTimestamp(),
                linkedDocId: newRef.id,
                linkedDocDate: info.returnInfo.date,
                clientSaveDate: new Date(),
                store: info.store,
                organizationId: info.organizationId })
            }
          }
        } else if (value.ref || allProductsDict[value.id]) {
          const increment = value.returnedQuantity || 0
          const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
          batch.update(valueRef, {[storeStockField]: fieldValue.increment(increment)})
          batch.set(valueRef.collection('movement').doc(), {
            productID: value.id,
            type: 'return',
            previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: newRef.id,
            linkedDocDate: info.returnInfo.date,
            clientSaveDate: new Date(),
            store: info.store,
            organizationId: info.organizationId })
        }
      }
    }
  } else if (info.returnInfo.returnType == 1 && !info.returnInfo.updateStock) {
    // return money and don't add the returned product back to stock - stock doesn't change
  }
  batch.set(db.collection('dailyStats').doc(statDocName), {...statFields,
    date: info.returnInfo.date,
    store: info.store,
    organizationId: info.organizationId
  }, {merge: true})
  batch.set(newRef, timestampLikeToFirestore(returnInfo)) // ATL-2021, see the sale update above

  

  // return on OFD
  let check: any
  const typedFiscalOp = fiscalOperator as { id: string } | null
  const typedOFDCreds = OFDCredentials as Record<string, unknown>
  if (info.returnInfo.returnType && typedFiscalOp) {
    const newInfo = {
      cartItems: info.cartItems,
      paymentMethod: info.returnInfo.returnMethod,
      finalTotalCartPrice: info.returnInfo.totalReturnedAmount,
      store: info.store
    }
    if (typedFiscalOp.id === 'light-kassa') {
      await loadLKTokensFromCache(info.store)
      await checkLKTokens(typedOFDCreds, info.store)
      check = await saveFiscalReturn(typedOFDCreds, newInfo as Record<string, unknown>, info.store)
      if (!check) {
        await setNewLKTokens(typedOFDCreds, info.store)
        // ATL-1170 review: the retry's result was discarded, so a return the OFD signed on the
        // second attempt was stored with no `returnFiscalInfo` at all.
        check = await saveFiscalReturn(typedOFDCreds, newInfo as Record<string, unknown>, info.store)
      }
    } else if (typedFiscalOp.id === 'wipon-kassa') {
      check = await saveReturn(typedOFDCreds, newInfo as Record<string, unknown>)
      if (!check) {
        // ATL-1170 review: retrying with `accessToken: undefined` could only 401, and the retry's
        // receipt was thrown away regardless. Guard the login, keep what it signs.
        const refreshed = await refreshCredentials(typedOFDCreds)
        if (refreshed) {
          check = await saveReturn(refreshed, newInfo as Record<string, unknown>)
        }
      }
    }
  }
  if (check != null) {
    batch.update(newRef, {returnFiscalInfo: check})
    batch.update(resolvedSaleRef, {returnFiscalInfo: check})
  }
  await commitGuardedCreate('returns.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  console.log('[saveSaleReturn] Starting Supabase dual-write for sale:', saleID);
  // ATL-2023: the dailyStats key this return was booked on above (`statDocName`), which the `returns`
  // document also stores (it spreads `returnInfo`). Both Supabase legs carry the SAME key: the return
  // row's `daily_stat_id` and the sale's `return_info.dailyStatID`. Without it the column stayed NULL
  // on every app-written return, and the reverse mirror had to DERIVE a key that can differ from the
  // one Firestore used. Read-mode independent: ReturnSaleScreen computes it the same way in both.
  const returnDailyStatID =
    typeof statDocName === 'string' && statDocName.length > 0 ? statDocName : undefined
  // Build return info for API - this should always run regardless of createSaleReturn success
  const returnInfoForApi = {
    returnType: info.returnInfo.returnType as number,
    updateStock: Boolean(info.returnInfo.updateStock),
    totalReturnedAmount: info.returnInfo.totalReturnedAmount as number,
    date: returnInstantIso,
    comment: (info.returnInfo.comment || info.returnInfo.returnComment) as string | undefined,
    returnComment: info.returnInfo.returnComment as string | undefined,
    returnReason: info.returnInfo.returnReason as unknown as Record<string, unknown> | undefined,
    cashier: info.returnInfo.cashier as string | undefined,
    store: (info.returnInfo.store || info.store) as string | undefined,
    organizationId: info.returnInfo.organizationId as string | undefined,
    ...(returnDailyStatID && { dailyStatID: returnDailyStatID }),
    ...(info.returnInfo.returnMethod && {
      returnMethod: info.returnInfo.returnMethod as unknown as Record<string, unknown>,
    }),
  };
  console.log('[saveSaleReturn] returnInfoForApi:', JSON.stringify(sanitizeForFirestore(returnInfoForApi)));

  // Create the return record in Supabase (fatal — throws on failure)
  const returnDto = {
    returnItems: info.cartItems
      // Delivery fees are not returnable products; custom lines ARE returnable now —
      // previously both were silently dropped here, so custom returns never reached
      // Supabase (drift). Works in both read modes: Firestore-read items carry the
      // legacy sentinel id, Supabase-read items carry lineType from the backend.
      .filter(item => item.isReturned && item.id && !isDeliveryLine(item))
      .map(item => {
        // Get item price - prefer returnedAmount, finalItemPrice, then salePrices for store
        const storePrice = item.salePrices?.[info.store] || 0;
        const itemPrice = item.returnedAmount || item.finalItemPrice || item.itemPriceWithoutModifiers || (storePrice * item.quantity) || item.price || 0;
        const unitPrice = item.quantity > 0 ? itemPrice / item.quantity : storePrice || item.price || 0;
        const storedItemPrice = item.itemPriceWithoutModifiers || item.finalItemPrice || (storePrice * item.quantity) || item.price || 0;
        const originalUnitPrice = item.quantity > 0 ? storedItemPrice / item.quantity : storePrice || item.price || 0;

        const base = {
          quantity: item.returnedQuantity || item.quantity,
          price: unitPrice,
          originalPrice: originalUnitPrice,
          refundAmount: item.returnedAmount || (unitPrice * (item.returnedQuantity || item.quantity || 0)),
          name: item.name,
          reason: item.returnReason,
        };

        if (isCustomLine(item)) {
          // No product behind a custom line: identity = the unique cart line id.
          // productId stays absent → backend stores null → return RPC skips stock.
          return { ...base, id: item.cartItemId || item.id, lineType: 'custom' as const };
        }
        return { ...base, productId: item.id };
      }),
    totalRefund: info.returnInfo.totalReturnedAmount || 0,
    returnDate: returnInstantIso,
    comment: info.returnInfo.comment,
    firestoreId: newRef.id,
    // ATL-821: id of the refund's Firestore `transactions` doc (set above, only for
    // returnType == 1 — a money refund). The backend stamps it onto the Supabase expense row so
    // the reconciler can see that row already exists instead of copying the doc in a second time.
    ...(returnInfo.transactionId && { transactionId: returnInfo.transactionId }),
    // Include return type and stock update flag
    returnType: info.returnInfo.returnType as number,
    updateStock: Boolean(info.returnInfo.updateStock),
    ...(returnDailyStatID && { dailyStatID: returnDailyStatID }),
    ...(info.returnInfo.returnMethod && {
      paymentMethod: typeof info.returnInfo.returnMethod === 'object'
        ? info.returnInfo.returnMethod.id
        : info.returnInfo.returnMethod,
    }),
    // Fiscal info from OFD providers (e.g., Wipon, LightKassa)
    ...(check && { returnFiscalInfo: check }),
  };
  console.log('[saveSaleReturn] Calling createSaleReturn with returnDto:', JSON.stringify(sanitizeForFirestore(returnDto)));
  const createdReturn = await bestEffortDualWrite('sales.createSaleReturn', () => salesApi.createSaleReturn(
    saleID,
    returnDto as Parameters<typeof salesApi.createSaleReturn>[1]
  ));
  console.log('[saveSaleReturn] createSaleReturn succeeded');

  // The Supabase RPC creates the return but does NOT set sales.return_id. Resolve the
  // created return's Supabase UUID so we can persist it on the sale (return_id is a
  // UUID column — a Firestore id would be rejected). The SaleDetail screen gates
  // return rendering on `detail.returnInfo && detail.returnId`.
  const createdReturnRecord = createdReturn as
    | { supabaseId?: string | null; supabase_id?: string | null; id?: string | null }
    | undefined;
  const createdReturnUuid =
    createdReturnRecord?.supabaseId || createdReturnRecord?.supabase_id || createdReturnRecord?.id || null;

  // Always update the sale's return_info field to keep it in sync
  // The returnInfo contains metadata about the return (type, date, amounts, etc.)
  console.log('[saveSaleReturn] Calling updateSale with returnInfo');
  await bestEffortDualWrite('sales.updateSale', () => salesApi.updateSale(saleID, {
    returnInfo: returnInfoForApi,
    ...(createdReturnUuid ? { returnId: createdReturnUuid } : {}),
  }));
}

export const deleteSaleReturn = async (db: FirestoreDb, returnRef: FirestoreDocumentRef, saleRef: FirestoreDocumentRef, saleInfo: any, returnInfo: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteSaleReturn')
  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)

  // Resolve refs to real Firestore DocumentReferences (may be plain {id} objects from Supabase)
  const resolvedReturnRef = getFirestoreRef({ id: returnRef.id, ref: returnRef }, 'returns')
  const resolvedSaleRef = getFirestoreRef({ id: saleRef.id, ref: saleRef }, 'sales')

  // delete return
  batch.delete(resolvedReturnRef)

  // update sale - delete return info and update cart items
  const updatedCartItems: CartItem[] = []
  for (const cartItem of saleInfo.cartItems) {
    const updatedCartItem = {...cartItem}
    delete updatedCartItem.isReturned
    delete updatedCartItem.returnedQuantity
    delete updatedCartItem.returnedAmount
    updatedCartItems.push(updatedCartItem)
  }
  batch.update(resolvedSaleRef, {
    cartItems: updatedCartItems,
    returnInfo: fieldValue.delete()
  })
  // update dailyStats
  // ATL-1013: business day, not the device's calendar day — see deleteSale.
  // ATL-1013: the raw return date, NOT `?? new Date()`. A today-fallback makes the helper's
  // refusal unreachable: a return with neither a stored id nor a usable date would apply the
  // NEGATIVE return increments to today's doc, which normally exists, so `batch.update`
  // succeeds and the corruption is silent. Failing loudly is the contract.
  const statDocName = dailyStatIDForRecord(saleInfo.returnInfo.dailyStatID, saleInfo.store, saleInfo.returnInfo.date as DateLike)
  const statFields = dailyStatIncrementsForReturns([saleInfo], true, true)
  
  // return money - update transaction and daily stats
  if (saleInfo.returnInfo.returnType == 1) { 
    // delete transaction
    batch.delete(db.collection('transactions').doc(returnInfo.transactionId))
  }
  // update stock if needed
  const storeStockField = 'storeStock.' + saleInfo.store
  if (saleInfo.returnInfo.returnType == 0 && !saleInfo.returnInfo.updateStock) {
    // exchange product and don't add the returned product back to stock
    for (const [index, value] of saleInfo.cartItems.entries()) { // bring products back to stock
      if (value.isReturned) {
        if (value.isComposite && !value.manufacturedBeforeSale) {
          for (const [_idx, ingredient] of value.ingredients.entries()) {
            const increment = (value.returnedQuantity * ingredient.quantity)
            const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
            batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
            batch.set(ingredientRef.collection('movement').doc(), {
              productID: ingredient.id,
              type: 'deleteReturn',
              previousInStock: allProductsDict[ingredient.id]?.storeStock?.[saleInfo.store] || 0,
              increment,
              date: fieldValue.serverTimestamp(),
              linkedDocId: returnRef.id,
              linkedDocDate: fieldValue.serverTimestamp(),
              clientSaveDate: new Date(),
              store: saleInfo.store,
              organizationId: saleInfo.organizationId
            })
          }
        } else {
          const increment = value.returnedQuantity
          const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
          batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
          batch.set(valueRef.collection('movement').doc(), {
            productID: value.id,
            type: 'deleteReturn',
            previousInStock: allProductsDict[value.id]?.storeStock?.[saleInfo.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: returnRef.id,
            linkedDocDate: saleInfo.returnInfo.date,
            clientSaveDate: new Date(),
            store: saleInfo.store,
            organizationId: saleInfo.organizationId
          })
        }
      }
    }
  } else if (saleInfo.returnInfo.returnType == 0 && saleInfo.returnInfo.updateStock) {
    // exchange product, stock doesn't change
  } else if (saleInfo.returnInfo.returnType == 1 && saleInfo.returnInfo.updateStock) {
    // return money and add returned product back to stock - remove from stock when deleting
    for (const [index, value] of saleInfo.cartItems.entries()) {
      if (value.isReturned) {
        if (value.isComposite && !value.manufacturedBeforeSale) {
          for (const [_idx, ingredient] of value.ingredients.entries()) {
            const increment = 0 - (value.returnedQuantity * ingredient.quantity)
            const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
            batch.update(ingredientRef, {[storeStockField]: fieldValue.increment(increment)})
            batch.set(ingredientRef.collection('movement').doc(), {
              productID: ingredient.id,
              type: 'deleteReturn',
              previousInStock: allProductsDict[ingredient.id]?.storeStock?.[saleInfo.store] || 0,
              increment,
              date: fieldValue.serverTimestamp(),
              linkedDocId: returnRef.id,
              linkedDocDate: fieldValue.serverTimestamp(),
              clientSaveDate: new Date(),
              store: saleInfo.store,
              organizationId: saleInfo.organizationId })
          }
        } else {
          const increment = 0 - value.returnedQuantity
          const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
          batch.update(valueRef, {[storeStockField]: fieldValue.increment(increment)})
          batch.set(valueRef.collection('movement').doc(), {
            productID: value.id,
            type: 'deleteReturn',
            previousInStock: allProductsDict[value.id]?.storeStock?.[saleInfo.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: returnRef.id,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: saleInfo.store,
            organizationId: saleInfo.organizationId
          })
        }
      }
    }
  } else if (saleInfo.returnInfo.returnType == 1 && !saleInfo.returnInfo.updateStock) {
    // return money and don't add returned product back to stock - stock doesn't change
  }
  batch.update(db.collection('dailyStats').doc(statDocName), statFields)
 
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  // Get the return ID from the returnRef
  const returnId = returnRef.id;
  // ATL-782 (review R2): the purge key MUST be the RETURN's id, not the parent sale's.
  // `sales.deleteSaleReturn` is a delete label, so a success purges the keyed document across
  // ALL labels — keyed on the sale it would silently discard still-needed queued sale writes,
  // while the queued `sales.createSaleReturn` (keyed on the return's id) survived to replay a
  // deleted return back into existence. The sale's id stays in context for debugging only.
  await bestEffortDualWriteDelete('sales.deleteSaleReturn', () => salesApi.deleteSaleReturn(saleInfo.id || saleRef.id, returnId), { firestoreId: returnId, saleFirestoreId: saleInfo.id || saleRef.id });
}

export const getReturnsOnDateRange = async (db: FirestoreDb, range: DateRange, flat = false, store: Store | null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      // Fetch sales with returns (returns are stored as returnInfo on sales)
      const params: Record<string, any> = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      if (store) {
        params.storeId = store
      }

      // ATL-1477: every sale in the range, not the newest 50.
      const sales = await getWholeList('/sales', params, newestFirst('saleDate'))

      // Filter for sales that have returns and transform to return format
      const returns: Return[] = []
      sales.forEach((sale: Record<string, unknown>) => {
        if (sale.returnInfo || sale.returns) {
          const returnInfo = sale.returnInfo || (sale.returns && (sale.returns as any[])[0])
          if (returnInfo) {
            const returnDate = returnInfo.date || returnInfo.returnDate || sale.saleDate
            const returnDateObj = returnDate ? new Date(returnDate) : null
            
            if (returnDateObj && returnDateObj >= range.startDate && returnDateObj <= range.endDate) {
              returns.push({
                ...returnInfo,
                id: returnInfo.id || sale.returnId || `return-${sale.id}`,
                saleID: sale.id,
                store: sale.storeId || sale.store,
                date: convertTimestamp(returnDate),
                ref: createMockRef('returns', returnInfo.id || sale.returnId || `return-${sale.id}`),
              })
            }
          }
        }
      })

      // Sort by date descending
      returns.sort((a, b) => {
        const dateA = convertToDate(a.date) || new Date()
        const dateB = convertToDate(b.date) || new Date()
        return dateB.getTime() - dateA.getTime()
      })

      if (flat) {
        return returns
      } else {
        const groupedReturns: Record<string, Return[]> = {} // date key - array of returns
        returns.forEach(function (ret) {
          const retDate = convertToDate(ret.date) || new Date()
          const date = format(retDate, 'd/M/y EEEE')
          const newArr = groupedReturns[date] ? [...groupedReturns[date]] : []
          newArr.push(ret)
          groupedReturns[date] = [...newArr]
        })
        const docsArr: GroupedData<Return>[] = []
        for (const key in groupedReturns) {
          const newData = {
            title: key,
            data: groupedReturns[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching returns from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'returns' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  let query = db.collection('returns') as FirestoreQuery<Return>
  if (store) {
    query = query.where('store', '==', store)
  }
  const querySnapshot = await query
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatReturns: Return[] = []
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Return
      snap.id = doc.id
      snap.ref = doc.ref
      flatReturns.push(snap)
    })
    return flatReturns
  } else {
    const groupedReturnsFS: Record<string, Return[]> = {} // date key - array of returns
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Return
      snap.id = doc.id
      snap.ref = doc.ref
      const snapDate = snap.date as { toDate: () => Date }
      const date = format(snapDate.toDate(), 'd/M/y EEEE')
      const newArr = groupedReturnsFS[date] ? [...groupedReturnsFS[date]] : []
      newArr.push(snap)
      groupedReturnsFS[date] = [...newArr]
    })
    const resultArrFS: GroupedData<Return>[] = []
    for (const key in groupedReturnsFS) {
      const newData = {
        title: key,
        data: groupedReturnsFS[key]
      }
      resultArrFS.push(newData)
    }
    return resultArrFS
  }
}

export const getCartItemsByProduct = async (db: FirestoreDb, product: Product, range: DateRange, store: Store | null) => {
  if (shouldUseSupabase()) {
    try {
      const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
      const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))
      
      // Fetch product movements of type 'sale' for this product
      const params: Record<string, any> = {
        productId: product.id,
        storeId: store,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        type: 'sale',
      }

      // ATL-1477: every movement in the range, not the newest 100.
      const movements = (await getWholeList('/product-movements', params, newestFirst('date'))).map((movement) => transformApiItem(movement, 'product-movements'))

      // Transform movements to cartItems format
      return movements.map((movement: any) => ({
        ...movement,
        saleDate: movement.date,
        id: product.id,
        ref: createMockRef('cartItems', movement.id),
      }))
    } catch (error: unknown) {
      console.error('Error fetching cart items by product from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'cartItems', function: 'getCartItemsByProduct' },
        extra: {
          productId: product.id,
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
  const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))
  const querySnapshot = await rootDb.collectionGroup('cartItems').where('id', '==', product.id)
  .where('saleDate', '>=', startDate)
  .where('saleDate', '<=', endDate)
  .where('store', '==', store)
  .orderBy('saleDate', 'desc').get()
  const docs: Array<Record<string, unknown>> = []
  querySnapshot.forEach(function (doc: any) {
    const snap = doc.data() as Record<string, unknown>
    snap.id = doc.id
    snap.ref = doc.ref
    docs.push(snap)
  })
  return docs
}

export const updateStockManually = async (value: any, store: any, newStockValue: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateStockManually')
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const organizationId = (jotaiStore.get(organizationAtom) as Organization).id
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
  // update stocks
  const storeStockField = 'storeStock.' + store
  const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
  batch.update(valueRef, { [storeStockField]: newStockValue })
  batch.set(valueRef.collection('movement').doc(), {
    productID: value.id,
    type: 'manualChange',
    previousInStock: value.inStockOld || 0,
    increment: newStockValue - value.storeStock[store],
    date: fieldValue.serverTimestamp(),
    linkedDocDate: fieldValue.serverTimestamp(),
    clientSaveDate: new Date(),
    store: store,
    organizationId: organizationId
  })
  await batch.commit()

  // Dual write to Supabase via NestJS backend.
  // The body key MUST be `newStock` — that is what `products.controller.ts:137` reads. It was
  // `quantity`, so `newStock` arrived undefined, `updateStockManually` passed `new_quantity: NULL`
  // into `set_stock_with_movement`, and the movement landed with a NULL increment. `SUM(increment)`
  // skips NULLs, so the adjustment silently vanished — and where it was the product's only movement
  // the stock read 0. 75 such rows across 7 orgs, still being written (newest 2026-07-19).
  // The `as any` is deliberately gone: it was the only thing hiding the mismatch, because the
  // controller types the body inline, so the generated client carries no body schema to check against.
  // ATL-765: routed through the SHARED bulk mirror, not a lone single-store PATCH. This is
  // load-bearing, not stylistic: the outbox keys entries by (label, productId), so a
  // single-store call for this product would SUPERSEDE (on failure) or PURGE (on success) a
  // queued multi-store bulk entry from updateProduct — silently destroying the other stores'
  // retries. sendBulkStockMirror folds any queued stores into this call so nothing is orphaned;
  // foldSource carries the product's current absolutes with this store's new value applied.
  await sendBulkStockMirror(
    value.id,
    [{ storeId: store, newStock: newStockValue }],
    { ...(value.storeStock || {}), [store]: newStockValue },
  );
}

export const updateMovement = async (movement: any, updatedFields: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateMovement')
  const firestoreRef = getFirestoreRef(movement, 'productMovements')
  reportFirestoreOnlyWriterUsage('updateMovement')
  await firestoreRef.update(updatedFields)
  // TODO: Add Supabase API call for movement updates if needed
}

export const deleteMovement = async (movement: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteMovement')
  const firestoreRef = getFirestoreRef(movement, 'productMovements')
  reportFirestoreOnlyWriterUsage('deleteMovement')
  await firestoreRef.delete()
  // TODO: Add Supabase API call for movement deletes if needed
}

export const getProductsMovementOnDateRange = async (range: DateRange, flat = false, store: Store | null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: Record<string, unknown> = {
        storeId: store,
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      // ATL-1477: every movement in the range, not the newest 100.
      const movements = (await getWholeList('/product-movements', params, newestFirst('date'))).map((movement) => transformApiItem(movement, 'product-movements')) as unknown as ProductMovement[]

      if (flat) {
        return movements
      } else {
        const groupedMovements: Record<string, ProductMovement[]> = {} // date key - array of movements
        movements.forEach(function (movement) {
          const movementDate = convertToDate(movement.date) || new Date()
          const date = format(getLocalStoreDate(store?.id, movementDate), 'd/M/y EEEE')
          const newArr = groupedMovements[date] ? [...groupedMovements[date]] : []
          newArr.push(movement)
          groupedMovements[date] = [...newArr]
        })
        const docsArr: GroupedData<ProductMovement>[] = []
        for (const key in groupedMovements) {
          const newData = {
            title: key,
            data: groupedMovements[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching products movement from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'product-movements', function: 'getProductsMovementOnDateRange' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const jotaiStore = getDefaultStore()
  const organizationId = (jotaiStore.get(organizationAtom) as Organization).id
  const querySnapshot = await rootDb.collectionGroup('movement').where('organizationId', '==', organizationId)
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .where('store', '==', store)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatMovements: ProductMovement[] = []
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data() as ProductMovement
      snap.id = doc.id
      snap.ref = doc.ref
      flatMovements.push(snap)
    })
    return flatMovements
  } else {
    const groupedMovementsFS: Record<string, ProductMovement[]> = {} // date key - array of movements
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data() as ProductMovement
      snap.id = doc.id
      snap.ref = doc.ref
      const date = format(getLocalStoreDate(store?.id, convertToDate(snap.date) || new Date()), 'd/M/y EEEE')
      const newArr = groupedMovementsFS[date] ? [...groupedMovementsFS[date]] : []
      newArr.push(snap)
      groupedMovementsFS[date] = [...newArr]
    })
    const resultMovementsArr: GroupedData<ProductMovement>[] = []
    for (const key in groupedMovementsFS) {
      const newData = {
        title: key,
        data: groupedMovementsFS[key]
      }
      resultMovementsArr.push(newData)
    }
    return resultMovementsArr
  }
}

export const saveNewProductCategory = async (db: FirestoreDb, categoryInfo: Partial<ProductCategory>) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewProductCategory')
  const batch = rootDb.batch()
  const newRef = db.collection('productCategories').doc()
  batch.set(newRef, { ...categoryInfo, id: newRef.id })
  const statFields = {
    productCategoriesCreated: fieldValue.increment(1),
  }
  batch.set(db.collection('dailyStats').doc(categoryInfo.dailyStatID), {
    ...statFields,
    store: categoryInfo.lastChangeStore,
    date: categoryInfo.lastChangeAt,
    organizationId: categoryInfo.organizationId
  }, {merge: true})
  await commitGuardedCreate('productCategories.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('productCategories.create', () => productCategoriesApi.createProductCategory({
    firestoreId: newRef.id,
    name: categoryInfo.name || '',
    // ATL-1892 — the same `order` the Firestore document just got. Without it the backend stored 0
    // for every category created since dual-write began, and a Supabase-read org lost its tab order.
    order: categoryInfo.order,
    color: categoryInfo.color,
    parentId: categoryInfo.parentID,
  }));

  return { id: newRef.id, ...categoryInfo }
}

export const updateProductCategory = async (ref: any, updatedInfo: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateProductCategory')
  const firestoreRef = getFirestoreRef(ref, 'productCategories')
  await firestoreRef.update(updatedInfo)

  // Dual write to Supabase - pass through, let backend handle
  await bestEffortDualWrite('productCategories.update', () => productCategoriesApi.updateProductCategory(ref.id, updatedInfo), { firestoreId: ref.id });
}

/** Firestore's per-batch write limit; a reorder larger than one batch is refused, never split. */
export const PRODUCT_CATEGORY_ORDER_BATCH_LIMIT = 500

/**
 * ATL-1892 — a category reorder: every category's new `order`, committed as ONE Firestore batch.
 *
 * The reorder used to fire one `updateProductCategory` per row in `Promise.all`. When one of them
 * rejected (another device deleted that category, a transient error) the others still landed, and
 * Firestore — the write master — was left half-renumbered with duplicate orders that no later read
 * repairs; each row also fired its own snapshot. A batch is all or nothing: either every category
 * gets its index or none does, and the listeners see one change.
 *
 * The Supabase mirror is sent only after the commit, one `PUT /product-categories/:id` per row
 * through the same best-effort/outbox path `updateProductCategory` uses (there is no bulk route).
 * A failed commit sends nothing, so Supabase is never told about an order Firestore does not hold.
 *
 * More rows than one batch can hold is refused before anything is written: splitting it into
 * several batches would reintroduce exactly the partial renumbering this function exists to remove.
 */
export const saveProductCategoriesOrder = async (updates: { ref: any; order: number }[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveProductCategoriesOrder')
  if (updates.length === 0) return
  if (updates.length > PRODUCT_CATEGORY_ORDER_BATCH_LIMIT) {
    throw new Error(
      `saveProductCategoriesOrder: ${updates.length} categories exceed one Firestore batch ` +
        `(${PRODUCT_CATEGORY_ORDER_BATCH_LIMIT}); refusing rather than committing a partial reorder`,
    )
  }
  const batch = rootDb.batch()
  for (const { ref, order } of updates) {
    batch.update(getFirestoreRef(ref, 'productCategories'), { order })
  }
  await batch.commit()

  await Promise.all(updates.map(({ ref, order }) =>
    bestEffortDualWrite('productCategories.update', () => productCategoriesApi.updateProductCategory(ref.id, { order }), { firestoreId: ref.id })
  ))
}

export const deleteProductCategory = async (ref: any, productsToMove: any[], moveCategory: any, userId: string) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteProductCategory')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'productCategories')
  batch.delete(firestoreRef)
  for (const product of productsToMove) {
    const productFirestoreRef = getFirestoreRef(product, 'products')
    batch.update(productFirestoreRef, {
      categoryID: moveCategory.id,
      lastChangeBy: userId,
      lastChangeAt: new Date(),
      lastChangeSource: 'deleteCategory',
    })
  }
  await batch.commit()

  // Dual write to Supabase via NestJS backend.
  //
  // ATL-1009: the batch above re-pointed `productsToMove` at `moveCategory` in FIRESTORE. This
  // call used to say only "delete the category", so Supabase soft-deleted the row and left every
  // one of those products' `category_id` pointing at it — and `category_id` is the column the read
  // path resolves `categoryID` from, so after a read cutover each product renders with no category
  // at all (`categoriesDict[deletedId]` is undefined). The move target rides along so the backend
  // performs the same reassignment in ONE call: a per-product update loop would be N round trips
  // from a till, which is the shape ATL-950 already rejected.
  //
  // Sent ONLY when this call actually moved something. `undefined` means "say nothing about the
  // products", which is the old behaviour; an empty string would mean "clear them", and on a
  // delete that moved nothing that would clear any product Supabase still has in the category
  // through drift — a write with no instruction behind it. Repairing those is the ATL-1009
  // backfill's job, where it is a decision rather than a side effect.
  const moveToCategoryId = productsToMove.length > 0 ? moveCategory?.id : undefined
  await bestEffortDualWriteDelete('productCategories.delete', () => productCategoriesApi.deleteProductCategory(ref.id, moveToCategoryId), { firestoreId: ref.id });
}

export const saveNewSupplier = async (db: FirestoreDb, supplierInfo: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewSupplier')
  const batch = rootDb.batch()
  const newRef = db.collection('suppliers').doc()
  batch.set(newRef, { ...supplierInfo, id: newRef.id })
  const statFields = {
    productSuppliersCreated: fieldValue.increment(1),
  }
  batch.set(db.collection('dailyStats').doc(supplierInfo.dailyStatID), {
    ...statFields,
    store: supplierInfo.lastChangeStore,
    date: supplierInfo.lastChangeAt,
    organizationId: supplierInfo.organizationId
  }, {merge: true})
  await commitGuardedCreate('suppliers.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('suppliers.create', () => suppliersApi.createSupplier({
    ...supplierInfo,
    firestoreId: newRef.id,
  }));

  return { id: newRef.id, ...supplierInfo }
}

export const updateSupplier = async (ref: any, updatedInfo: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateSupplier')
  const firestoreRef = getFirestoreRef(ref, 'suppliers')
  await firestoreRef.update(updatedInfo)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('suppliers.update', () => suppliersApi.updateSupplier(ref.id, updatedInfo), { firestoreId: ref.id });
}

export const deleteSupplier = async (ref: any, supplierProducts: any[], stores: Record<string, any>) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteSupplier')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'suppliers')
  batch.delete(firestoreRef)
  const activeInStore: Record<string, boolean> = {}
  for (const key in stores) {
    activeInStore[key] = false
  }
  for (const product of supplierProducts) {
    const productFirestoreRef = getFirestoreRef(product, 'products')
    batch.update(productFirestoreRef, {
      supplierID: null,
      activeInStore: activeInStore,
    })
  }
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('suppliers.delete', () => suppliersApi.deleteSupplier(ref.id), { firestoreId: ref.id });
}

// Print Template Images
export const uploadPrintTemplateImage = async (file: any, name: string) => {
  reportFirestoreOnlyWriterUsage('uploadPrintTemplateImage')
  if (Platform.OS === 'web') {
    return await storage.child('printTemplates/' + name).put(file, {
      contentType: 'image/jpeg',
    })
  } else {
    return await storage.child('printTemplates/' + name).putFile(file, {
      contentType: 'image/jpeg',
    })
  }
}

export const deletePrintTemplateImage = (name: string) => {
  reportFirestoreOnlyWriterUsage('deletePrintTemplateImage')
  return storage.child('printTemplates/' + name).delete()
}

export const saveNewPricingRevision = async (db: FirestoreDb, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewPricingRevision')
  reportFirestoreOnlyWriterUsage('saveNewPricingRevision', db?.id)
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom) as Record<string, any>
  for (const [index, value] of info.items.entries()) {
    const productValue = { ...value }
    delete productValue.purchasePriceOld
    delete productValue.packagePriceOld
    delete productValue.salePriceOld
    delete productValue.woltSalePriceOld
    delete productValue.greenBeeSalePriceOld
    delete productValue.activeInEcommerceOld
    delete productValue.activeInWoltOld
    delete productValue.activeInGreenBeeOld
    delete productValue.nameOld
    delete productValue.descriptionOld
    const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
    batch.update(valueRef, productValue)
    batch.set(valueRef.collection('history').doc(), productValue)
  }
  batch.set(db.collection('pricingRevisions').doc(), info)
  await batch.commit()
}

export const getSpendingsOnDateRange = async (db: FirestoreDb, range: DateRange, flat = false, store: Store | null = null) => {
  if (shouldUseSupabase()) {
    try {
      const params: Record<string, unknown> = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      if (store) {
        params.storeId = store
      }

      // Using typed API - returns SpendingFirestoreResponse[] with camelCase fields and Firestore aliases.
      // ATL-1477: every spending in the range, not the newest 50.
      const spendings = await readWholeList(
        (window) => spendingsApi.getSpendingsWindow({ ...params, ...window }),
        { label: 'spendings', order: newestFirst('spendingDate') },
      ) as unknown as Spending[]

      if (flat) {
        return spendings
      } else {
        const groupedSpendings: Record<string, Spending[]> = {} // date key - array of spendings
        spendings.forEach(function (spending) {
          const spendingDate = convertToDate(spending.date) || new Date()
          const spendingStore = spending.store || 'common'
          const date = format(getLocalStoreDate(spendingStore, spendingDate), 'd/M/y EEEE')
          const newArr = groupedSpendings[date] ? [...groupedSpendings[date]] : []
          newArr.push(spending)
          groupedSpendings[date] = [...newArr]
        })
        const docsArr: GroupedData<Spending>[] = []
        for (const key in groupedSpendings) {
          const newData = {
            title: key,
            data: groupedSpendings[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching spendings from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'spendings' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  let query = db.collection('spendings') as FirestoreQuery<Spending>
  if (store) {
    query = query.where('store', 'in', [store, 'common'])
  }
  const querySnapshot = await query
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatSpendings: Spending[] = []
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Spending
      snap.id = doc.id
      snap.ref = doc.ref as any
      flatSpendings.push(snap)
    })
    return flatSpendings
  } else {
    const groupedSpendingsFS: Record<string, Spending[]> = {} // date key - array of spendings
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Spending
      snap.id = doc.id
      snap.ref = doc.ref as any
      const date = format(getLocalStoreDate(snap.store, convertToDate(snap.date) || new Date()), 'd/M/y EEEE')
      const newArr = groupedSpendingsFS[date] ? [...groupedSpendingsFS[date]] : []
      newArr.push(snap)
      groupedSpendingsFS[date] = [...newArr]
    })
    const resultSpendingsArr: GroupedData<Spending>[] = []
    for (const key in groupedSpendingsFS) {
      const newData = {
        title: key,
        data: groupedSpendingsFS[key]
      }
      resultSpendingsArr.push(newData)
    }
    return resultSpendingsArr
  }
}

/**
 * Save a new spending and its money leg.
 *
 * Returns the new document's ref AND the id of the leg it booked (ATL-1790): the caller caches the
 * spending, and `deleteSpending` removes the leg only through the cached copy's `transactionId` — a
 * copy built from the ref alone left the leg live in Firestore when the spending was deleted from
 * that copy, so the wallet kept money for an expense that no longer existed.
 */
export const saveNewSpending = async (db: FirestoreDb, info: Partial<Spending>, passedBatch: ReturnType<FirestoreDb['batch']> | null = null) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewSpending')
  const batch = passedBatch ? passedBatch : rootDb.batch()
  const newRef = db.collection('spendings').doc()
  const transactionRef = db.collection('transactions').doc()
  // Sanitize info to convert Firestore Timestamps to ISO strings (prevents serialization errors)
  const sanitizedInfo = sanitizeForFirestoreDeep(info) as Partial<Spending>
  batch.set(newRef, { ...sanitizedInfo, transactionId: transactionRef.id })
  // update daily stats
  const statDocName = info.dailyStatID
  const statFields = dailyStatIncrementsForSpendings([info], true, false)
  batch.set(db.collection('dailyStats').doc(statDocName), {
    ...statFields,  
    store: info.store,
    date: info.date, 
    organizationId: info.organizationId
  }, {merge: true})
  // save transaction
  batch.set(transactionRef, {
    linkedDocId: newRef.id,
    linkedDocType: 'spending',
    externalDestination: info.category,
    type: 'expense',
    sourceWallet: typeof info.source === 'object' ? (info.source as { id: string })?.id : info.source,
    value: info.amount,
    date: info.date,
    store: info.store,
    organizationId: info.organizationId,
    createdDate: new Date(),
  })
  if (passedBatch) {
    return [ batch, newRef ]
  }
  await commitGuardedCreate('spendings.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  const spendingDto = {
    firestoreId: newRef.id,
    organizationId: info.organizationId,
    storeId: info.store,
    name: info.details || '',
    amount: info.amount,
    category: typeof info.category === 'object' ? (info.category as { id: string })?.id : info.category,
    // Defensive (ATL-536/539, Sentry 22A/22F class): the schema's payment field is an OBJECT
    // (matches the READ shape) — never let a bare id string through; wrap it as { id }.
    source:
      info.source && typeof info.source === 'object'
        ? info.source
        : info.source
          ? { id: info.source }
          : undefined,
    transactionId: transactionRef?.id,
    dailyStatId: info.dailyStatID,
    spendingType: info.type ?? 0,
    ...(info.date && {
      spendingDate: convertToDate(info.date)?.toISOString() || new Date().toISOString(),
    }),
  };
  await bestEffortDualWrite('spendings.create', () => spendingsApi.createSpending(spendingDto as Parameters<typeof spendingsApi.createSpending>[0]));

  return { ref: newRef, transactionId: transactionRef.id }
}

export const dailyStatIncrementsForSpendings = (spendings: any[], forFirebase: boolean, negative: boolean) => {
  const statFields: Record<string, any> = {}
  for (const info of spendings) {
    statFields['spendingsAmount'] = (statFields['spendingsAmount'] || 0) + info.amount
    statFields['spendingsQuantity'] = (statFields['spendingsQuantity'] || 0) + 1
    // Handle source as either an object with id property or a string ID
    const sourceId = typeof info.source === 'object' ? info.source?.id : info.source
    if (sourceId) {
      statFields[sourceId + 'SpendingsAmount'] = (statFields[sourceId + 'SpendingsAmount'] || 0) + info.amount
      statFields[sourceId + 'SpendingsQuantity'] = (statFields[sourceId + 'SpendingsQuantity'] || 0) + 1
    }
  }
  if (negative) {
    for (const key in statFields) {
      statFields[key] = 0 - statFields[key]
    }
  }
  if (forFirebase) {
    for (const key in statFields) {
      statFields[key] = fieldValue.increment(statFields[key])
    }
  }
  return statFields
}

export const deleteSpending = async (db: FirestoreDb, ref: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteSpending')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'spendings')
  batch.delete(firestoreRef)
  // update daily stats
  // ATL-1013: business day, not the device's calendar day — see deleteSale.
  const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.date)
  const statFields = dailyStatIncrementsForSpendings([info], true, true)
  batch.update(db.collection('dailyStats').doc(statDocName), statFields)
  // delete transaction
  if (info.transactionId) {
    batch.delete(db.collection('transactions').doc(info.transactionId))
  }
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('spendings.delete', () => spendingsApi.deleteSpending(ref.id), { firestoreId: ref.id });
}

export const dailyStatIncrementsForCashboxTransactions = (cashboxTransactions: any[], forFirebase: boolean, negative: boolean) => {
  const statFields: Record<string, any> = {}
  for (const info of cashboxTransactions) {
    if (info.type.id === 'openShift') {
      // Use wallet ID for cash operations, fallback to store if wallet not provided
      const walletId = info.wallet || (info.store + '-cash')
      statFields[walletId + 'StartAmount'] = info.amount
      statFields['shiftOpened'] = true
      statFields['shiftOpenedTime'] = info.date
    }
    if (info.type.id === 'deposit') {
      // Use wallet ID for cash operations, fallback to store if wallet not provided
      const walletId = info.wallet || (info.store + '-cash')
      statFields[walletId + 'DepositsAmount'] = (statFields[walletId + 'DepositsAmount'] || 0) + info.amount
    }
    if (info.type.id === 'withdrawal') {
      // Use wallet ID for cash operations, fallback to store if wallet not provided
      const walletId = info.wallet || (info.store + '-cash')
      statFields[walletId + 'WithdrawalsAmount'] = (statFields[walletId + 'WithdrawalsAmount'] || 0) + info.amount
    }
    if (info.type.id === 'closeShift') {
      statFields['shiftClosed'] = true
      statFields['shiftClosedTime'] = info.date
      if (info.endAmounts) {
        for (const key in info.endAmounts) {
          statFields[key + 'EndAmount'] = info.endAmounts[key]
        }
      }
      if (info.systemEndAmounts) {
        for (const key in info.systemEndAmounts) {
          statFields[key + 'SystemEndAmount'] = info.systemEndAmounts[key]
        }
      }
    }
  }
  
  // Identify numeric fields that should use increment() and negation
  const numericFields = new Set()
  for (const key in statFields) {
    if (key.includes('DepositsAmount') || 
        key.includes('WithdrawalsAmount')) {
      numericFields.add(key)
    }
  }
  
  // Apply negative handling only to numeric fields
  if (negative) {
    for (const key in statFields) {
      if (numericFields.has(key)) {
        statFields[key] = 0 - statFields[key]
      }
    }
  }
  
  // Apply fieldValue.increment() only to numeric fields when forFirebase is true
  if (forFirebase) {
    for (const key in statFields) {
      if (key.includes('DepositsAmount') || 
        key.includes('WithdrawalsAmount')) {
        statFields[key] = FieldValue.increment(statFields[key])
      }
      // Non-numeric fields (booleans, dates) are kept as direct assignments
    }
  }
  
  return statFields
}

/**
 * A cashbox operation: a shift's open or close, a cash deposit or withdrawal.
 *
 * ATL-1445 stage 3 P9b — through the `writeW` seam. Firestore mode runs master's body verbatim
 * (`firestoreFirst`). Supabase mode (no shipped bundle is capable; the staging preview only, `writeWPreview.ts`)
 * queues the `POST /cashbox-transactions` the body's dual-write sends, money leg included, with the day's shift
 * fields as local effects (`cashboxTransactionLocalEffects`: the batch's own
 * `dailyStatIncrementsForCashboxTransactions`, deposits and withdrawals as increments, the rest assigned). A
 * close on a wipon-kassa store closes the OFD shift first, as the body does (`closeWiponShiftForCashboxClose`).
 */
export const saveCashboxTransaction = (db: FirestoreDb, info: any): Promise<void> =>
  writeW({
    label: 'cashboxTransactions.create',
    kind: 'create',
    offline: 'queue',
    firestoreFirst: async () => {
      const batch = rootDb.batch()
      const newRef = db.collection('cashboxTransactions').doc()
      /**
       * ATL-1245 — the money leg, held so the dual-write can carry it.
       *
       * A deposit/withdrawal commits TWO Firestore documents: this cashbox row and a
       * `transactions` document that actually moves the cash between wallets. Only the first was
       * ever mirrored, so on production 2026-09-07 **198 such legs existed in Firestore and in no
       * form at all in Supabase** — 12 trading organizations, 39 distinct days, still arriving on
       * 2026-09-06, with `cashbox_transactions.transaction_id` NULL on 198 of 198.
       *
       * It travels inside the cashbox dual-write rather than as a second call on purpose: two
       * independent requests can land one-and-not-the-other, which is the divergence being fixed.
       * One request means one outbox entry, and the backend writes both rows or neither.
       *
       * The wallet ids are the ones written to Firestore just below, forwarded verbatim — the
       * `<storeId>-cash` / `<storeId>-extracted-cash` convention lives here, and a second spelling
       * of it on the server would drift the first time either changed.
       */
      let linkedTransaction: {
        firestoreId: string
        value: number
        sourceWallet?: string
        destinationWallet?: string
      } | null = null

      // update daily stats
      const statDocName = info.dailyStatID
      const statFields = dailyStatIncrementsForCashboxTransactions([info], true, false)
  
      if (info.type.id === 'openShift') {
        batch.set(newRef, info)
      }
      if (info.type.id === 'deposit') {
        const transactionRef = db.collection('transactions').doc()
        batch.set(newRef, { ...info, transactionId: transactionRef.id })
        // save transaction
        const cashWallet = info.wallet || (info.store + '-cash')
        batch.set(transactionRef, {
          linkedDocId: newRef.id,
          linkedDocType: 'deposit',
          type: 'transfer',
          destinationWallet: cashWallet,
          value: info.amount,
          date: info.date,
          store: info.store,
          organizationId: info.organizationId,
          createdDate: new Date(),
        })
        // No `sourceWallet` — a deposit has none in Firestore, and none on the 1,606 migrated rows.
        // No `date` either: the backend defaults the leg to the cashbox row's own parsed date, which
        // is this same `info.date` after one parse instead of two spellings of it.
        // `info.amount || 0` is the SAME expression the `amount` field below uses, deliberately: the
        // two must never disagree, and `parseFloat('')` upstream can produce NaN, which `@IsNumber()`
        // refuses — a 400 is a deterministic defect the outbox never queues, so a leg that could fail
        // validation would cost the cashbox row that lands today.
        linkedTransaction = {
          firestoreId: transactionRef.id,
          value: info.amount || 0,
          destinationWallet: cashWallet,
        }
      }
      if (info.type.id == 'withdrawal') {
        const transactionRef = db.collection('transactions').doc()
        batch.set(newRef, { ...info, transactionId: transactionRef.id })
        // save transaction
        const cashWallet = info.wallet || (info.store + '-cash')
        batch.set(transactionRef, {
          linkedDocId: newRef.id,
          linkedDocType: 'withdrawal',
          type: 'transfer',
          sourceWallet: cashWallet,
          destinationWallet: info.store + '-extracted-cash',
          value: info.amount,
          date: info.date,
          store: info.store,
          organizationId: info.organizationId,
          createdDate: new Date(),
        })
        linkedTransaction = {
          firestoreId: transactionRef.id,
          value: info.amount || 0,
          sourceWallet: cashWallet,
          destinationWallet: info.store + '-extracted-cash',
        }
      }
      if (info.type.id === 'closeShift') {
        batch.set(newRef, info)
        // extract cash and close wipon ofd cashbox
        const jotaiStore =  getDefaultStore()
        const storesDict = jotaiStore.get(storesDictAtom)
        const storeInfo = storesDict?.[info.store]
        if ((storeInfo as any)?.fiscalOperator?.id === 'wipon-kassa') {
          try {
            let newStats = await getActiveShift((storeInfo as any)?.wiponCredentials)
            let newCredentials = (storeInfo as any)?.wiponCredentials
            if (!newStats) {
              // ATL-1170 review: this was the FIFTH spelling of the inline refresh, and it still had
              // the defect the ticket is about — `{...credentials, accessToken: undefined}` written
              // straight back over working credentials whenever the login failed, so closing a shift
              // during a provider outage cost the store its token. It also wrote through the raw
              // Firestore ref, which skips the Supabase dual-write and is a silent no-op on any store
              // carrying no `ref`.
              const refreshed = await refreshCredentials((storeInfo as any)?.wiponCredentials)
              if (refreshed) {
                newCredentials = refreshed
                try {
                  await updateStoreWiponCredentials((storeInfo as any)?.ref, refreshed)
                } catch (credentialError) {
                  // ATL-2035: `books_write_error` keeps it in Sentry under the write-failures-and-crashes policy.
                  Sentry.captureException(credentialError, { tags: { books_write_error: 'wipon-credentials-update' } })
                }
                newStats = await getActiveShift(refreshed)
              }
            }
            if (newStats) {
              await extractCash(newCredentials, newStats.shift.cashbox.balance)
              await closeCashboxShift(newCredentials)
            }
          } catch(error) {
            // ATL-2035: a fiscal cash withdrawal / shift close that failed — a write failure, kept in Sentry.
            Sentry.captureException(error, { tags: { books_write_error: 'wipon-shift-close' } })
            console.log(error)
          }
        }
      }
      batch.set(db.collection('dailyStats').doc(statDocName), {...statFields,
        date: info.date,
        store: info.store,
        organizationId: info.organizationId
      }, {merge: true})
      await commitGuardedCreate('cashboxTransactions.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

      // Dual write to Supabase via NestJS backend. Forward endAmounts (per-wallet cash at
      // shift close) so the Supabase closeShift row mirrors Firestore (was dropped before).
      await bestEffortDualWrite('cashboxTransactions.create', () => cashboxTransactionsApi.createCashboxTransaction({
        firestoreId: newRef.id,
        storeId: info.store,
        type: info.type?.id || info.type,
        amount: info.amount || 0,
        wallet: info.wallet,
        ...(info.endAmounts ? { endAmounts: info.endAmounts } : {}),
        ...(info.systemEndAmounts ? { systemEndAmounts: info.systemEndAmounts } : {}),
        date: info.date?.toDate ? info.date.toDate().toISOString() : (info.date instanceof Date ? info.date.toISOString() : info.date),
        // ATL-1245 — the money leg travels with its parent, in the same request.
        ...(linkedTransaction ? { transaction: linkedTransaction } : {}),
      } as any));
    },
    // Supabase mode only (P9b). The request is the body's dual-write (`cashboxTransactionCreateDto` builds the
    // same DTO, money leg included; `firebaseCashboxWritersSupabase.p9b.test.js` holds the two byte for byte),
    // identified by the cashbox document id this plan mints, as the body mints it. Every input the body would
    // write unreadably (an unknown type, no store, a non-finite amount, an unreadable date) is refused here,
    // before anything happens. A close on a wipon-kassa store closes the OFD shift as the body does — in
    // `beforeAppend`, so only once every refusal that does not depend on it has passed.
    plan: async () => {
      const input: CashboxWriteInput = info
      const type = cashboxTransactionTypeOf(input)
      const newRef = db.collection('cashboxTransactions').doc()
      const legId = type === 'deposit' || type === 'withdrawal' ? db.collection('transactions').doc().id : null
      const dto = cashboxTransactionCreateDto(newRef.id, type, input, legId)
      const request = await buildOutboxRequest((client) => createCashboxTransactionsApi(client).createCashboxTransaction(dto))
      const effects = assertLocalEffects(cashboxTransactionLocalEffects(input))
      return {
        documentId: newRef.id,
        relatedIds: [],
        request,
        effects,
        ...(type === 'closeShift' && {
          beforeAppend: async () => {
            await closeWiponShiftForCashboxClose(dto.storeId)
            return { request, effects }
          },
        }),
        fromAck: () => undefined,
      }
    },
  })

/**
 * Edit cashbox shift stats (open/close shift amounts)
 * @param {Object} db - Firestore database instance
 * @param {Object} shift - Shift info with id, openId, dailyStatID, date, etc.
 * @param {number} editType - 0 for open shift, 3 for close shift
 * @param {number} newAmount - New amount value
 * @param {string} store - Store ID
 * @param {string} editPaymentMethod - Payment method ID (for close shift)
 * @param {string} cashWallet - Cash wallet ID
 * @param {Object} constants - Constants with payment methods
 *
 * ATL-1445 stage 3 P9b — through the `writeW` seam; Firestore mode runs master's body verbatim. Supabase mode
 * queues the body's `PATCH` (`cashboxShiftEditOf` builds the same id and DTO) as an absolute field set.
 */
export const editCashboxShiftStats = (db: FirestoreDb, shift: any, editType: number, newAmount: number, store: string, editPaymentMethod: string, cashWallet: string): Promise<void> =>
  writeW({
    label: 'cashboxTransactions.update',
    kind: 'absolute',
    offline: 'queue',
    firestoreFirst: async () => {
      // ATL-1013: getLocalStoreDate is autoclose-NAIVE, so a shift opened in the post-midnight
      // tail re-stated a doc one day past the one it was written to. See deleteSale.
      const statDocName = dailyStatIDForRecord(shift.dailyStatID, store, shift.date)

      if (editType === 0) {
        // Opening shift - update open amount
        await db.collection('cashboxTransactions').doc(shift.openId).update({ amount: newAmount } as any)

        // Update daily stats with wallet start amount
        const wallet = cashWallet || (store + '-cash')
        await db.collection('dailyStats').doc(statDocName).update({ [wallet + 'StartAmount']: newAmount } as any)

        // Dual write to Supabase via NestJS backend
        await bestEffortDualWrite('cashboxTransactions.update', () => cashboxTransactionsApi.updateCashboxTransaction(shift.openId, { amount: newAmount }));

      } else if (editType === 3) {
        // Closing shift - update end amounts
        const newAmounts = { ...shift.endAmounts }
        newAmounts[editPaymentMethod] = newAmount

        await db.collection('cashboxTransactions').doc(shift.id).update({ endAmounts: newAmounts } as any)
        await db.collection('dailyStats').doc(statDocName).update({ [editPaymentMethod + 'EndAmount']: newAmount } as any)

        // Dual write to Supabase via NestJS backend
        await bestEffortDualWrite('cashboxTransactions.update', () => cashboxTransactionsApi.updateCashboxTransaction(shift.id, { endAmounts: newAmounts }));
      }
    },
    // Supabase mode only (P9b): the `PATCH /cashbox-transactions/:id` the body's dual-write sends, an ABSOLUTE
    // field set (§13: a shift's amounts are not deltas), with the day's field it sets as a local effect. The
    // body does nothing for any other edit type; a plan cannot queue nothing, so it refuses one.
    plan: async () => {
      const edit = cashboxShiftEditOf(shift, editType, newAmount, store, editPaymentMethod, cashWallet)
      // The day the body updates (ATL-1013: the row's own business day).
      const dailyStatId = dailyStatIDForRecord(shift.dailyStatID, store, shift.date)
      const request = await buildOutboxRequest((client) =>
        createCashboxTransactionsApi(client).updateCashboxTransaction(edit.documentId, edit.dto))
      return {
        documentId: edit.documentId,
        relatedIds: [],
        request,
        effects: assertLocalEffects(cashboxDayEffects(store, dailyStatId, {}, { [edit.field]: newAmount }, [])),
        fromAck: () => undefined,
      }
    },
  })

/**
 * Cancel a shift closing - delete the close shift transaction and reset daily stats
 * @param {Object} db - Firestore database instance
 * @param {Object} closeShiftTransaction - The close shift cashbox transaction to delete
 * @param {Object} dailyStatRef - Reference to the daily stats document
 *
 * ATL-1445 stage 3 P9b — through the `writeW` seam; Firestore mode runs master's body verbatim. Supabase mode
 * queues the body's `DELETE` (a queued delete purges this device's own unsent writes of the row when nothing
 * else needs them — `planDeletePurge`), with the closing fields deleted from the day as its local effect.
 */
export const cancelShiftClosing = (db: FirestoreDb, closeShiftTransaction: any, dailyStatRef: any): Promise<void> =>
  writeW({
    label: 'cashboxTransactions.delete',
    kind: 'delete',
    offline: 'queue',
    firestoreFirst: async () => {
      const batch = rootDb.batch()

      // Delete the close shift transaction.
      // ATL-1358 — same resolution as `deleteCashboxTransaction` below, and for the same reason: the
      // caller hands this the row `getCashboxTransactionsOnDateRange` returned, whose `ref` is the
      // backend's `{ id }` shim in Supabase read mode. A real ref passes straight through.
      const firestoreRef = getFirestoreRef(closeShiftTransaction, 'cashboxTransactions')
      batch.delete(firestoreRef)

      // Build stat fields to delete
      const statFields: Record<string, any> = {
        shiftClosed: fieldValue.delete(),
        shiftClosedTime: fieldValue.delete(),
        employeeBonusAmount: fieldValue.delete()
      }

      // Remove end amounts from daily stats
      if (closeShiftTransaction.endAmounts) {
        for (const key in closeShiftTransaction.endAmounts) {
          statFields[key + 'EndAmount'] = fieldValue.delete()
          statFields[key + 'SystemEndAmount'] = fieldValue.delete()
        }
      }

      // ATL-1358 — `dailyStats[store].ref` exists only on the Firestore branch of the atom: the
      // Supabase branch (`AppStack`) writes `{ ...stat, id: dateKey }` with no ref at all (ATL-1725:
      // `fetchDailyStatsFromSupabase` drops the backend's id-less `ref: {}`, which this `||` took for
      // a reference). Derive the business day's doc from the shift's own store and date (ATL-1013)
      // when the caller has no reference to give.
      const statRef = dailyStatRef || db.collection('dailyStats').doc(
        dailyStatIDForRecord(closeShiftTransaction.dailyStatID, closeShiftTransaction.store, closeShiftTransaction.date)
      )
      batch.update(statRef, statFields)
      // ATL-1358 review — a plain `commit()`, deliberately, and NOT `commitBatchTolerant`. A batch is
      // atomic: if this `update` hits a dailyStats doc Firestore does not hold, the `delete` above is
      // rolled back with it and BOTH stores keep the row — a failed operation, but a consistent one.
      // Tolerating the NOT_FOUND would let the Supabase delete on the next line run against a
      // Firestore that still has the document, which is the one-sided delete, not the cure for it.
      // Nor does this need `runIdempotentDelete` the way its sibling below does: every stat field
      // here is a `fieldValue.delete()` sentinel, so re-applying the reversal is a no-op.
      await batch.commit()

      // Dual write to Supabase via NestJS backend.
      // ATL-1245 — `bestEffortDualWriteDelete`, not `bestEffortDualWrite`: the backend's delete now
      // answers a REAL 404 for a row it does not hold (the old `count === 0` test was unreachable, so
      // every delete answered 200). A cashbox row written before this org started dual-writing was
      // never mirrored, so that 404 is the expected outcome, not a failure — and on the plain wrapper
      // it classifies as transient, queues, burns ten attempts over three days, reports a permanent
      // Supabase loss that never existed, and parks that document's other outbox entries behind it.
      await bestEffortDualWriteDelete('cashboxTransactions.delete', () => cashboxTransactionsApi.deleteCashboxTransaction(firestoreRef.id), { firestoreId: firestoreRef.id });
    },
    // Supabase mode only (P9b): the `DELETE /cashbox-transactions/:id` the body's dual-write sends, queued (the
    // body's batch commits against the offline cache too). Its local effect deletes the closing fields the body
    // deletes from the day — except `employeeBonusAmount`, which Supabase derives on the server (§8.5, not
    // decided): the overlay never guesses a server-derived value.
    plan: async () => {
      const firestoreRef = getFirestoreRef(closeShiftTransaction, 'cashboxTransactions')
      const documentId: string = firestoreRef.id
      // The day the body updates: the caller's reference, or the close's own business day (ATL-1013/1358).
      const dailyStatId = cancelledShiftCloseDayOf(dailyStatRef, () =>
        dailyStatIDForRecord(closeShiftTransaction.dailyStatID, closeShiftTransaction.store, closeShiftTransaction.date))
      const request = await buildOutboxRequest((client) => createCashboxTransactionsApi(client).deleteCashboxTransaction(documentId))
      return {
        documentId,
        relatedIds: [],
        request,
        effects: assertLocalEffects(cancelledShiftCloseLocalEffects(closeShiftTransaction, dailyStatId)),
        fromAck: () => undefined,
      }
    },
  })

export const getCashboxTransactionsOnDateRange = async (db: FirestoreDb, range: DateRange, flat = false, store: Store | null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: {
        startDate: string;
        endDate: string;
        storeId?: string;
      } = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      if (store) {
        params.storeId = store as unknown as string
      }

      // ATL-1477: every cashbox transaction in the range, not the newest 50.
      const transactions = await readWholeList(
        (window) => cashboxTransactionsApi.getCashboxTransactionsWindow({ ...params, ...window }),
        { label: 'cashbox-transactions', order: newestFirst('date') },
      ) as unknown as CashboxTransaction[]

      if (flat) {
        return transactions
      } else {
        const docs: Record<string, CashboxTransaction[]> = {} // date key - array of transactions
        transactions.forEach(function (tx) {
          const txDate = convertToDate(tx.date) || new Date()
          const txStore = tx.store as string | { id: string } | undefined
          const txStoreId = (typeof txStore === 'string' ? txStore : txStore?.id) || (typeof store === 'string' ? store : (store as { id?: string })?.id)
          const date = format(getLocalStoreDate(txStoreId, txDate), 'd/M/y EEEE')
          const newArr = docs[date] ? [...docs[date]] : []
          newArr.push(tx)
          docs[date] = [...newArr]
        })
        const docsArr: GroupedData<CashboxTransaction>[] = []
        for (const key in docs) {
          const newData = {
            title: key,
            data: docs[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching cashbox transactions from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'cashboxTransactions' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  let query = db.collection('cashboxTransactions') as FirestoreQuery<CashboxTransaction>
  if (store) {
    query = query.where('store', '==', store)
  }
  const querySnapshot = await query
    .where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatCashbox: CashboxTransaction[] = []
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as CashboxTransaction
      snap.id = doc.id
      snap.ref = doc.ref
      flatCashbox.push(snap)
    })
    return flatCashbox
  } else {
    const groupedCashbox: Record<string, CashboxTransaction[]> = {} // date key - array of transactions
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as CashboxTransaction
      snap.id = doc.id
      snap.ref = doc.ref
      const snapDate = snap.date as { toDate: () => Date }
      const date = format(snapDate.toDate(), 'd/M/y EEEE')
      const newArr = groupedCashbox[date] ? [...groupedCashbox[date]] : []
      newArr.push(snap)
      groupedCashbox[date] = [...newArr]
    })
    const resultCashboxArr: GroupedData<CashboxTransaction>[] = []
    for (const key in groupedCashbox) {
      const newData = {
        title: key,
        data: groupedCashbox[key]
      }
      resultCashboxArr.push(newData)
    }
    return resultCashboxArr
  }
}

/**
 * The cash wallet a cashbox row belongs to, as the ID STRING the dailyStats keys are built from.
 *
 * ATL-1358 — the two read paths spell this field differently and only one of them is a string.
 * Firestore hands back what was written (`'<storeId>-cash'`); the backend hands back the lookup
 * OBJECT its transform builds (`{ id, firestoreId, name, text }`). Concatenating the object
 * yields `[object Object]WithdrawalsAmount`, so the reversal is booked under a key no screen
 * reads: the row disappears while the shift report keeps counting it. That is a quieter failure
 * than the one this ticket was filed for, and a worse one — hence a named helper rather than an
 * inline `||`.
 */
const cashboxWalletId = (info: CashboxTransaction): string => {
  const wallet = info.wallet as string | { id?: string } | null | undefined
  const walletId = typeof wallet === 'string' ? wallet : wallet?.id
  return walletId || (info.store + '-cash')
}

/**
 * Delete a cash deposit or withdrawal (or any cashbox row), reversing its day's amount and deleting its money leg.
 *
 * ATL-1445 stage 3 P9b — through the `writeW` seam; Firestore mode runs master's body verbatim. Supabase mode
 * is refuse-offline (invariant 12): the body runs in a Firestore transaction, which fails visibly offline.
 */
export const deleteCashboxTransaction = (db: FirestoreDb, info: CashboxTransaction): Promise<void> =>
  writeW({
    label: 'cashboxTransactions.delete',
    kind: 'delete',
    offline: 'refuse',
    firestoreFirst: async () => {
      // ATL-1358 — resolve the ref rather than trusting `info.ref`. In Supabase read mode the row
      // arrives from the backend carrying `ref: { id }` — a plain object, not a DocumentReference —
      // and `batch.delete()` rejects that SYNCHRONOUSLY, before a single write is queued. The throw
      // reached an un-awaited `async` onPress, so the tap did nothing at all and said nothing: no
      // Firestore delete, no dailyStats reversal, no Supabase delete, no error. A real ref is passed
      // through untouched, so the Firestore path is unchanged.
      const firestoreRef = getFirestoreRef(info, 'cashboxTransactions')
      // ATL-1013: business day, not the device's calendar day — see deleteSale.
      const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.date as DateLike)
      const statFields: Record<string, unknown> = {}
      const walletId = cashboxWalletId(info)
      // The money leg. Hoisted out of the two type branches it used to be duplicated in: only
      // `deposit` and `withdrawal` are ever WRITTEN with a `transactionId` (`saveCashboxTransaction`),
      // so this is the same set of rows, expressed as the condition that actually matters — a leg is
      // deleted because one exists, not because of how the row is labelled.
      const legId = info.transactionId
      if (info.type?.id === 'deposit') {
        statFields[walletId + 'DepositsAmount'] = fieldValue.increment(-(info.amount || 0))
      }
      if (info.type?.id === 'withdrawal') {
        statFields[walletId + 'WithdrawalsAmount'] = fieldValue.increment(-(info.amount || 0))
      }
      // ATL-1358 — idempotent, because fixing the button above is what makes a second tap REACHABLE.
      // The reversal is a `fieldValue.increment`, so a batch re-applied by a double-tap (this row sits
      // on screen for the whole Firestore+API round trip, and this customer's experience of the button
      // was precisely "tap it again, nothing happened") would subtract the withdrawal from the shift
      // TWICE. `runIdempotentDelete` reads the row inside a transaction first and skips the whole
      // reversal when it is already gone — the same guard `deleteSupply`/`deleteSale` use, added after
      // a triple-fired supply delete drove a product's stock to -645.
      //
      // THE TRADE, stated because it is not free: a transaction needs a server round trip, while the
      // batch this replaced committed against the offline cache and synced later. So a till with no
      // connectivity now gets a visible "could not delete" instead of a queued delete. That is the
      // same trade every other money-moving delete here already makes (`deleteSale`, `deleteSupply`,
      // `deleteWriteOff`, `deleteRevision`, `deleteTransfer`), and offline REPLAY is one of the
      // hazards this helper names: a queued `increment(-amount)` that replays is this bug in money.
      await runIdempotentDelete(firestoreRef, 'cashboxTransactions.delete', (tx: any) => {
        if (legId) {
          tx.delete(db.collection('transactions').doc(legId))
        }
        tx.update(db.collection('dailyStats').doc(statDocName), statFields)
      })

      // Dual write to Supabase via NestJS backend. Keyed by the ref actually deleted, so the two
      // stores can never be asked to delete different documents.
      const cashboxTransactionId = firestoreRef.id
      // ATL-1245 — a never-mirrored row now answers a real 404; see `cancelShiftClosing` above for
      // why that has to reach `bestEffortDualWriteDelete` rather than the plain wrapper.
      await bestEffortDualWriteDelete('cashboxTransactions.delete', () => cashboxTransactionsApi.deleteCashboxTransaction(cashboxTransactionId), { firestoreId: cashboxTransactionId });
    },
    // Supabase mode only (P9b): REFUSE-OFFLINE (invariant 12, as the body's transaction is). The
    // `DELETE /cashbox-transactions/:id` the body's dual-write sends, sent once behind an empty queue; the
    // server reverses the day's amount and buries the money leg in one transaction. No local effects: it
    // resolves only on the acknowledgement, and the next read shows the server's totals.
    plan: async () => {
      const firestoreRef = getFirestoreRef(info, 'cashboxTransactions')
      const documentId: string = firestoreRef.id
      const request = await buildOutboxRequest((client) => createCashboxTransactionsApi(client).deleteCashboxTransaction(documentId))
      return {
        documentId,
        relatedIds: [],
        request,
        effects: null,
        ackDisposition: cashboxDeleteAckDisposition,
        fromAck: () => undefined,
      }
    },
  })

// ============================================================================
// ATL-1445 stage 3 P9b — the cashbox writers' Supabase-mode plans
// ============================================================================
//
// Supabase mode only: nothing below runs in Firestore mode, where each writer runs master's body verbatim.
// `firebaseCashboxWriters.p9bDifferential.test.js` proves Firestore mode unchanged;
// `firebaseCashboxWritersSupabase.p9b.test.js` holds every plan to its body (the request byte for byte, the
// day's fields to the batch's own, the wipon close call by call).

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** The cashbox operation types the backend accepts (`CreateCashboxTransactionDto.type`). */
const CASHBOX_TRANSACTION_TYPES = ['openShift', 'closeShift', 'deposit', 'withdrawal'] as const
type CashboxTransactionTypeId = (typeof CASHBOX_TRANSACTION_TYPES)[number]
const isCashboxTransactionTypeId = (value: unknown): value is CashboxTransactionTypeId =>
  typeof value === 'string' && (CASHBOX_TRANSACTION_TYPES as readonly string[]).includes(value)

/**
 * What `saveCashboxTransaction`'s plan reads of its untyped `info` (the screens build it: `ShiftScreen`,
 * `CashboxTransactionScreen`, `ShiftsStats`). Every field is narrowed before use.
 */
type CashboxWriteInput = {
  type?: unknown
  amount?: unknown
  wallet?: unknown
  store?: unknown
  date?: unknown
  dailyStatID?: unknown
  endAmounts?: unknown
  systemEndAmounts?: unknown
}

/** The operation's type id. The body branches on `info.type.id`; an unknown one writes no cashbox row there. */
const cashboxTransactionTypeOf = (input: CashboxWriteInput): CashboxTransactionTypeId => {
  const id = isPlainRecord(input.type) ? input.type.id : undefined
  if (!isCashboxTransactionTypeId(id)) {
    throw new Error(`saveCashboxTransaction: unknown cashbox operation type ${JSON.stringify(id ?? null)}`)
  }
  return id
}

/**
 * The amount the request carries: the body's `info.amount || 0`. An open, a deposit and a withdrawal carry the
 * cashier's amount, which must be a finite number (the body would book `NaN` into the day and `0` into
 * Supabase); a close carries none (its amounts are `endAmounts`), or a finite one.
 */
const cashboxAmountOf = (type: CashboxTransactionTypeId, amount: unknown): number => {
  if (type === 'closeShift' && amount === undefined) return 0
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    throw new Error(`saveCashboxTransaction: the ${type} amount is not a finite number`)
  }
  return amount || 0
}

/**
 * Per-wallet / per-method amounts (`endAmounts`, `systemEndAmounts`): absent, or an object of finite numbers.
 * The body forwards them as they are; a non-number would be written to the day as a closing figure.
 */
const cashboxAmountsOf = (field: string, value: unknown): Record<string, number> | undefined => {
  if (!value) return undefined
  if (!isPlainRecord(value)) throw new Error(`saveCashboxTransaction: ${field} is not an object`)
  const out: Record<string, number> = {}
  for (const [key, amount] of Object.entries(value)) {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) {
      throw new Error(`saveCashboxTransaction: ${field}.${key} is not a finite number`)
    }
    out[key] = amount
  }
  return out
}

/**
 * The cash wallet as the body spells it into the day's keys and the money leg (`info.wallet || <store>-cash`).
 * A wallet that is not an id string is refused: the body would key the day's fields `[object Object]…`
 * (the ATL-1358 class) and send the leg a wallet the backend's DTO refuses.
 */
const cashboxWalletOf = (wallet: unknown): string | undefined => {
  if (wallet === undefined || typeof wallet === 'string') return wallet
  throw new Error('saveCashboxTransaction: the wallet is not a wallet id')
}

/**
 * The request's `date`, as the body's dual-write spells it (a Timestamp's or a Date's ISO instant, a string as
 * is). An operation with no readable instant is refused: the body cannot write one either (Firestore refuses
 * `undefined`), and a row with no date is outside every date-range read.
 */
const cashboxDateOf = (date: unknown): string => {
  const iso =
    isPlainRecord(date) && typeof date.toDate === 'function'
      ? isoInstantOf(date)
      : date instanceof Date
        ? isoInstantOf(date)
        : typeof date === 'string' && Number.isFinite(Date.parse(date))
          ? date
          : null
  if (iso === null) throw new Error('saveCashboxTransaction: the operation has no readable date')
  return iso
}

/**
 * The `POST /cashbox-transactions` body `saveCashboxTransaction`'s dual-write sends, field for field and in its
 * order (the request is compared BYTE for byte with the body's): the operation, and for a deposit or a
 * withdrawal its money leg (ATL-1245), the `transactions` document the body writes in the same batch.
 */
const cashboxTransactionCreateDto = (
  firestoreId: string,
  type: CashboxTransactionTypeId,
  input: CashboxWriteInput,
  legId: string | null,
): CreateCashboxTransaction => {
  const store = input.store
  if (typeof store !== 'string' || store.length === 0) throw new Error('saveCashboxTransaction: the operation names no store')
  const amount = cashboxAmountOf(type, input.amount)
  const wallet = cashboxWalletOf(input.wallet)
  const endAmounts = cashboxAmountsOf('endAmounts', input.endAmounts)
  const systemEndAmounts = cashboxAmountsOf('systemEndAmounts', input.systemEndAmounts)
  const date = cashboxDateOf(input.date)
  const cashWallet = wallet || (store + '-cash')
  const transaction =
    legId === null
      ? null
      : type === 'deposit'
        ? { firestoreId: legId, value: amount, destinationWallet: cashWallet }
        : { firestoreId: legId, value: amount, sourceWallet: cashWallet, destinationWallet: store + '-extracted-cash' }
  return {
    firestoreId,
    storeId: store,
    type,
    amount,
    wallet,
    ...(endAmounts ? { endAmounts } : {}),
    ...(systemEndAmounts ? { systemEndAmounts } : {}),
    date,
    ...(transaction ? { transaction } : {}),
  }
}

/** The day's fields `dailyStatIncrementsForCashboxTransactions` moves by increment (the rest it assigns). */
const isCashboxIncrementField = (field: string): boolean => field.includes('DepositsAmount') || field.includes('WithdrawalsAmount')

/** A day field's value as the local view shows it: a date as its ISO instant, a primitive as is. */
const cashboxAssignValue = (field: string, value: unknown): JsonPrimitive => {
  const shown = toDisplayJson(value)
  if (shown === undefined || (shown !== null && typeof shown === 'object')) {
    throw new Error(`cashbox local effects: ${field} is not a day field value`)
  }
  return shown
}

/**
 * The local effects of a change to one business day's shift fields: increments, assignments and deletions on
 * the day's `dailyStatId`. A `dailyStatId` that is not a `d-M-y-<store>` id, or no store, contributes no day
 * rather than a guessed one (as `paidSaleLocalEffects`).
 */
const cashboxDayEffects = (
  store: unknown,
  dailyStatId: unknown,
  increments: { [field: string]: number },
  assign: { [field: string]: JsonPrimitive },
  remove: string[],
): LocalEffects => {
  const businessDay = businessDayOfDailyStatId(dailyStatId)
  const dailyStats: DailyStatEffect[] =
    businessDay !== null && typeof dailyStatId === 'string' && typeof store === 'string' && store.length > 0
      ? [{ storeId: store, dailyStatId, businessDay, increments, assign, ...(remove.length > 0 ? { remove } : {}) }]
      : []
  return { v: LOCAL_EFFECTS_VERSION, stock: [], dailyStats, records: [] }
}

/**
 * The day's fields a cashbox operation books, from the batch's own function
 * (`dailyStatIncrementsForCashboxTransactions`, as raw numbers): deposits and withdrawals as increments, the
 * shift's open / close flags, times and amounts as assignments. On `info.dailyStatID`, as the body's batch.
 */
const cashboxTransactionLocalEffects = (input: CashboxWriteInput): LocalEffects => {
  const fields: Record<string, unknown> = dailyStatIncrementsForCashboxTransactions([input], false, false)
  const increments: Record<string, unknown> = {}
  const assign: { [field: string]: JsonPrimitive } = {}
  for (const [field, value] of Object.entries(fields)) {
    if (isCashboxIncrementField(field)) increments[field] = value
    else assign[field] = cashboxAssignValue(field, value)
  }
  return cashboxDayEffects(input.store, input.dailyStatID, rawIncrements(increments), assign, [])
}

/**
 * The document, request and day field of a shift amount edit, as `editCashboxShiftStats`'s body picks them:
 * the open's amount (type 0, `<wallet>StartAmount`), or one method's counted amount at close (type 3,
 * `<method>EndAmount`). Any other type: the body edits nothing, so there is nothing to queue — refused.
 */
const cashboxShiftEditOf = (
  shift: { openId?: unknown; id?: unknown; endAmounts?: unknown },
  editType: number,
  newAmount: number,
  store: string,
  editPaymentMethod: string,
  cashWallet: string,
): { documentId: string; dto: UpdateCashboxTransaction; field: string } => {
  if (editType === 0) {
    if (typeof shift.openId !== 'string' || shift.openId.length === 0) throw new Error('editCashboxShiftStats: the shift has no open')
    return { documentId: shift.openId, dto: { amount: newAmount }, field: (cashWallet || (store + '-cash')) + 'StartAmount' }
  }
  if (editType === 3) {
    if (typeof shift.id !== 'string' || shift.id.length === 0) throw new Error('editCashboxShiftStats: the shift has no close')
    const endAmounts = isPlainRecord(shift.endAmounts) ? shift.endAmounts : {}
    return { documentId: shift.id, dto: { endAmounts: { ...endAmounts, [editPaymentMethod]: newAmount } }, field: editPaymentMethod + 'EndAmount' }
  }
  throw new Error(`editCashboxShiftStats: edit type ${editType} edits nothing`)
}

/**
 * The id of the day `cancelShiftClosing`'s body updates: the caller's `dailyStatRef` when it gave one (a
 * Firestore reference — one with no id is refused: the body's `batch.update` throws on it), else `derived`.
 */
const cancelledShiftCloseDayOf = (dailyStatRef: unknown, derived: () => string): string => {
  if (!dailyStatRef) return derived()
  if (isPlainRecord(dailyStatRef) && typeof dailyStatRef.id === 'string' && dailyStatRef.id.length > 0) return dailyStatRef.id
  throw new Error('cancelShiftClosing: the day reference has no id')
}

/**
 * The day's fields a cancelled close deletes, on the day the body updates (`dailyStatRef`, or the close's own
 * business day): `shiftClosed`, `shiftClosedTime`, and each counted method's `EndAmount` / `SystemEndAmount`.
 * NOT `employeeBonusAmount`, which the body also deletes: Supabase derives it on the server (§8.5, not
 * decided), so the overlay leaves the server's value alone.
 */
const cancelledShiftCloseLocalEffects = (close: { endAmounts?: unknown; store?: unknown }, dailyStatId: string): LocalEffects => {
  const remove = ['shiftClosed', 'shiftClosedTime']
  if (isPlainRecord(close.endAmounts)) {
    for (const key of Object.keys(close.endAmounts)) remove.push(key + 'EndAmount', key + 'SystemEndAmount')
  }
  return cashboxDayEffects(close.store, dailyStatId, {}, {}, remove)
}

/**
 * What `DELETE /cashbox-transactions/:id` answers (`CashboxTransactionsService.deleteCashboxTransaction`,
 * pinned by `firebaseCashboxWritersSupabase.p9b.test.js` against the backend source): `{ success: true }`,
 * both when this request deleted the row and when it was already soft-deleted (`already_deleted`); a row the
 * backend does not hold is an entity 404 (`absent`, by the branch). The writer has no tail, so `applied` for
 * both 200s vouches for nothing it would act on: the row is gone either way. Anything else: `unrecognized`.
 */
export const cashboxDeleteAckDisposition = (ack: unknown): WAckDisposition =>
  isPlainRecord(ack) && ack.success === true ? 'applied' : 'unrecognized'

/**
 * The wipon-kassa close `saveCashboxTransaction`'s body runs before it commits a shift close, for Supabase
 * mode (`beforeAppend`). The body keeps its own copy inline, so Firestore mode is byte- and microtask-identical
 * to master; `firebaseCashboxWritersSupabase.p9b.test.js` holds this copy to it call by call — with ONE
 * difference: the body persists credentials it had to refresh (`updateStoreWiponCredentials`, a W `stores`
 * writer not converted yet, P13); this copy does not, because in Supabase mode an unconverted W writer may not
 * run (it would write Firestore first). The refreshed credentials are used for this close and then dropped: the
 * next wipon call refreshes them again. The stores conversion re-adds the write here (side ticket in §18).
 */
const closeWiponShiftForCashboxClose = async (store: string): Promise<void> => {
  const storeInfo = getDefaultStore().get(storesDictAtom)?.[store]
  const fiscalOperator = storeInfo?.fiscalOperator
  if (isPlainRecord(fiscalOperator) && fiscalOperator.id === 'wipon-kassa') {
    try {
      let newStats = await getActiveShift(storeInfo?.wiponCredentials)
      let newCredentials = storeInfo?.wiponCredentials
      if (!newStats) {
        const refreshed = await refreshCredentials(storeInfo?.wiponCredentials)
        if (refreshed) {
          newCredentials = refreshed
          newStats = await getActiveShift(refreshed)
        }
      }
      if (newStats) {
        await extractCash(newCredentials, newStats.shift.cashbox.balance)
        await closeCashboxShift(newCredentials)
      }
    } catch(error) {
      // ATL-2035: a fiscal cash withdrawal / shift close that failed — a write failure, kept in Sentry.
      Sentry.captureException(error, { tags: { books_write_error: 'wipon-shift-close' } })
      console.log(error)
    }
  }
}

/**
 * ATL-867 — a saved cart's `discountAmount` must be the SAME finite number in both stores.
 *
 * The checkout screen holds it as a text-input value: the number 0 until the cashier types, a
 * string afterwards. Sending the raw state made the backend refuse the cart with a 400
 * ("discountAmount must be a number conforming to the specified constraints"), which
 * `classifyDualWriteDefect` treats as a DETERMINISTIC defect — never queued in the outbox, never
 * retried — while Firestore had already committed. The cart existed in one store and not the other.
 *
 * Normalising HERE, before the Firestore write, is what makes the two stores agree: coercing only
 * at the API call would have left Firestore holding "500" and Supabase 500, which is a parity
 * defect of its own. This runs for every caller, not just the checkout screen.
 *
 * An UNPARSEABLE value is deliberately left untouched rather than defaulted to 0 — inventing a
 * number for a money field is the silent-wrong-books failure this repo forbids. The screen refuses
 * such input before it ever reaches here; if something else produces one, the API rejects it loudly.
 */
const withNormalizedDiscountAmount = (info: any) => {
  if (!info || typeof info !== 'object' || !('discountAmount' in info)) return info
  const normalized = parseMoneyInput(info.discountAmount)
  if (normalized === null || normalized === info.discountAmount) return info
  return { ...info, discountAmount: normalized }
}

export const saveSavedCart = async (db: FirestoreDb, info: any) => {
  // Sanitize cart items to remove non-serializable fields (ref, functions, etc.)
  const sanitizedInfo = sanitizeForFirestoreDeep(withNormalizedDiscountAmount(info)) as typeof info
  const result = await db.collection('savedCarts').add(sanitizedInfo)

  // Dual write to Supabase via NestJS backend (pass-through pattern)
  // Use sanitizedInfo to avoid sending non-serializable Firestore objects (DocumentReferences, Timestamps).
  // Saved-cart items use the same CartItemDto as sales, so coerce them to the API's expected types
  // (barcode→string, createdAt/updatedAt→ISO) — raw Firestore products carry number/Timestamp in
  // Firestore-read mode and would fail request validation otherwise (ATL-481). Best-effort (ATL-461).
  const savedInfo = sanitizedInfo as Record<string, any>
  await bestEffortDualWrite('savedCarts.create', () => savedCartsApi.createSavedCart({
    ...savedInfo,
    ...(Array.isArray(savedInfo.cartItems) && {
      cartItems: savedInfo.cartItems.map((it: any) => coerceCartItemForApi(it)),
    }),
    ...(Array.isArray(savedInfo.items) && {
      items: savedInfo.items.map((it: any) => coerceCartItemForApi(it)),
    }),
    firestoreId: result.id,
  } as Record<string, unknown>));

  return result
}

export const deleteSavedCart = async (ref: any) => {
  const firestoreRef = getFirestoreRef(ref, 'savedCarts')
  await firestoreRef.delete()

  // Dual write to Supabase via NestJS backend. ATL-1188: durable — a transient failure here is
  // queued and replayed, where the hand-rolled try/catch this replaces reported two tags and
  // dropped the write. A 404 stays free: the cart predates this org's dual-write and was never
  // mirrored (see `bestEffortDualWriteDelete`).
  await bestEffortDualWriteDelete(
    'savedCarts.delete',
    () => savedCartsApi.deleteSavedCart(firestoreRef.id),
    { firestoreId: firestoreRef.id },
  )
}

/**
 * The `constants/printChecks` document, as the fleet actually stores it (ATL-1038).
 *
 * `{name, value}` on 400 of 402 orgs, plus printer GEOMETRY (`fontSize`, `width`) on two and a
 * redundant `organizationId` on one. The index signature is not laziness — it is the reason the
 * dual-write below sends the whole document rather than reconstructing `{name, value}`, which would
 * silently drop those orgs' printer settings on the first toggle.
 */
interface PrintChecksConstant {
  name?: string
  value?: boolean
  [key: string]: unknown
}

/**
 * ATL-1038 — the receipt-printing toggle, dual-written.
 *
 * ## What was wrong
 *
 * This function wrote Firestore and returned. It was the only writer of `constants/printChecks`,
 * so the Supabase copy could only ever be whatever the migration last put there — and a merchant
 * who toggled receipt printing changed one store and not the other. A fleet-wide correction on
 * 2026-08-23 fixed six organizations and drove `constants-parity` from 11 to 5; two had drifted
 * again by 2026-09-01 (SixSeven and HIM.HOUSE ASTANA, both `false` in Firestore and `true` in
 * Supabase), because the writer was never fixed. At the read cutover both would have STARTED
 * printing receipts nobody asked for.
 *
 * ## Why the whole document, and why it is read back
 *
 * The Firestore write is a FIELD update: it changes `value` and leaves the rest of the document
 * alone. Sending `{name: 'printChecks', value}` to Supabase would not be the same write — on the
 * three orgs whose document carries more than `{name, value}` it would erase printer geometry and
 * create drift of its own while claiming to fix it. Reading the document back after the update and
 * storing it verbatim makes the row equal to the document by construction, which is exactly what
 * the `doc-kv` parity check compares.
 *
 * The read-back lives INSIDE the `bestEffortDualWrite` callback so that it inherits the wrapper's
 * contract: the gate skips it entirely when dual-write is off (no wasted read), and a failure is
 * reported rather than swallowed. Firestore is committed first and is the source of truth while
 * reads are on it, so nothing here may throw to the caller — `PaymentView` does not await this.
 *
 * `firestoreId` MUST be passed: without it `enqueueDualWriteFailure` cannot derive a document
 * identity from a URL whose segments are all short words (`/settings/constants/printChecks` — the
 * id-shaped-segment test needs 15+ alphanumeric characters), and it would refuse to queue a
 * transient failure. `printChecks` is the document id, and it is also the correct supersede key:
 * a newer toggle must replace an older queued one rather than replay after it.
 */
export const updatePrintChecks = async (db: FirestoreDb, value: boolean) => {
  const ref = db.collection<PrintChecksConstant>('constants').doc('printChecks')
  // Firestore is the source of truth — this write must surface to the user if it fails.
  await ref.update({ value })

  // Dual write to Supabase via NestJS backend — best-effort (canonical wrapper: never throws).
  await bestEffortDualWrite(
    'organizationConstants.printChecks.update',
    async () => {
      const snapshot = await ref.get()
      const document = snapshot.data()
      if (!document) {
        // The update above succeeded, so the document existed a moment ago. Sending a fabricated
        // body instead would write a printer setting nobody chose.
        throw new Error('constants/printChecks could not be read back after a successful update')
      }
      return settingsApi.upsertOrganizationConstant('printChecks', document)
    },
    { firestoreId: 'printChecks' },
  )
}

export const saveProductTag = async (db: FirestoreDb, category: any, index: number | null, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveProductTag')
  let newData: any[] = []
  if (category.data?.length > 0) {
    newData = [...category.data]
  }
  const isNew = index == null
  if (isNew) {
    newData.push(info)
  } else {
    newData[index] = info
  }
  // Firestore is the source of truth — this write must surface to the user if it fails
  // (the previous single try/catch swallowed Firestore errors too = silent data loss).
  const firestoreRef = getFirestoreRef(category, 'productTags')
  reportFirestoreOnlyWriterUsage('saveProductTag', db?.id)
  await firestoreRef.update({ data: newData })

  // Dual write to Supabase via NestJS backend — best-effort (canonical wrapper: never throws).
  if (isNew) {
    await bestEffortDualWrite('productTags.create', () => productTagsApi.createProductTag({
      firestoreId: info.id,
      name: info.name,
      color: info.color,
      icon: info.icon,
      categoryId: category.id,
    }), { firestoreId: info.id });
  } else {
    await bestEffortDualWrite('productTags.update', () => productTagsApi.updateProductTag(info.id, {
      name: info.name,
      color: info.color,
      icon: info.icon,
    }), { firestoreId: info.id });
  }
}

export const uploadProductTagImage = (file: any, name: string) => storage.child('productTagImages/' + name).put(file, {
  contentType: 'image/png',
})

export const getChecklist = async (db: FirestoreDb, id: string) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const response = await api.get(`/checklists/${id}`)
      return transformApiItem(response.data, 'checklists')
    } catch (error: unknown) {
      console.error('Error fetching checklist from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'checklists' },
        extra: {
          checklistId: id,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  const querySnapshot = await db.collection('checklists').doc(id).get()
  if (snapshotExists(querySnapshot)) {
    const doc = querySnapshot.data() as any
    if (doc) {
      doc.id = querySnapshot.id
      doc.ref = querySnapshot.ref
      return doc
    }
  }
  return null
}

export const getLatestChecklistBeforeDate = async (db: FirestoreDb, date: Date, store: string) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: Record<string, any> = {
        date: date instanceof Date ? date.toISOString() : date,
      }

      if (store) {
        params.storeId = store
      }

      const response = await api.get('/checklists/latest/before-date', { params })
      return response.data ? transformApiItem(response.data, 'checklists') : null
    } catch (error: unknown) {
      console.error('Error fetching latest checklist from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'checklists' },
        extra: {
          date,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  const querySnapshot = await db.collection('checklists').where('store', '==', store).where('date', '<', date).orderBy('date', 'desc').limit(1).get()
  if (!querySnapshot.empty) {
    const snap = querySnapshot.docs[0].data() as any
    if (snap) {
      snap.id = querySnapshot.docs[0].id
      snap.ref = querySnapshot.docs[0].ref
      return snap
    }
  }
  return null
}


export const updateChecklist = async (db: FirestoreDb, id: string, info: any) => {
  const batch = rootDb.batch()
  batch.set(db.collection('checklists').doc(id), info)
  // update daily stats
  let incompleteTasks = 0
  for (const [_index, value] of info.content.entries()) {
    if ((value.type === 'task' || value.type === 'extra') && !value.done) {
      incompleteTasks += 1
    }
  }
  const statFields = {
    incompleteChecklistItems: incompleteTasks,
  }
  batch.update(db.collection('dailyStats').doc(id.replace('-seller', '')), statFields)
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('checklists.update', () => checklistsApi.updateChecklist(id, {
    content: info.content,
    checklistType: info.checklistType || 'seller',
  }));
}

export const addChecklist = async (db: FirestoreDb, id: string, info: any) => {
  const batch = rootDb.batch()
  batch.set(db.collection('checklists').doc(id), info)
  // update daily stats
  let incompleteTasks = 0
  for (const [_index, value] of info.content.entries()) {
    if ((value.type === 'task' || value.type === 'extra') && !value.done) {
      incompleteTasks += 1
    }
  }
  const statFields = {
    incompleteChecklistItems: incompleteTasks,
  }
  batch.set(db.collection('dailyStats').doc(id.replace('-seller', '')), {
    ...statFields,
    date: info.date,
    store: info.store,
    organizationId: info.organizationId
  }, {merge: true})
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  // Note: organizationId is extracted from JWT by the backend
  await bestEffortDualWrite('checklists.create', () => checklistsApi.createChecklist({
    firestoreId: id,
    storeId: info.store,
    checklistType: info.checklistType || 'seller',
    content: info.content,
    date: info.date?.toDate ? info.date.toDate().toISOString() : (info.date instanceof Date ? info.date.toISOString() : info.date),
  }));
}

// export const getProductUpdatesOnDateRange = async (db, range, flat) => {
//   const startDate = new Date(range.startDate.setHours(0,0,0,0))
//   const endDate = new Date(range.endDate.setHours(23,59,59,999))
//   var querySnapshot = db.collectionGroup('history')
//   querySnapshot = await querySnapshot.where('lastChangeAt', '>=', startDate)
//     .where('date', '<=', endDate)
//     .orderBy('date', 'desc')
//     .get()
//   if (flat) {
//     var docs = [] 
//     querySnapshot.forEach(function(doc) {
//       var snap = doc.data()
//       snap.id = doc.id
//       snap.ref = doc.ref
//       docs.push(snap)
//     })
//     return docs
//   } else {
//     var docs = {} // date key - array of sales
//     querySnapshot.forEach(function(doc) {
//       var snap = doc.data()
//       snap.id = doc.id
//       snap.ref = doc.ref
//       const date = format(snap.lastChangeAt.toDate(), "d/M/y EEEE")
//       var newArr = docs[date] ? [...docs[date]] : []
//       newArr.push(snap)
//       docs[date] = [...newArr]
//     })
//     var docsArr = []
//     for (var key in docs) {
//       const newData = {
//         title: key,
//         data: docs[key]
//       }
//       docsArr.push(newData)
//     }
//     return docsArr
//   }
// }

export const saveNewStore = async (db: FirestoreDb, info: any, allFlatProducts: any[], constants: any, copySettingsFrom: { id: string } | null = null) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewStore')
  const batch = rootDb.batch()
  const newStoreRef = db.collection('stores').doc()
  batch.set(newStoreRef, info)
  // create new cash wallet for that store
  const cashWalletInfo = {
    id: newStoreRef.id + '-cash',
    text: i18n.t('cashFromCashbox') + ' ' + info.name,
    storeId: newStoreRef.id,
  }
  const extractedCashWalletInfo = {
    id: newStoreRef.id + '-extracted-cash',
    text: i18n.t('extractedCash') + ' ' + info.name,
    storeId: newStoreRef.id,
  }
  // This save rebuilds the wallets/paymentMethods/purchaseMethods constants
  // arrays and writes them back. If any is not yet an array, `constants` hasn't
  // fully hydrated (e.g. partial Supabase subscription load) — spreading/iterating
  // undefined throws (ATL-597: Hermes "Cannot convert undefined value to object").
  // Abort before committing rather than defaulting to []: writing [] would wipe
  // the org's existing config. Throwing preserves the data and surfaces a
  // retryable error (the original crash already aborted before commit too).
  if (
    !Array.isArray(constants?.wallets?.data) ||
    !Array.isArray(constants?.paymentMethods?.data) ||
    !Array.isArray(constants?.purchaseMethods?.data)
  ) {
    throw new Error('CONSTANTS_NOT_LOADED')
  }
  const walletsData = constants.wallets.data
  const paymentMethodsData = constants.paymentMethods.data
  const purchaseMethodsData = constants.purchaseMethods.data
  const newWalletsData = [...walletsData, cashWalletInfo, extractedCashWalletInfo]
  batch.update(db.collection('constants').doc('wallets'), {
    data: sanitizeConstantsData(newWalletsData)
  })
  // ATL-750: every constants element mutated below is ALSO a Supabase row that must be updated.
  // Collected with its array index (the index IS the displayOrder — see savePaymentMethods /
  // savePurchaseMethods) so the dual-write after the commit can send exactly what changed.
  type MutatedConstant = { element: Record<string, unknown> & { id: string }; index: number }
  const mutatedPaymentMethods: MutatedConstant[] = []
  const mutatedPurchaseMethods: MutatedConstant[] = []

  if (copySettingsFrom) {
    // Validate source store has all required wallet configurations
    const missingWallets: string[] = []
    for (const element of paymentMethodsData) {
      if (element.id !== 'cash' && element.activeInStore?.[copySettingsFrom.id] && !element.destinationWalletForStore?.[copySettingsFrom.id]) {
        missingWallets.push(element.text || element.id)
      }
    }
    if (missingWallets.length > 0) {
      throw new Error(`MISSING_WALLET_CONFIG:${missingWallets.join(',')}`)
    }

    // payment methods - activeInStore, destinationWalletForStore
    const newPMDataCopy: unknown[] = []
    for (let index = 0; index < paymentMethodsData.length; index++) {
      const element = paymentMethodsData[index]
      element.activeInStore = { ...element.activeInStore, [newStoreRef.id]: element.activeInStore[copySettingsFrom.id] || false }
      if (element.id === 'cash') {
        element.destinationWalletForStore = { ...element.destinationWalletForStore, [newStoreRef.id]: cashWalletInfo }
      } else {
        element.destinationWalletForStore = {
          ...element.destinationWalletForStore,
          [newStoreRef.id]: element.destinationWalletForStore[copySettingsFrom.id]
        }
      }
      newPMDataCopy.push(element)
      mutatedPaymentMethods.push({ element, index })
    }
    batch.update(db.collection('constants').doc('paymentMethods'), { data: sanitizeConstantsData(newPMDataCopy) })

    // purchase methods - activeInStore
    const newPurData: unknown[] = []
    for (let idx = 0; idx < purchaseMethodsData.length; idx++) {
      const element = purchaseMethodsData[idx]
      element.activeInStore = { ...element.activeInStore, [newStoreRef.id]: element.activeInStore[copySettingsFrom.id] }
      newPurData.push(element)
      mutatedPurchaseMethods.push({ element, index: idx })
    }
    batch.update(db.collection('constants').doc('purchaseMethods'), { data: sanitizeConstantsData(newPurData) })
  } else {
    // payment methods - activeInStore, destinationWalletForStore
    const newPMDataDefault: unknown[] = []
    for (let index = 0; index < paymentMethodsData.length; index++) {
      const element = paymentMethodsData[index]
      if (element.id === 'cash') {
        element.destinationWalletForStore = { ...element.destinationWalletForStore, [newStoreRef.id]: cashWalletInfo }
        element.activeInStore = { ...element.activeInStore, [newStoreRef.id]: true }
        mutatedPaymentMethods.push({ element, index })
      }
      newPMDataDefault.push(element)
    }
    batch.update(db.collection('constants').doc('paymentMethods'), { data: sanitizeConstantsData(newPMDataDefault) })
    // purchase methods - activeInStore
    const newPurDataDefault: unknown[] = []
    for (let idx = 0; idx < purchaseMethodsData.length; idx++) {
      const element = purchaseMethodsData[idx]
      element.activeInStore = { ...element.activeInStore, [newStoreRef.id]: true }
      newPurDataDefault.push(element)
      mutatedPurchaseMethods.push({ element, index: idx })
    }
    batch.update(db.collection('constants').doc('purchaseMethods'), { data: sanitizeConstantsData(newPurDataDefault) })
  }
  // ATL-1216 — ONE clock read for the whole day-keyed group below.
  //
  // The daily stat, its `date`, the checklist and ITS `date` were four independent `new Date()`
  // calls, and three of them feed DOCUMENT IDS that have to agree: `updateChecklist` addresses the
  // daily stat by stripping `-seller` off the checklist's id, so if the two ids name different days
  // the first ticked checkbox runs `batch.update` against a `dailyStats` document that does not
  // exist and the whole batch fails — `markChecked` neither awaits nor catches it, so the checklist
  // is simply un-tickable. A store created across a local-midnight tick was enough.
  //
  // ⚠️ This makes the group internally consistent; it does NOT make it agree with the reader.
  // `SellerChecklistScreen` derives the id from `getLocalStoreDate(store)` — the STORE's timezone —
  // while this mints it from the DEVICE's local date. For a store in another timezone that window
  // is hours wide, and the screen then looks up an id this function never minted and creates a
  // second checklist for the same business day. Pre-existing and out of ATL-1216's scope; tracked
  // as ATL-1224.
  const storeCreatedAt = new Date()
  // create dailystat for store
  const dailyStatID = format(storeCreatedAt, "d-M-y") + '-' + newStoreRef.id
  batch.set(db.collection('dailyStats').doc(dailyStatID), {
    store: newStoreRef.id,
    date: storeCreatedAt,
    organizationId: db.id, 
  })
  // create checklist for store
  // `checklistContent` is hoisted too, so the Supabase mirror below sends THE SAME values this
  // batch writes rather than a second copy of the literal that can drift.
  const checklistId = dailyStatID + '-seller'
  const checklistRef = db.collection('checklists').doc(checklistId)
  const checklistContent = [{
    type: 'header',
    text: i18n.t('checklist.cleaning')
  }, {
    type: 'task',
    text: i18n.t('checklist.wipeTheWindows'),
    done: false,
  }, {
    type: 'task',
    text: i18n.t('checklist.washTheFloor'),
    done: false,
  }, {
    type: 'header',
    text: i18n.t('checklist.shift')
  }, {
    type: 'task',
    text: i18n.t('checklist.openShift'),
    done: false,
  }]
  batch.set(checklistRef, {
    date: storeCreatedAt,
    store: newStoreRef.id,
    organizationId: db.id,
    content: checklistContent,
  })

  // update sale settings for the new store
  let newStoreSaleSettings: Record<string, unknown> | null = null
  if (constants.saleSettings?.data) {
    const newSaleSettingsData = { ...constants.saleSettings.data }
    if (copySettingsFrom && constants.saleSettings.data[copySettingsFrom.id]) {
      // copy settings from existing store
      newSaleSettingsData[newStoreRef.id] = { ...constants.saleSettings.data[copySettingsFrom.id] }
    } else {
      // use default settings
      newSaleSettingsData[newStoreRef.id] = {
        deliveryPrice: 0,
        deliveryPaidByCustomer: true,
        deliveryIncludeInReceipt: true,
        deliveryPaymentSourceWallet: null,
        defaultDeliverySpendingCategory: null,
        showSaveCartForLater: true,
        allowMultipleProductSelection: true,
        dateFieldVisible: false,
        storeFieldVisible: false,
        tableNumberFieldVisible: false,
        commentFieldVisible: false,
        showTotalPurchasePriceInCart: false,
        showPurchasePriceInEditCartItem: false,
        mergeSimilarItems: true
      }
    }
    newStoreSaleSettings = newSaleSettingsData[newStoreRef.id]
    batch.update(db.collection('constants').doc('saleSettings'), { data: newSaleSettingsData })
  }

  await batch.commit()

  if (copySettingsFrom) {
    // ATL-774: seed the NEW store's key on every product with FIELD-PATH updates, never a
    // whole-map overwrite. The old shape re-asserted `{ ...element.activeInStore }` etc. from
    // the caller's in-memory snapshot — a stale snapshot regressed OTHER stores' values in
    // Firestore with no Supabase mirror to carry the same stale absolute across (the exact
    // lost-update class ATL-762 fixed on updateProduct, except here the two sides part
    // company). A field-path update touches ONLY the new store's key, which makes the missing
    // Supabase mirror provably harmless: the new pair's value is always 0, and Supabase
    // represents "no stock" as an absent product_store_stock row that every consumer reads
    // through || 0 / COALESCE — semantically identical (asserted by the parity suite, which
    // fails if this path ever writes a non-zero value).
    //
    // Chunked into WriteBatches and AWAITED (the old code fired one unhandled direct
    // `ref.update` per product inside a .then — unbounded, unordered, and a rejection was
    // silently dropped). A failure here must not roll back the already-committed store, so
    // it is reported loudly instead of thrown.
    // Fault isolation is per CHUNK (review concern): a WriteBatch is atomic, so one
    // stale/deleted product doc fails its whole chunk — but it must not abort the REMAINING
    // chunks (the old unawaited shape failed per-doc but silently; this fails per-chunk and
    // loudly). Failed chunks are reported once at the end with the affected range sizes; the
    // missing new-store keys read as inactive/0 and are recoverable by re-running the copy.
    const BACKFILL_BATCH_LIMIT = 400
    const failedChunks: Array<{ startIndex: number; size: number; error: string }> = []
    for (let i = 0; i < allFlatProducts.length; i += BACKFILL_BATCH_LIMIT) {
      const chunk = allFlatProducts.slice(i, i + BACKFILL_BATCH_LIMIT)
      try {
        const backfillBatch = rootDb.batch()
        for (const element of chunk) {
          const productFirestoreRef = getFirestoreRef(element, 'products')
          backfillBatch.update(productFirestoreRef, {
            [`activeInStore.${newStoreRef.id}`]: element.activeInStore?.[copySettingsFrom.id] ?? false,
            [`salePrices.${newStoreRef.id}`]: element.salePrices?.[copySettingsFrom.id] ?? null,
            [`storeStock.${newStoreRef.id}`]: 0,
          })
        }
        await backfillBatch.commit()
      } catch (error) {
        failedChunks.push({ startIndex: i, size: chunk.length, error: (error as Error)?.message })
      }
    }
    if (failedChunks.length > 0) {
      console.error(
        '[saveNewStore] per-product new-store backfill partially failed (store itself is committed)',
        JSON.stringify({ storeId: newStoreRef.id, failedChunks }),
      )
      Sentry.captureMessage('saveNewStore: product backfill chunks failed', {
        level: 'error',
        tags: { save_new_store_backfill_failed: true },
        fingerprint: ['save-new-store-product-backfill-failed'],
        contexts: {
          save_new_store: {
            storeId: newStoreRef.id,
            products: allFlatProducts.length,
            failedChunks: failedChunks.slice(0, 10),
          },
        },
      })
    }
  }

  // Dual write to Supabase via NestJS backend - pass through, let backend handle
  const createdStore = await bestEffortDualWrite('stores.create', () => storesApi.createStore(
    filterUndefined({
      ...info,
      firestoreId: newStoreRef.id,
    }) as Parameters<typeof storesApi.createStore>[0]
  ));

  // ATL-790 regression (2026-11-02): the seed is issued IMMEDIATELY after the store create, ahead of
  // the wallets, the methods, the sale settings and the checklist. It depends on the Supabase store
  // row and on nothing else in that chain. It used to run last, and was therefore made durable only
  // when its turn came: Дерсу's store `1HU6Mvcrsh6N71OmF22D` (2026-10-28, a bundle that had this
  // seed) got `stores.create` and both wallets live, its method updates later through the outbox, and
  // the sale settings, the checklist and this seed never. Nothing reported it, because nothing had
  // failed: the chain stalled before the seed was attempted, and the catalog's copied prices existed
  // in Firestore only. ⚠️ This shortens the window to one request after the commit; it does not close
  // it. A continuation that dies between the commit and this call still loses the seed — that is the
  // generic Firestore-mode window every writer has, closed by ATL-1459's mirror intent (dark).
  //
  // ATL-790: mirror the per-product Firestore backfill above. The copied salePrices are REAL
  // prices — unlike the zero-only storeStock seed (omission-equivalent: an absent
  // product_store_stock row reads as 0), a price Supabase lacks simply reads absent at the
  // Phase-5 cutover. ONE bulk call for the whole catalog (the backend RPC merges only the new
  // store's active_in_store key — the same field-path discipline as the Firestore batch above).
  // Values are coerced (comma-aware price, Boolean visibility): the DTO validates and the client
  // re-validates with Zod, so one raw legacy value must not fail the WHOLE call. An
  // uncoercible/absent price omits salePrice (= source store had none). storeStock is
  // deliberately NOT sent — the zero seed stays omission-equivalent (asserted by the parity
  // suite).
  //
  // Runs OUTSIDE the createdStore gate, unlike the wallet/method mirrors: when stores.create
  // failed transiently its outbox entry will replay and heal, and the seed must not be lost in
  // the meantime. A LIVE seed call would 400 against the missing store row (a deterministic
  // drop), so when createdStore is null the seed is queued directly instead — the synthetic
  // transient error below carries the full request, and because outbox replay is ordered per
  // firestoreId group (both entries key on the new store's id), the seed replays strictly AFTER
  // the store create that it needs. If the store create never lands, the seed replay 400s and
  // drops — correctly, there is nothing to seed against.
  if (copySettingsFrom) {
    const seedItems = allFlatProducts.map((element: any) => {
      const price = parseDecimalValue(element.salePrices?.[copySettingsFrom.id])
      return {
        productId: getFirestoreRef(element, 'products').id,
        ...(price !== null ? { salePrice: price } : {}),
        // Boolean() coercion: legacy Firestore maps can hold 0/1/"true"; the DTO is
        // @IsBoolean() and the client re-validates with Zod, so a raw non-boolean would fail
        // the WHOLE call. Truthiness matches how every activeInStore consumer reads the map.
        active: Boolean(element.activeInStore?.[copySettingsFrom.id] ?? false),
      }
    })
    if (seedItems.length > 0) {
      const seedRequest = { storeId: newStoreRef.id, items: seedItems }
      await bestEffortDualWrite(
        'products.seedStoreSettings',
        async () => {
          if (!createdStore) {
            const queueOnly = new Error(
              'stores.create did not land; queueing store-settings seed for ordered replay',
            ) as Error & { requestConfig?: unknown }
            queueOnly.requestConfig = {
              method: 'POST',
              url: '/products/store-settings/seed',
              data: seedRequest,
            }
            throw queueOnly
          }
          const response = await productsApi.seedStoreProductSettings(seedRequest)
          const failedItems = (response?.results ?? []).filter((r) => !r.ok)
          if (failedItems.length > 0) {
            // Same contract as the bulk stock mirror: a 200 with failed items would purge the
            // queued retry those items still need, so re-queue ONLY the failed subset.
            console.error(
              '[saveNewStore] Supabase store-settings seed partially failed',
              JSON.stringify({ storeId: newStoreRef.id, failed: failedItems.slice(0, 10) }),
            )
            Sentry.captureMessage('saveNewStore: store-settings seed items failed', {
              level: 'warning',
              tags: {
                supabase_dual_write_error: true,
                dual_write: 'products.seedStoreSettings',
                dual_write_module: 'products',
              },
              fingerprint: ['save-new-store-settings-seed-item-failed'],
              contexts: {
                save_new_store: {
                  storeId: newStoreRef.id,
                  items: seedItems.length,
                  failed: failedItems.length,
                  sample: failedItems.slice(0, 10),
                },
              },
            })
            const failedIds = new Set(failedItems.map((r) => r.productId))
            const retry = new Error(
              `store-settings seed: ${failedItems.length}/${seedItems.length} item(s) failed`,
            ) as Error & { requestConfig?: unknown }
            retry.requestConfig = {
              method: 'POST',
              url: '/products/store-settings/seed',
              data: {
                storeId: newStoreRef.id,
                items: seedItems.filter((i) => failedIds.has(i.productId)),
              },
            }
            throw retry
          }
          return response
        },
        { firestoreId: newStoreRef.id },
      )
    }
  }


  // ATL-750: creating a store is NOT only a `stores` row. The Firestore batch above also minted the
  // store's two cash wallets and rewrote paymentMethods / purchaseMethods / saleSettings — and none
  // of that was ever sent to Supabase, so a store added after its org was migrated had NO cash
  // destination wallet there: money with nowhere to attribute the moment reads flip to Supabase.
  // Measured before this fix: 9 orgs, 35 wallets missing, 30 of them exactly a `-cash` /
  // `-extracted-cash` pair for a store created post-migration.
  //
  // ORDER IS LOAD-BEARING: the wallets carry `storeId`, and the backend resolves it to the store's
  // UUID — it must exist first. `bestEffortDualWrite` returns null both when dual-write is gated off
  // and when the call failed; either way there is no Supabase store to attach any of this to, so we
  // stop rather than emit a cascade of writes that cannot succeed. One clear signal (the
  // `stores.create` failure) instead of five confusing ones.
  if (createdStore) {
    // The two wallets, in the array order they were appended in above (index == displayOrder,
    // the same convention saveWallets uses).
    const newWallets = [cashWalletInfo, extractedCashWalletInfo]
    for (let i = 0; i < newWallets.length; i++) {
      const wallet = newWallets[i]
      await bestEffortDualWrite('wallets.create', () => walletsApi.createWallet({
        ...wallet,
        firestoreId: wallet.id,
        displayOrder: walletsData.length + i,
      }), { firestoreId: wallet.id })
    }

    // Payment methods: the new store's `activeInStore` flag and — for cash — the
    // `destinationWalletForStore` entry pointing at the wallet just created.
    for (const { element, index } of mutatedPaymentMethods) {
      await bestEffortDualWrite('paymentMethods.update', () => paymentMethodsApi.updatePaymentMethod(element.id, {
        ...element,
        displayOrder: index,
      }), { firestoreId: element.id })
    }

    // Purchase methods: the new store's `activeInStore` flag.
    for (const { element, index } of mutatedPurchaseMethods) {
      await bestEffortDualWrite('purchaseMethods.update', () => purchaseMethodsApi.updatePurchaseMethod(element.id, {
        ...element,
        displayOrder: index,
      }), { firestoreId: element.id })
    }

    // Sale settings for the new store (backend upserts if no row exists).
    if (newStoreSaleSettings) {
      await bestEffortDualWrite('saleSettings.update', () => saleSettingsApi.updateSaleSettings(
        newStoreRef.id,
        newStoreSaleSettings as Parameters<typeof saleSettingsApi.updateSaleSettings>[1],
      ), { firestoreId: newStoreRef.id })
    }

    // ATL-1216: the seller checklist minted in the same batch. ATL-750 mirrored the wallets, the
    // methods and the sale settings and missed this one, so every store created after its org was
    // migrated left a Firestore checklist with no Supabase counterpart — measured 2026-09-06 as 7
    // documents across 6 orgs, each created within seconds of its store row.
    //
    // It is worth stating why nothing caught it for a year: this write was never ATTEMPTED, so
    // there was no failure to report, nothing for `bestEffortDualWrite` to enqueue and no Sentry
    // event. The only symptom is an id-level `setdiff` gap — which is a BLOCKING clause on the
    // read cutover, so the cost of the omission is that the org cannot be flipped at all.
    //
    // Inside the `createdStore` gate for the same reason the wallets are: the checklist carries
    // `storeId`, which the backend resolves to the store's UUID.
    //
    // ⚠️ RESIDUAL, named rather than half-fixed: `stores.create` is replay-safe, so when it does
    // NOT land it is remembered (uninformed policy skip) or queued (transient failure) and can
    // land later — while everything under this gate was never attempted and is not queued. That
    // reopens this exact gap. It is ATL-750's gate, not this call's: the wallets, the payment and
    // purchase methods and the sale settings all share it, and the money-carrying ones matter more
    // than the checklist. Fixing it here alone would mirror a checklist into a store that has no
    // cash wallet. Tracked as ATL-1222; not the mechanism behind any measured instance — in all 7
    // documents found on prod the store row was created 1.4-3.6 s after the checklist, i.e. by a
    // LIVE `stores.create`, which this gate admits. That samples the live path ONLY, so it says
    // nothing about how often the queued path fires — unmeasured, not rare.
    //
    // (The ATL-790 seed above queues itself instead. That works because the outbox groups by the
    // request's derived document id and both entries derive the STORE's id, so the seed replays
    // after the create it needs. A checklist request derives the CHECKLIST's id, so the same trick
    // would put it in its own group with no ordering guarantee against the store create.)
    await bestEffortDualWrite('checklists.create', () => checklistsApi.createChecklist({
      firestoreId: checklistId,
      storeId: newStoreRef.id,
      checklistType: 'seller',
      content: checklistContent,
      date: storeCreatedAt.toISOString(),
    }), { firestoreId: checklistId })

    // (ATL-790 seed: issued above, before this gate — it must run even when createdStore is null.)
  }

  return newStoreRef;
}

/**
 * Sanitize a value for Firestore:
 * - Convert mock Timestamps (objects with toDate method) to Date objects
 * - Strip undefined values from objects
 * - Skip Firestore DocumentReference objects (they can't be serialized as plain objects)
 * - Recursively sanitize nested objects
 */
export const sanitizeForFirestoreDeep = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;

  // Check if it's a mock Timestamp (has toDate function but NOT a DocumentReference)
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as any).toDate === 'function') {
    // DocumentReferences also have toDate on their parent property, so check if it's a Timestamp specifically
    // Timestamps have seconds and nanoseconds properties
    if ('seconds' in value || 'nanoseconds' in value || (value as any)._seconds !== undefined) {
      return (value as any).toDate();
    }
  }

  // Skip Firestore DocumentReference objects - they have firestore and path properties
  // and their constructor name contains "Reference"
  if (typeof value === 'object' && value !== null) {
    const constructorName = (value as any).constructor?.name || '';
    if (constructorName.includes('Reference') || constructorName.includes('DocumentReference') ||
        ('firestore' in value && 'path' in value && typeof (value as any).path === 'string')) {
      return value; // Return as-is, don't try to serialize
    }
  }

  // Handle Date objects - preserve them for Firestore to serialize
  // Date objects have no enumerable properties, so Object.entries(date) returns []
  // which would incorrectly convert them to {} without this check
  if (value instanceof Date) {
    return value;
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map(item => sanitizeForFirestoreDeep(item));
  }

  // Handle plain objects
  if (typeof value === 'object' && value !== null) {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      // Skip 'ref' properties (DocumentReferences can't be serialized as nested values)
      if (key === 'ref') continue;
      // Skip function-valued fields — Firestore rejects them ('Unsupported field value:
      // a function'). Some product objects (e.g. supplier-dict items) carry such fields.
      if (typeof val === 'function') continue;
      if (val !== undefined) {
        result[key] = sanitizeForFirestoreDeep(val);
      }
    }
    return result;
  }

  return value;
};

/** Sanitize constants array for Firestore: strip undefined values and convert mock Timestamps */
const sanitizeConstantsData = (data: any[]): any[] =>
  data.map(item => sanitizeForFirestoreDeep(item))

export const saveWallets = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('wallets'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('wallets.create', () => walletsApi.createWallet({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('wallets.update', () => walletsApi.updateWallet(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteWallet = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('wallets'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('wallets.delete', () => walletsApi.deleteWallet(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('wallets') });
}

export const savePaymentMethods = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('paymentMethods'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('paymentMethods.create', () => paymentMethodsApi.createPaymentMethod({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    } as any));
  } else {
    await bestEffortDualWrite('paymentMethods.update', () => paymentMethodsApi.updatePaymentMethod(info.id, {
      ...info,
      displayOrder: index,
    } as any));
  }
}

export const deletePaymentMethod = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('paymentMethods'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('paymentMethods.delete', () => paymentMethodsApi.deletePaymentMethod(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('paymentMethods') });
}

export const savePurchaseMethods = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('purchaseMethods'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('purchaseMethods.create', () => purchaseMethodsApi.createPurchaseMethod({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('purchaseMethods.update', () => purchaseMethodsApi.updatePurchaseMethod(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const saveSaleSettings = async (storeId: string, settings: any) => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const batch = rootDb.batch()
  const saleSettingsRef = db.collection('constants').doc('saleSettings')
  batch.set(saleSettingsRef, { data: { [storeId]: settings } }, { merge: true })
  await batch.commit()

  // Dual-write to Supabase via API (backend upserts if no row exists)
  await bestEffortDualWrite('saleSettings.update', () => saleSettingsApi.updateSaleSettings(storeId, settings))
}

export const deletePurchaseMethod = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('purchaseMethods'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('purchaseMethods.delete', () => purchaseMethodsApi.deletePurchaseMethod(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('purchaseMethods') });
}

export const saveProductModifiers = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveProductModifiers')
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('productModifiers'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('productModifiers.create', () => productModifiersApi.createProductModifier({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('productModifiers.update', () => productModifiersApi.updateProductModifier(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteProductModifiers = async (db: FirestoreDb, index: number, allData: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteProductModifiers')
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('productModifiers'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('productModifiers.delete', () => productModifiersApi.deleteProductModifier(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('productModifiers') });
}

export const saveRoles = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('roles'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    // ATL-1890: the array position IS the order (as every other constants writer sends it). Roles
    // sent none, so every app-created role landed with display_order NULL and read in an order the
    // merchant never chose.
    await bestEffortDualWrite('roles.create', () => rolesApi.createRole({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    } as any));
  } else {
    await bestEffortDualWrite('roles.update', () => rolesApi.updateRole(info.id, {
      ...info,
      displayOrder: index,
    } as any));
  }
}

export const deleteRole = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('roles'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('roles.delete', () => rolesApi.deleteRole(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('roles') });
}

export const saveProductUnits = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.set(db.collection('constants').doc('productUnits'), { data: sanitizeConstantsData(updatedData) }, { merge: true })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('productUnits.create', () => productUnitsApi.createProductUnit({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('productUnits.update', () => productUnitsApi.updateProductUnit(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteProductUnit = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('productUnits'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('productUnits.delete', () => productUnitsApi.deleteProductUnit(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('productUnits') });
}

export const saveSpendingCategories = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('spendingCategories'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('spendingCategories.create', () => spendingCategoriesApi.createSpendingCategory({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('spendingCategories.update', () => spendingCategoriesApi.updateSpendingCategory(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteSpendingCategory = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('spendingCategories'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('spendingCategories.delete', () => spendingCategoriesApi.deleteSpendingCategory(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('spendingCategories') });
}

export const saveDiscountOptions = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('discountOptions'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('discountOptions.create', () => discountOptionsApi.createDiscountOption({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('discountOptions.update', () => discountOptionsApi.updateDiscountOption(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteDiscountOption = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('discountOptions'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('discountOptions.delete', () => discountOptionsApi.deleteDiscountOption(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('discountOptions') });
}

export const saveWriteOffReasons = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('writeOffReasons'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('writeOffReasons.create', () => writeOffReasonsApi.createWriteOffReason({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('writeOffReasons.update', () => writeOffReasonsApi.updateWriteOffReason(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteWriteOffReason = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('writeOffReasons'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('writeOffReasons.delete', () => writeOffReasonsApi.deleteWriteOffReason(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('writeOffReasons') });
}

export const saveReturnReasons = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('returnReasons'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  if (isNew) {
    await bestEffortDualWrite('returnReasons.create', () => returnReasonsApi.createReturnReason({
      ...info,
      firestoreId: info.id,
      displayOrder: index,
    }));
  } else {
    await bestEffortDualWrite('returnReasons.update', () => returnReasonsApi.updateReturnReason(info.id, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteReturnReason = async (db: FirestoreDb, index: number, allData: any[]) => {
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('returnReasons'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('returnReasons.delete', () => returnReasonsApi.deleteReturnReason(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('returnReasons') });
}

export const saveRecurringSpendings = async (db: FirestoreDb, info: any, index: number, allData: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveRecurringSpendings')
  const batch = rootDb.batch()
  const updatedData = [...allData]
  const isNew = index >= allData.length
  updatedData[index] = info
  batch.update(db.collection('constants').doc('recurringSpendings'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  // Use id, firestoreId, or supabaseId - whichever is available
  const itemId = info.id || info.firestoreId || info.supabaseId;
  if (isNew) {
    await bestEffortDualWrite('recurringSpendings.create', () => recurringSpendingsApi.createRecurringSpending({
      ...info,
      firestoreId: itemId,
      displayOrder: index,
    }));
  } else {
    if (!itemId) {
      console.warn('No ID found for recurring spending update, skipping Supabase write');
      return;
    }
    await bestEffortDualWrite('recurringSpendings.update', () => recurringSpendingsApi.updateRecurringSpending(itemId, {
      ...info,
      displayOrder: index,
    }));
  }
}

export const deleteRecurringSpending = async (db: FirestoreDb, index: number, allData: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteRecurringSpending')
  const batch = rootDb.batch()
  const deletedItem = allData[index]
  const updatedData = [...allData]
  updatedData.splice(index, 1)
  batch.update(db.collection('constants').doc('recurringSpendings'), { data: sanitizeConstantsData(updatedData) })
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('recurringSpendings.delete', () => recurringSpendingsApi.deleteRecurringSpending(deletedItem.id), { firestoreId: deletedItem.id, deletedFrom: constantsItemLocation('recurringSpendings') });
}

export const checkIdUniquenes = (id: string, constants: any) => {
  let check = true
  const thingsToCheck = ['paymentMethods', 'wallets', 'purchaseMethods']
  thingsToCheck.forEach(el => {
    const allData = constants[el].data
    for (let index = 0; index < allData.length; index++) {
      const element = allData[index]
      if (element.id === id) {
        check = false
      }
    }
  })

  return check
}

export const updateLoyaltySettings = async (db: FirestoreDb, info: any) => {
  await db.collection('constants').doc('loyaltySettings').update(info)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('settings.updateLoyaltySettings', () => settingsApi.updateLoyaltySettings(info as any));
}

export const updateMarketplaceSettings = async (db: any, info: any, enabledCategories: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateMarketplaceSettings')
  const batch = rootDb.batch()
  batch.update(db, info)
  for (const [categoryId, enabled] of Object.entries(enabledCategories)) {
    batch.update(db.collection('productCategories').doc(categoryId), {
      marketplaceDisabled: !enabled
    })
  }
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('settings.updateMarketplaceSettings', () => settingsApi.updateMarketplaceSettings({
    ...info,
    enabledCategories: enabledCategories,
  } as any));
}

export const saveNewMarketingList = async (db: FirestoreDb, info: any) => {
  reportFirestoreOnlyWriterUsage('saveNewMarketingList', db?.id)
  await db.collection('marketingLists').add(info)
}

export const getTransactionsOnDateRange = async (db: FirestoreDb, range: DateRange, flat = false, store: Store | null = null) => {
  if (shouldUseSupabase()) {
    try {
      const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
      const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))

      const params: Record<string, unknown> = {
        type: 'expense',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }

      if (store) {
        params.storeId = store
      }

      // Using typed API - returns TransactionFirestoreResponse[] with camelCase data.
      // ATL-1477: every transaction in the range; `limit: 1000` silently cut off the rest.
      const transactions = await readWholeList(
        (window) => transactionsApi.getTransactionsWindow({ ...params, ...window }),
        { label: 'transactions', order: newestFirst('date') },
      ) as unknown as Transaction[]

      if (flat) {
        return transactions
      } else {
        const groupedTxns: Record<string, Transaction[]> = {} // date key - array of transactions
        transactions.forEach(function (transaction) {
          const txDateField = transaction.date as { toDate?: () => Date } | string | Date | undefined
          const transactionDate = txDateField && typeof txDateField === 'object' && 'toDate' in txDateField && txDateField.toDate ? txDateField.toDate() : new Date((txDateField || new Date()) as string | Date)
          const date = format(transactionDate, 'd/M/y EEEE')
          const newArr = groupedTxns[date] ? [...groupedTxns[date]] : []
          newArr.push(transaction)
          groupedTxns[date] = [...newArr]
        })
        const docsArr: GroupedData<Transaction>[] = []
        for (const key in groupedTxns) {
          const newData = {
            title: key,
            data: groupedTxns[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching transactions from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'transactions', function: 'getTransactionsOnDateRange' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
  const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))
  let query = db.collection('transactions').where('type', '==', 'expense') as FirestoreQuery<Transaction>
  if (store) {
    query = query.where('store', '==', store)
  }
  const querySnapshot = await query.where('date', '>=', startDate)
    .where('date', '<=', endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatTxns: Transaction[] = []
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Transaction
      snap.id = doc.id
      snap.ref = doc.ref
      flatTxns.push(snap)
    })
    return flatTxns
  } else {
    const groupedTxnsFS: Record<string, Transaction[]> = {} // date key - array of transactions
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Transaction
      snap.id = doc.id
      snap.ref = doc.ref
      const snapDate = snap.date as { toDate: () => Date }
      const date = format(snapDate.toDate(), 'd/M/y EEEE')
      const newArr = groupedTxnsFS[date] ? [...groupedTxnsFS[date]] : []
      newArr.push(snap)
      groupedTxnsFS[date] = [...newArr]
    })
    const resultTxnsArr: GroupedData<Transaction>[] = []
    for (const key in groupedTxnsFS) {
      const newData = {
        title: key,
        data: groupedTxnsFS[key]
      }
      resultTxnsArr.push(newData)
    }
    return resultTxnsArr
  }
}

export const getComplexSaleTransactionsOnDateRange = async (range: DateRange, flat = false, store: Store | null = null) => {
  if (shouldUseSupabase()) {
    try {
      const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
      const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))

      const params: Record<string, unknown> = {
        type: 'income',
        linkedDocType: 'complexSale',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }

      if (store) {
        params.storeId = store
      }

      // Using typed API - returns TransactionFirestoreResponse[] with camelCase data.
      // ATL-1477: every transaction in the range; `limit: 1000` silently cut off the rest.
      const transactions = await readWholeList(
        (window) => transactionsApi.getTransactionsWindow({ ...params, ...window }),
        { label: 'transactions', order: newestFirst('date') },
      ) as unknown as Transaction[]

      if (flat) {
        return transactions
      } else {
        const groupedComplexTxns: Record<string, Transaction[]> = {} // date key - array of transactions
        transactions.forEach(function (transaction) {
          const txDateField = transaction.date as { toDate?: () => Date } | string | Date | undefined
          const transactionDate = txDateField && typeof txDateField === 'object' && 'toDate' in txDateField && txDateField.toDate ? txDateField.toDate() : new Date((txDateField || new Date()) as string | Date)
          const date = format(transactionDate, 'd/M/y EEEE')
          const newArr = groupedComplexTxns[date] ? [...groupedComplexTxns[date]] : []
          newArr.push(transaction)
          groupedComplexTxns[date] = [...newArr]
        })
        const docsArr: GroupedData<Transaction>[] = []
        for (const key in groupedComplexTxns) {
          const newData = {
            title: key,
            data: groupedComplexTxns[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching complex sale transactions from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'transactions', function: 'getComplexSaleTransactionsOnDateRange' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom) as FirestoreDb
  const startDate = new Date(range.startDate.setHours(0, 0, 0, 0))
  const endDate = new Date(range.endDate.setHours(23, 59, 59, 999))
  let query = db.collection('transactions').where('type', '==', 'income').where('linkedDocType', '==', 'complexSale') as FirestoreQuery<Transaction>
  if (store) {
    query = query.where('store', '==', store)
  }
  const querySnapshot = await query.where('date', '>=', startDate)
    .where('date', '<=', endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatComplexTxns: Transaction[] = []
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Transaction
      snap.id = doc.id
      snap.ref = doc.ref
      flatComplexTxns.push(snap)
    })
    return flatComplexTxns
  } else {
    const groupedComplexTxnsFS: Record<string, Transaction[]> = {} // date key - array of transactions
    querySnapshot.forEach(function (doc) {
      const snap = doc.data() as Transaction
      snap.id = doc.id
      snap.ref = doc.ref
      const snapDate = snap.date as { toDate: () => Date }
      const date = format(snapDate.toDate(), 'd/M/y EEEE')
      const newArr = groupedComplexTxnsFS[date] ? [...groupedComplexTxnsFS[date]] : []
      newArr.push(snap)
      groupedComplexTxnsFS[date] = [...newArr]
    })
    const resultComplexTxnsArr: GroupedData<Transaction>[] = []
    for (const key in groupedComplexTxnsFS) {
      const newData = {
        title: key,
        data: groupedComplexTxnsFS[key]
      }
      resultComplexTxnsArr.push(newData)
    }
    return resultComplexTxnsArr
  }
}

export const saveNewTransaction = async (db: FirestoreDb, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewTransaction')
  if (info.linkedDocType === 'workingHour') {
    return await saveNewWorkingHourPaymentTransaction(info as any)
  }
  const batch = rootDb.batch()
  const newRef = db.collection('transactions').doc()
  // Sanitize externalDestination: dropdown objects contain non-serializable fields (date.toDate, ref, etc.)
  const sanitizedExtDest = info.externalDestination ? {
    id: info.externalDestination.id,
    text: info.externalDestination.text || info.externalDestination.name,
    name: info.externalDestination.name || info.externalDestination.text,
  } : null
  // Deep-sanitize before write: a free-text recurringSpending/spending destination has no `id`, so
  // sanitizedExtDest.id is undefined — and Firestore (ignoreUndefinedProperties is unset ⇒ false)
  // throws "Unsupported field value: undefined (found in field externalDestination.id)", surfacing the
  // generic save error to the user. sanitizeForFirestoreDeep strips nested undefined/functions/refs.
  const docData = sanitizeForFirestoreDeep({
    ...info,
    externalDestination: sanitizedExtDest,
    createdDate: new Date(),
  }) as Record<string, unknown>
  batch.set(newRef, docData)
  if (info.linkedDocType === 'supply') {
    // mark supplies paid
    const externalDest = info.externalDestination as { id: string }
    const localDateRange = dateRangeWithTimezoneAndAutoclose(info.paymentRange as DateRange, [info.store as string])
    const supplies = await db.collection('supplies').where('supplierID', '==', externalDest.id).where('supplyDate', '>=', localDateRange.startDate).where('supplyDate', '<=', localDateRange.endDate).where('isPaid', '==', false).get()
    supplies.forEach(s => {
      batch.update(s.ref, {
        isPaid: true,
        transactionId: newRef.id,
        paymentMethod: info.sourceWallet,
        paymentDate: info.date,
        paidBy: info.paidBy,
      })
    })
  }
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  const sourceWallet = info.sourceWallet as { id?: string } | string | undefined
  const destWallet = info.destinationWallet as { id?: string } | string | undefined
  await bestEffortDualWrite('transactions.create', () => transactionsApi.createTransaction({
    firestoreId: newRef.id,
    // ATL-540: never send undefined-cast-as-string — org is resolved server-side by the guard
    organizationId: (info.organizationId as string) || undefined,
    storeId: info.store || undefined,
    type: (info.type || 'income') as 'income' | 'expense' | 'transfer',
    value: info.value as number,
    sourceWallet: (typeof sourceWallet === 'object' ? sourceWallet?.id : sourceWallet) || undefined,
    destinationWallet: (typeof destWallet === 'object' ? destWallet?.id : destWallet) || undefined,
    linkedDocId: info.linkedDocId || undefined,
    linkedDocType: info.linkedDocType || undefined,
    externalDestination: sanitizedExtDest || undefined,
    paymentRange: info.paymentRange || undefined,
    paidBy: info.paidBy || undefined,
    dailyStatId: info.dailyStatID || undefined,
    monthlyStatId: info.monthlyStatID || undefined,
    // ATL-1781: `??`, not `||` — an empty note is the device's value, as on every other path
    details: info.details ?? undefined,
    date: convertToDate(info.date as any)?.toISOString() || new Date().toISOString(),
  }));

  return newRef
}

export const deleteTransaction = async (db: FirestoreDb, ref: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteTransaction')
  if (info.linkedDocType === 'workingHour') {
    return await deleteWorkingHourPaymentTransaction(ref, info)
  }
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'transactions')
  batch.delete(firestoreRef)
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('transactions.delete', () => transactionsApi.deleteTransaction(ref.id), { firestoreId: ref.id });
}

/**
 * Add a payment to a complex sale
 * @param {Object} db - Firestore database instance
 * @param {Object} complexSaleRef - Reference to the complex sale document
 * @param {Object} payment - Payment info object
 * @param {Array} existingPayments - Existing payments array
 * @param {string} store - Store ID
 * @param {string} organizationId - Organization ID
 * @returns {Object} - { transactionId, payment }
 */
export const addComplexSalePayment = async (
  db: FirestoreDb,
  complexSaleRef: FirestoreRef,
  payment: { paymentMethod: PaymentMethod; amount: number; date: Date; description?: string; createdBy?: string },
  existingPayments: Record<string, unknown>[],
  store: string,
  organizationId: string
) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#addComplexSalePayment')
  const batch = rootDb.batch()
  // Resolve to a real Firestore ref — accepts a whole item or a ref (ATL-548 pattern)
  const resolvedSaleRef = getFirestoreRef(complexSaleRef, 'complexSales')

  // Create transaction document
  const walletId = payment.paymentMethod?.destinationWalletForStore?.[store]?.id
  const transactionInfo = {
    linkedDocId: resolvedSaleRef.id,
    linkedDocType: 'complexSale',
    type: 'income',
    destinationWallet: walletId || '',
    value: payment.amount,
    date: payment.date,
    dailyStatID: getDailyStatID(store, payment.date),
    details: payment.description || 'Complex sale payment',
    paidBy: payment.createdBy,
    store: store,
    organizationId: organizationId,
    createdDate: new Date()
  }

  const transactionRef = db.collection('transactions').doc()
  batch.set(transactionRef, transactionInfo)

  // Update payment with transaction ID
  const paymentWithTransaction = { ...payment, transactionId: transactionRef.id }

  // Update complex sale with new payment
  const updatedPayments = [...existingPayments, paymentWithTransaction]
  batch.update(resolvedSaleRef, { payments: updatedPayments })

  // Update daily stats
  const dailyStatID = getDailyStatID(store, payment.date) || ''
  const dailyStatRef = db.collection('dailyStats').doc(dailyStatID)

  const pmId = payment.paymentMethod.id

  const saleStatFields: Record<string, unknown> = {}
  saleStatFields[pmId + 'SalesAmountPaid'] = fieldValue.increment(payment.amount)
  saleStatFields[pmId + 'SalesQuantityPaid'] = fieldValue.increment(1)
  if (walletId) {
    saleStatFields[walletId + 'SalesAmountPaid'] = fieldValue.increment(payment.amount)
    saleStatFields[walletId + 'SalesQuantityPaid'] = fieldValue.increment(1)
  }

  // Bank percent / commission
  if (payment.paymentMethod.comission && payment.paymentMethod.comission > 0) {
    saleStatFields['bankPercent'] = fieldValue.increment((payment.amount || 0) * payment.paymentMethod.comission / 100)
  }

  batch.set(dailyStatRef, saleStatFields, { merge: true })

  await commitGuardedCreate('complexSales.payment.create', db, transactionRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('transactions.create', () => transactionsApi.createTransaction({
    firestoreId: transactionRef.id,
    // ATL-540: omit rather than send '' — org is resolved server-side by the guard
    organizationId: db.id || undefined,
    storeId: store,
    type: 'income',
    value: payment.amount,
    sourceWallet: walletId || '',
    destinationWallet: walletId,
    linkedDocId: resolvedSaleRef.id,
    linkedDocType: 'complexSale',
    paidBy: payment.createdBy,
    // ATL-1781: the payment note the Firestore document carries
    details: transactionInfo.details,
    date: payment.date instanceof Date ? payment.date.toISOString() : payment.date,
  } as any));

  // Update complex sale payments in Supabase
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(resolvedSaleRef.id, {
    payments: updatedPayments as Record<string, unknown>[],
  }));

  return { transactionId: transactionRef.id, payment: paymentWithTransaction }
}

/**
 * Delete a payment from a complex sale
 * @param {Object} db - Firestore database instance
 * @param {Object} complexSaleRef - Reference to the complex sale document
 * @param {Object} payment - Payment to delete
 * @param {Array} existingPayments - Existing payments array
 * @param {string} store - Store ID
 */
export const deleteComplexSalePayment = async (
  db: FirestoreDb,
  complexSaleRef: FirestoreRef,
  payment: { paymentMethod: PaymentMethod; amount: number; date?: Date | { toDate: () => Date }; id?: string; transactionId?: string },
  existingPayments: Record<string, unknown>[],
  store: string
) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteComplexSalePayment')
  const batch = rootDb.batch()
  // Resolve to a real Firestore ref — accepts a whole item or a ref (ATL-548 pattern)
  const resolvedSaleRef = getFirestoreRef(complexSaleRef, 'complexSales')

  // Delete the associated transaction
  if (payment.transactionId) {
    const transactionRef = db.collection('transactions').doc(payment.transactionId)
    batch.delete(transactionRef)
  }

  // Update complex sale - remove payment
  const updatedPayments = existingPayments.filter(p => p.id !== payment.id)
  batch.update(resolvedSaleRef, { payments: updatedPayments })

  // Update daily stats to decrement payment stats
  const paymentDate = (payment.date as { toDate?: () => Date })?.toDate?.() || payment.date || new Date()
  const dailyStatID = getDailyStatID(store, paymentDate as Date)
  const dailyStatRef = db.collection('dailyStats').doc(dailyStatID || '')

  const pmId = payment.paymentMethod?.id
  const walletId = payment.paymentMethod?.destinationWalletForStore?.[store]?.id

  const saleStatFields: Record<string, unknown> = {}
  if (pmId) {
    saleStatFields[pmId + 'SalesAmountPaid'] = fieldValue.increment(-payment.amount)
    saleStatFields[pmId + 'SalesQuantityPaid'] = fieldValue.increment(-1)
  }
  if (walletId) {
    saleStatFields[walletId + 'SalesAmountPaid'] = fieldValue.increment(-payment.amount)
    saleStatFields[walletId + 'SalesQuantityPaid'] = fieldValue.increment(-1)
  }

  // Bank percent / commission
  if (payment.paymentMethod?.comission && payment.paymentMethod.comission > 0) {
    saleStatFields['bankPercent'] = fieldValue.increment(-((payment.amount || 0) * payment.paymentMethod.comission / 100))
  }

  batch.set(dailyStatRef, saleStatFields, { merge: true })

  await batch.commit()

  // Dual write to Supabase via NestJS backend. ATL-1188: durable — this is money movement, and
  // the previous hand-rolled catch dropped a 5xx silently, leaving Supabase holding a transaction
  // the customer's Firestore books no longer have. A 404 is still free (pre-migration txn); note
  // the old predicate matched `message.includes('404')`, which read a timeout on a URL containing
  // those digits as "already absent" and discarded the write.
  if (payment.transactionId) {
    const paymentTransactionId = payment.transactionId
    await bestEffortDualWriteDelete(
      'transactions.delete',
      () => transactionsApi.deleteTransaction(paymentTransactionId),
      { firestoreId: paymentTransactionId },
    )
  }

  // Update complex sale payments in Supabase
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(resolvedSaleRef.id, {
    payments: updatedPayments as Record<string, unknown>[],
  }));

  return updatedPayments
}

/**
 * Update complex sale status
 * @param {Object} complexSaleRef - Reference to the complex sale document
 * @param {Object} newStatus - New status object
 * @param {Array} existingUpdates - Existing updates array
 * @param {string} userId - User ID who made the update
 */
export const updateComplexSaleStatus = async (complexSaleRef: any, newStatus: any, existingUpdates: any[], userId: string) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateComplexSaleStatus')
  const operation = {
    type: 'statusUpdate',
    oldStatus: null, // Will be set by caller
    status: newStatus,
    date: new Date(),
    updatedBy: userId
  }
  const updatedData = {
    status: newStatus,
    isActive: newStatus.isActive,
    updates: [...existingUpdates, operation]
  }
  const firestoreRef = getFirestoreRef(complexSaleRef, 'complexSales')
  await firestoreRef.update(updatedData)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(firestoreRef.id, {
    status: newStatus,
    statusUpdates: [...existingUpdates, operation],
  }));

  return { updatedData, operation }
}

/**
 * Add comment to complex sale
 * @param {Object} complexSaleRef - Reference to the complex sale document
 * @param {Object} comment - Comment object
 * @param {Array} existingComments - Existing comments array
 */
export const addComplexSaleComment = async (complexSaleRef: any, comment: any, existingComments: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#addComplexSaleComment')
  const updatedData = {
    comments: [...existingComments, comment]
  }
  const firestoreRef = getFirestoreRef(complexSaleRef, 'complexSales')
  await firestoreRef.update(updatedData)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(firestoreRef.id, {
    comments: [...existingComments, comment],
  }));

  return updatedData
}

/**
 * Delete comment from complex sale
 * @param {Object} complexSaleRef - Reference to the complex sale document
 * @param {string} commentId - ID of comment to delete
 * @param {Array} existingComments - Existing comments array
 */
export const deleteComplexSaleComment = async (complexSaleRef: any, commentId: string, existingComments: any[]) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteComplexSaleComment')
  const updatedComments = existingComments.filter((c: any) => c.id !== commentId)
  const updatedData = {
    comments: updatedComments
  }
  const firestoreRef = getFirestoreRef(complexSaleRef, 'complexSales')
  await firestoreRef.update(updatedData)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(firestoreRef.id, {
    comments: updatedComments,
  }));

  return updatedComments
}

/**
 * Update user settings
 * @param {Object} userRef - Reference to the user document
 * @param {Object} info - Updated user info
 */
export const updateUser = async (userRef: any, info: any) => {
  const firestoreRef = getFirestoreRef(userRef, 'users')
  await firestoreRef.update(info)

  // Dual write to Supabase - pass through, let backend handle
  await bestEffortDualWrite('users.update', () => usersApi.updateUser(userRef.id, filterUndefined(info)));
}

/**
 * Update store settings
 * @param {Object} storeRef - Reference to the store document
 * @param {Object} info - Updated store info
 */
export const updateStore = async (storeRef: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateStore')
  // Update Firestore
  const firestoreRef = getFirestoreRef(storeRef, 'stores')
  await firestoreRef.update(info)

  // Send to Supabase API - filter undefined values before sending
  await bestEffortDualWrite('stores.update', () => storesApi.updateStore(storeRef.id, filterUndefined(info)));
}

/**
 * Delete store
 * @param {Object} storeRef - Reference to the store document
 * @param {Array} allFlatProducts - every product in the org, so the store's per-product keys go too
 */
export const deleteStore = async (storeRef: any, allFlatProducts: any[] = []) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteStore')
  const firestoreRef = getFirestoreRef(storeRef, 'stores')
  await firestoreRef.delete()

  // Delete from Supabase
  await bestEffortDualWriteDelete('stores.delete', () => storesApi.deleteStore(storeRef.id), { firestoreId: storeRef.id });

  // ATL-911 — PRUNE the store's per-product keys. Deleting the store document used to leave
  // `salePrices.<storeId>`, `activeInStore.<storeId>` and `storeStock.<storeId>` behind on every
  // product in the org: 3,748 orphaned keys across 22 orgs, 999 in the worst one.
  //
  // They are not cosmetic. The parity gate compares Firestore's salePrices keys against Supabase
  // and reports every one naming a now-absent store as a CRITICAL difference — so a single store
  // deletion buries the org's real diffs under hundreds of false ones, and the gate stops being
  // readable exactly when someone needs to read it.
  //
  // This mirrors `saveNewStore`'s backfill, which is its exact inverse, and keeps its discipline:
  // chunked WriteBatches, fault isolation per CHUNK (a batch is atomic, so one stale product doc
  // fails its chunk — it must not abort the remaining chunks), awaited rather than fired, and a
  // failure reported loudly instead of thrown. The store itself is already committed; a partial
  // prune must never roll that back, and a leftover key is recoverable by re-running.
  const PRUNE_BATCH_LIMIT = 400
  const failedChunks: Array<{ startIndex: number; size: number; error: string }> = []
  for (let i = 0; i < allFlatProducts.length; i += PRUNE_BATCH_LIMIT) {
    const chunk = allFlatProducts.slice(i, i + PRUNE_BATCH_LIMIT)
    try {
      const pruneBatch = rootDb.batch()
      for (const element of chunk) {
        const productFirestoreRef = getFirestoreRef(element, 'products')
        // `salePrices` and `activeInStore` ONLY — deliberately NOT `storeStock`.
        //
        // Deleting a store's stock key removes QUANTITY, and a stock change with no
        // `product_movements` row to say where it went is precisely the ATL-707 shape: id-level
        // parity reads perfect while the ledger diverges. That is what paused the reconciliation
        // sweep, and it is not a thing to reintroduce from a settings screen.
        //
        // The stock of a deleted store is a real question with a real answer (write off? transfer
        // out? keep as historical?) and it needs a movement-backed decision, not a key deletion.
        // Until then the key stays — visible and wrong is better than invisible and wrong.
        //
        // The ATL-741 coverage matrix is what caught this: adding a storeStock write here made the
        // census 45 → 46 and the site unclaimed by any registry entry. The instrument worked.
        pruneBatch.update(productFirestoreRef, {
          [`activeInStore.${storeRef.id}`]: fieldValue.delete(),
          [`salePrices.${storeRef.id}`]: fieldValue.delete(),
        })
      }
      await pruneBatch.commit()
    } catch (error) {
      failedChunks.push({ startIndex: i, size: chunk.length, error: (error as Error)?.message })
    }
  }
  if (failedChunks.length > 0) {
    console.error(
      '[deleteStore] per-product key prune partially failed (the store itself IS deleted)',
      JSON.stringify({ storeId: storeRef.id, failedChunks }),
    )
    Sentry.captureMessage('deleteStore: product key prune chunks failed', {
      level: 'error',
      tags: { delete_store_prune_failed: true },
      fingerprint: ['delete-store-product-prune-failed'],
      contexts: {
        delete_store: {
          storeId: storeRef.id,
          products: allFlatProducts.length,
          failedChunks: failedChunks.slice(0, 10),
        },
      },
    })
  }
}

/**
 * Update organization settings
 * @param {Object} organizationRef - Reference to the organization document
 * @param {Object} info - Updated organization info
 */
export const updateOrganization = async (organizationRef: any, info: any) => {
  const firestoreRef = getFirestoreRef(organizationRef, 'organizations')
  await firestoreRef.update(info)

  // Dual write to Supabase - pass through, let backend handle
  await bestEffortDualWrite('organizations.update', () => organizationsApi.updateOrganization(organizationRef.id, info));
}

/**
 * ATL-615: dual-write the lift-block override to Supabase. This is the only STORED billing-gate
 * field (currentMonthSalesUSD is computed server-side from sales, never written). Without it, an
 * admin "unlock" would be lost after the Phase-5 read cutover. The caller writes Firestore first;
 * this is the best-effort, non-fatal Supabase half. Pass liftBlockTill: null to clear the override.
 */
export const dualWriteLiftBlock = async (
  organizationId: string,
  liftBlockTill: Date | null,
) => {
  await bestEffortDualWrite('organizations.liftBlock', () =>
    organizationsApi.updateBillingUsage(organizationId, {
      liftBlockTill: liftBlockTill === null ? null : liftBlockTill.toISOString(),
    }),
  )
}

/**
 * Update user's selected store
 * @param {Object} userRef - Reference to the user document
 * @param {Object} store - Store object
 */
export const updateUserStore = async (userRef: any, store: any) => {
  // Persist only the slim serializable snapshot ({ id, name }). Callers (ChooseStoreScreen,
  // ProfileScreen) pass full storesDict items whose live native doc.ref is a cyclic
  // DocumentReference — writing it raw poisons the user doc and fatally crashes
  // JSON.stringify on the next UserDetail open (ATL-555).
  const slimStore = store && typeof store === 'object'
    ? { id: store.id, name: store.name }
    : store
  const firestoreRef = getFirestoreRef(userRef, 'users')
  await firestoreRef.update({ store: slimStore })

  // Dual write to Supabase
  await bestEffortDualWrite('users.update', () => usersApi.updateUser(userRef.id, {
    storeId: store?.id || store,
  }));
}

/**
 * Result of updating constants data with dual-write
 */
export interface UpdateConstantsResult {
  firestoreSuccess: boolean
  supabaseSuccess: boolean
  supabaseError?: string
}

/**
 * Update constants/editable list data (discount options, write-off reasons, etc.)
 * @param {Object} ref - Reference to the constants document
 * @param {Array} data - Updated data array
 * @param {string} constantType - Type of constant (discountOptions, writeOffReasons, etc.)
 * @param {Array} originalData - Original data before edits (for comparison)
 * @returns {Promise<UpdateConstantsResult>} Result with success flags
 */
export const updateConstantsData = async (
  ref: any,
  data: any[],
  constantType?: string,
  originalData?: any[]
): Promise<UpdateConstantsResult> => {
  const result: UpdateConstantsResult = {
    firestoreSuccess: false,
    supabaseSuccess: true,
  }

  // Write to Firestore
  const firestoreRef = getFirestoreRef(ref, 'constants')
  await firestoreRef.update({ data })
  result.firestoreSuccess = true

  // Dual write to Supabase via individual module APIs
  if (constantType && originalData) {
    try {
      await syncEditableConstantToSupabase(constantType, data, originalData)
    } catch (error) {
      result.supabaseSuccess = false
      result.supabaseError = error instanceof Error ? error.message : String(error)
    }
  }

  return result
}

/**
 * Get supabaseId mapping from settings API for a constant type.
 * Maps firestore_id (or id) to supabaseId (UUID).
 */
async function getSupabaseIdMapping(constantType: string): Promise<Map<string, string>> {
  try {
    // Fetch all constants from the /settings endpoint
    const data = await typedClient.get<any>('/settings', { responseSchema: z.any() })

    // Map the constant type to the API response key
    const typeToKey: Record<string, string> = {
      discountOptions: 'discountOptions',
      writeOffReasons: 'writeOffReasons',
      returnReasons: 'returnReasons',
      spendingCategories: 'spendingCategories',
      adminSpendingCategories: 'spendingCategories',
    }
    const key = typeToKey[constantType]
    if (!key) return new Map()

    const items = (data as any)[key] || []

    // For adminSpendingCategories, filter to only admin ones
    const filteredItems =
      constantType === 'adminSpendingCategories'
        ? items.filter((item: any) => item.isAdmin)
        : constantType === 'spendingCategories'
          ? items.filter((item: any) => !item.isAdmin)
          : items

    // Build mapping: id (firestore_id or generated id) -> supabaseId (UUID)
    const mapping = new Map<string, string>()
    for (const item of filteredItems) {
      if (item.supabaseId) {
        // Map both id and firestoreId to supabaseId for reliable lookup
        mapping.set(item.id, item.supabaseId)
        if (item.firestoreId && item.firestoreId !== item.id) {
          mapping.set(item.firestoreId, item.supabaseId)
        }
      }
    }
    return mapping
  } catch (error) {
    console.warn('Failed to fetch supabaseId mapping:', error)
    return new Map()
  }
}

/**
 * Sync editable constant changes to Supabase
 */
async function syncEditableConstantToSupabase(
  constantType: string,
  newData: any[],
  originalData: any[]
): Promise<void> {
  const originalMap = new Map(originalData.map(item => [item.id, item]))
  const newMap = new Map(newData.map(item => [item.id, item]))

  // Determine creates, updates, and deletes
  const toCreate: any[] = []
  const toUpdate: any[] = []
  const toDelete: any[] = []

  // Find items to create or update
  newData.forEach((item, index) => {
    const original = originalMap.get(item.id)
    if (!original) {
      toCreate.push({ ...item, displayOrder: index })
    } else if (original.text !== item.text || originalData.indexOf(original) !== index) {
      toUpdate.push({ ...item, displayOrder: index })
    }
  })

  // Find items to delete
  originalData.forEach(item => {
    if (!newMap.has(item.id)) {
      toDelete.push(item)
    }
  })

  // Execute operations based on constant type
  const api = getEditableConstantApi(constantType)
  if (!api) return

  // Create new items
  for (const item of toCreate) {
    await bestEffortDualWrite('editableConstant.create', () => api.create({
      firestoreId: item.id,
      text: item.text,
      displayOrder: item.displayOrder,
      ...(constantType === 'adminSpendingCategories' && { isAdmin: true }),
    }))
  }

  // For update/delete operations, we need to resolve firestore_id to supabaseId
  // This is necessary when data was loaded from Firestore (no supabaseId) and
  // the firestore_id contains special characters like "50%"
  let supabaseIdMapping: Map<string, string> | null = null
  if (toUpdate.length > 0 || toDelete.length > 0) {
    // Check if any items are missing supabaseId
    const needsMapping =
      toUpdate.some(item => !item.supabaseId) || toDelete.some(item => !item.supabaseId)
    if (needsMapping) {
      supabaseIdMapping = await getSupabaseIdMapping(constantType)
    }
  }

  // Update existing items - use supabaseId (UUID) when available for reliable API calls
  for (const item of toUpdate) {
    // Prefer supabaseId (the Supabase UUID) over id (which may be firestore_id with special chars)
    let apiId = item.supabaseId
    if (!apiId && supabaseIdMapping) {
      apiId = supabaseIdMapping.get(item.id)
    }
    if (!apiId) {
      apiId = item.id // Fallback to id if no mapping found
    }
    await bestEffortDualWrite('editableConstant.update', () => api.update(apiId, {
      text: item.text,
      displayOrder: item.displayOrder,
    }))
  }

  // Delete removed items - use supabaseId when available
  for (const item of toDelete) {
    let apiId = item.supabaseId
    if (!apiId && supabaseIdMapping) {
      apiId = supabaseIdMapping.get(item.id)
    }
    if (!apiId) {
      apiId = item.id // Fallback to id if no mapping found
    }
    // ATL-1207 — this is the LIVE delete path for `discountOptions`, `writeOffReasons`,
    // `returnReasons`, `spendingCategories` and `adminSpendingCategories`: `EditableListScreen`
    // saves the whole array through `updateConstantsData`, which diffs it here. The dedicated
    // `deleteDiscountOption`/`deleteWriteOffReason`/… functions that also state a location are
    // reached only by `utils/constantsDualWriteTest.js`, so without this the guard would be wired
    // exclusively on paths the app never takes. The ids are the reusable kind it exists for —
    // `assignMissingConstantIds` mints them from the item's own text — and the document is
    // `constants/<constantType>` with the items under `data` (asserted from the `EditableList`
    // navigation sites in `constantsDeleteLocation.atl1207.test.js`).
    //
    // `item.id`, NOT `apiId`: `apiId` may be the Supabase UUID, which belongs in the URL and not
    // in a lookup against `constants/<family>.data[].id`, which is what the probe matches. And
    // stated only when there IS an id — a location whose id the probe cannot match answers "still
    // deleted", the one verdict that fires the delete, so half-stating it is worse than not.
    //
    // ⚠️ ONE CONSEQUENCE, ACCEPTED. Stating an identity also makes this label OUTBOX-ELIGIBLE for
    // items whose id the URL scan could not recover (`voda`, `50%` — anything under 15 characters
    // or carrying punctuation); those failures used to be refused as `no-document-identity` and
    // lost outright. Queueing them is the point — a lost delete is a row that comes back at the
    // read cutover. Deleting an item that was never mirrored to Supabase answers the
    // entity-absent 404 on replay too, and since ATL-1193 the drain treats that as delivered
    // for every delete label, as the live path does, rather than burning ten attempts and
    // dropping it as a loss.
    const itemFirestoreId: unknown = item.id
    const deleteContext =
      typeof itemFirestoreId === 'string' && itemFirestoreId.length > 0
        ? { firestoreId: itemFirestoreId, deletedFrom: constantsItemLocation(constantType) }
        : undefined
    // ATL-1195: with an id the delete takes the shared 404 contract (an entity-absent 404 is free);
    // an id-less item cannot satisfy `bestEffortDualWriteDelete`'s required `firestoreId`, and is
    // not queueable anyway, so it keeps the plain wrapper.
    if (deleteContext) {
      await bestEffortDualWriteDelete('editableConstant.delete', () => api.delete(apiId), deleteContext)
    } else {
      await bestEffortDualWrite('editableConstant.delete', () => api.delete(apiId), deleteContext)
    }
  }
}

/**
 * Get the appropriate API for an editable constant type
 */
function getEditableConstantApi(constantType: string) {
  switch (constantType) {
    case 'discountOptions':
      return {
        create: (data: any) => discountOptionsApi.createDiscountOption(data),
        update: (id: string, data: any) => discountOptionsApi.updateDiscountOption(id, data),
        delete: (id: string) => discountOptionsApi.deleteDiscountOption(id),
      }
    case 'writeOffReasons':
      return {
        create: (data: any) => writeOffReasonsApi.createWriteOffReason(data),
        update: (id: string, data: any) => writeOffReasonsApi.updateWriteOffReason(id, data),
        delete: (id: string) => writeOffReasonsApi.deleteWriteOffReason(id),
      }
    case 'returnReasons':
      return {
        create: (data: any) => returnReasonsApi.createReturnReason(data),
        update: (id: string, data: any) => returnReasonsApi.updateReturnReason(id, data),
        delete: (id: string) => returnReasonsApi.deleteReturnReason(id),
      }
    case 'spendingCategories':
    case 'adminSpendingCategories':
      return {
        create: (data: any) => spendingCategoriesApi.createSpendingCategory(data),
        update: (id: string, data: any) => spendingCategoriesApi.updateSpendingCategory(id, data),
        delete: (id: string) => spendingCategoriesApi.deleteSpendingCategory(id),
      }
    default:
      return null
  }
}

/**
 * Update organization onboarding checklist
 * @param {Object} organizationRef - Reference to the organization document
 * @param {Object} onboardingChecklist - Updated checklist
 * @param {number} onboardingTasksLeft - Tasks remaining
 */
export const updateOnboardingChecklist = async (organizationRef: any, onboardingChecklist: any, onboardingTasksLeft: number) => {
  const firestoreRef = getFirestoreRef(organizationRef, 'organizations')
  await firestoreRef.update({ onboardingChecklist, onboardingTasksLeft })

  // Dual write to Supabase
  await bestEffortDualWrite('organizations.updateOnboarding', () => organizationsApi.updateOnboarding(organizationRef.id, { onboardingChecklist, onboardingTasksLeft }));
}

/**
 * Complete onboarding
 * @param {Object} organizationRef - Reference to the organization document
 */
export const completeOnboarding = async (organizationRef: any) => {
  const firestoreRef = getFirestoreRef(organizationRef, 'organizations')
  await firestoreRef.update({ onboardingCompleted: true })

  // Dual write to Supabase
  await bestEffortDualWrite('organizations.updateOnboarding', () => organizationsApi.updateOnboarding(organizationRef.id, { onboardingCompleted: true }));
}

/**
 * Update saved cart
 * @param {Object} savedCartRef - Reference to the saved cart document
 * @param {Object} savedCartInfo - Updated saved cart info
 */
export const updateSavedCart = async (savedCartRef: any, savedCartInfo: any) => {
  const firestoreRef = getFirestoreRef(savedCartRef, 'savedCarts')
  // Sanitize cart items to remove non-serializable fields (ref, functions, etc.)
  // ATL-867: same discountAmount normalization as the create path — see withNormalizedDiscountAmount.
  const sanitizedInfo = sanitizeForFirestoreDeep(withNormalizedDiscountAmount(savedCartInfo)) as typeof savedCartInfo
  await firestoreRef.update(sanitizedInfo)

  // Dual write to Supabase. Coerce cart items to the API's expected types (barcode→string,
  // createdAt/updatedAt→ISO) exactly like the create path — in Firestore-read mode the raw items
  // carry numeric barcode + Timestamp objects that would otherwise fail request validation and
  // drift (ATL-481/482). Use sanitizedInfo (not the raw input) for consistency with create.
  const updateInfo = sanitizedInfo as Record<string, any>
  await bestEffortDualWrite('savedCarts.update', () => savedCartsApi.updateSavedCart(firestoreRef.id, {
    ...updateInfo,
    ...(Array.isArray(updateInfo.cartItems) && {
      cartItems: updateInfo.cartItems.map((it: any) => coerceCartItemForApi(it)),
    }),
    ...(Array.isArray(updateInfo.items) && {
      items: updateInfo.items.map((it: any) => coerceCartItemForApi(it)),
    }),
  } as Record<string, unknown>));
}

/**
 * Update client order
 * @param {Object} orderRef - Reference to the order document
 * @param {Object} updatedData - Updated order data
 */
export const updateClientOrder = async (orderRef: any, updatedData: any) => {
  const firestoreRef = getFirestoreRef(orderRef, 'orders')
  await firestoreRef.update(updatedData)

  // Dual write to Supabase
  await bestEffortDualWrite('orders.update', () => ordersApi.updateOrder(firestoreRef.id, updatedData));
}

/**
 * Update store timezone
 * @param {Object} storeRef - Reference to the store document
 * @param {string} timezone - Timezone string
 */
export const updateStoreTimezone = async (storeRef: any, timezone: string) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateStoreTimezone')
  const firestoreRef = getFirestoreRef(storeRef, 'stores')
  await firestoreRef.update({ timezone })

  // Dual write to Supabase
  await bestEffortDualWrite('stores.update', () => storesApi.updateStore(storeRef.id, { timezone }));
}

/**
 * Update store wipon credentials
 * @param {Object} storeRef - Reference to the store document
 * @param {Object} wiponCredentials - Wipon credentials object
 */
export const updateStoreWiponCredentials = async (storeRef: any, wiponCredentials: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateStoreWiponCredentials')
  const firestoreRef = getFirestoreRef(storeRef, 'stores')
  await firestoreRef.update({ wiponCredentials })

  // Dual write to Supabase
  await bestEffortDualWrite('stores.update', () => storesApi.updateStore(storeRef.id, { wiponCredentials }));
}

/*
 * `updateSubscription` was removed in ATL-855.
 *
 * Its one caller (`BillingScreen.restorePurchases`) passed the ORGANIZATION's ref while the helper
 * resolved it as `getFirestoreRef(ref, 'subscriptions')`. `getFirestoreRef` checks `item.id` before
 * its "already a real Firestore ref" branch and a `DocumentReference` has `.id`, so in BOTH read
 * modes this wrote `organizations/{org}/subscriptions/{orgId}` — a self-keyed document in the
 * subcollection the Paddle mirror owns, not the organization document the app reads for billing.
 *
 * Subscription state is now written server-side only, under the ATL-760 downgrade guard:
 * `apps/atlas-backend/src/modules/organizations/organization-subscription.service.ts`, reached from
 * `global/Firebase/organizationSubscription.ts`.
 */

// Firestore batch limit is 500 operations
const BATCH_LIMIT = 500

/**
 * Delete documents in batches from Firestore
 * @param {Array} documentRefs - Array of document references to delete
 * @param {string} collectionName - Name of the collection (for dual-write)
 */
export const deleteDocumentsInBatches = async (documentRefs: any[], collectionName: string) => {
  // Process documents in batches
  for (let i = 0; i < documentRefs.length; i += BATCH_LIMIT) {
    const batch = rootDb.batch()
    const batchDocs = documentRefs.slice(i, i + BATCH_LIMIT)

    batchDocs.forEach((docRef: any) => {
      batch.delete(docRef)
    })

    await batch.commit()
    console.log(`Deleted batch ${Math.floor(i / BATCH_LIMIT) + 1}: ${batchDocs.length} documents from ${collectionName}`)
  }
}

/**
 * Reset collection data before a cutoff date
 * @param {Object} db - Firestore database instance
 * @param {string} collection - Collection name
 * @param {string} dateField - Date field to filter by
 * @param {Date} cutoffDate - Delete all documents before this date
 */
export const resetCollectionData = async (db: FirestoreDb, collection: string, dateField: string, cutoffDate: Date) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#resetCollectionData')
  reportFirestoreOnlyWriterUsage('resetCollectionData', db?.id, { target: collection })
  const querySnapshot = await db
    .collection(collection)
    .where(dateField, '<', cutoffDate)
    .get()

  if (querySnapshot.size > 0) {
    const documentRefs: any[] = []
    querySnapshot.forEach((doc: any) => {
      documentRefs.push(doc.ref)
    })
    console.log(`Found ${querySnapshot.size} documents to delete from ${collection}`)
    await deleteDocumentsInBatches(documentRefs, collection)
    console.log(`Successfully deleted ${querySnapshot.size} documents from ${collection}`)
  } else {
    console.log(`No documents found in ${collection} before ${cutoffDate.toISOString()}`)
  }
}

/**
 * Reset movement collection group data before a cutoff date
 * @param {Object} db - Firestore database instance
 * @param {string} organizationId - Organization ID
 * @param {Date} cutoffDate - Delete all documents before this date
 */
export const resetMovementData = async (db: FirestoreDb, organizationId: string, cutoffDate: Date) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#resetMovementData')
  reportFirestoreOnlyWriterUsage('resetMovementData', organizationId)
  // Get all stores for this organization
  const storesSnapshot = await db.collection('stores').get()
  let totalDeleted = 0

  // Query movement collection group for each store
  for (const storeDoc of storesSnapshot.docs) {
    const storeId = storeDoc.id

    const querySnapshot = await rootDb
      .collectionGroup('movement')
      .where('organizationId', '==', organizationId)
      .where('store', '==', storeId)
      .where('date', '<', cutoffDate)
      .orderBy('date', 'desc')
      .get()

    if (querySnapshot.size > 0) {
      const documentRefs: any[] = []
      querySnapshot.forEach((doc: any) => {
        documentRefs.push(doc.ref)
      })
      console.log(`Found ${querySnapshot.size} movement documents to delete for store ${storeId}`)
      await deleteDocumentsInBatches(documentRefs, 'movement')
      totalDeleted += querySnapshot.size
      console.log(`Successfully deleted ${querySnapshot.size} movement documents for store ${storeId}`)
    }
  }

  if (totalDeleted > 0) {
    console.log(`Successfully deleted ${totalDeleted} total movement documents`)
  } else {
    console.log(`No movement documents found before ${cutoffDate.toISOString()}`)
  }
}

/**
 * Reset all product stocks to 0
 * @param {Object} productsDict - Dictionary of products
 */
export const resetProductStocks = async (productsDict: Record<string, any>) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#resetProductStocks')
  reportFirestoreOnlyWriterUsage('resetProductStocks')
  const productIds = Object.keys(productsDict)
  let updateCount = 0
  let batch = rootDb.batch()
  const updatedProducts: { id: string; storeStock: any }[] = []

  for (const productId of productIds) {
    const product = productsDict[productId]
    const storeStock = { ...product.storeStock }
    for (const storeId in storeStock) {
      storeStock[storeId] = 0
    }
    console.log('Resetting stock for product:', productId, storeStock)
    const productRef = getFirestoreRef(product, 'products')
    batch.update(productRef, { storeStock })
    updatedProducts.push({ id: productId, storeStock })
    updateCount++

    // Commit batch when reaching limit
    if (updateCount >= BATCH_LIMIT) {
      await batch.commit()
      batch = rootDb.batch()
      updateCount = 0
    }
  }

  // Commit remaining updates
  if (updateCount > 0) {
    await batch.commit()
  }

  // CONTRACT (ATL-773): this function deliberately makes NO Supabase call of its own. Its one
  // mirror is the CALLER's single organizationsApi.resetData({ resetProductStocks: true }) call
  // (ResetDataScreen), whose reset_organization_data RPC writes the reset LEDGER-first since
  // migration 20260728150000 (one stock_reset movement per pair; cache derived by the sync
  // trigger, so the zero is durable). Any NEW caller of this function must invoke that same
  // mirror itself, or the reset will be Firestore-only — the parity suite asserts the
  // no-own-call shape, so a silent second mirror added here would go red.

  console.log('Successfully reset all product stocks')
}

/**
 * Delete all products from organization
 * @param {Object} db - Firestore database instance
 */
export const deleteAllProducts = async (db: FirestoreDb) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteAllProducts')
  reportFirestoreOnlyWriterUsage('deleteAllProducts', db?.id)
  const productsSnapshot = await db.collection('products').get()

  if (productsSnapshot.size > 0) {
    const documentRefs: any[] = []
    productsSnapshot.forEach((doc) => {
      documentRefs.push(doc.ref)
    })
    console.log(`Found ${productsSnapshot.size} products to delete`)
    await deleteDocumentsInBatches(documentRefs, 'products')
    console.log(`Successfully deleted ${productsSnapshot.size} products`)
  } else {
    console.log('No products found to delete')
  }
}

export const getManufacturesOnDateRange = async (db: FirestoreDb, range: any, flat = false, store: any = null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: Record<string, any> = {
        startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
        endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      }

      if (store) {
        params.storeId = store
      }

      // ATL-1477: every manufacture in the range, not the newest 50.
      const manufactures = (await getWholeList('/manufactures', params, newestFirst('date'))).map((m) => transformApiItem(m, 'manufactures'))

      if (flat) {
        return manufactures
      } else {
        const docs: Record<string, any[]> = {} // date key - array of manufactures
        manufactures.forEach(function (m: any) {
          const mDate = m.date?.toDate ? m.date.toDate() : new Date(m.date)
          const date = format(getLocalStoreDate(m.store || store, mDate), 'd/M/y EEEE')
          const newArr = docs[date] ? [...docs[date]] : []
          newArr.push(m)
          docs[date] = [...newArr]
        })
        const docsArr: { title: string; data: any }[] = []
        for (const key in docs) {
          const newData = {
            title: key,
            data: docs[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching manufactures from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'manufactures' },
        extra: {
          range,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  let querySnapshot: any = db.collection('manufactures')
  if (store) {
    querySnapshot = querySnapshot.where('store', '==', store)
  }
  querySnapshot = await querySnapshot.where('date', '>=', range.startDate)
    .where('date', '<=', range.endDate)
    .orderBy('date', 'desc')
    .get()
  if (flat) {
    const flatManufactures: any[] = []
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      flatManufactures.push(snap)
    })
    return flatManufactures
  } else {
    const groupedManufactures: Record<string, any[]> = {} // date key - array of sales
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      const date = format(getLocalStoreDate(snap.store, convertToDate(snap.date) || new Date()), 'd/M/y EEEE')
      const newArr = groupedManufactures[date] ? [...groupedManufactures[date]] : []
      newArr.push(snap)
      groupedManufactures[date] = [...newArr]
    })
    const resultManufacturesArr: any[] = []
    for (const key in groupedManufactures) {
      const newData = {
        title: key,
        data: groupedManufactures[key]
      }
      resultManufacturesArr.push(newData)
    }
    return resultManufacturesArr
  }
}

/** Mirrors the backend `CreateManufactureDto.batchNumber` `@MaxLength(100)` (ATL-1701). */
const MAX_MANUFACTURE_BATCH_NUMBER_LENGTH = 100

export const saveNewManufacture = async (db: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewManufacture')
  // ATL-1701: optional batch number + import provenance. Normalised once, up front, so the
  // Firestore document and the Supabase payload carry the identical value. The dual-write below
  // runs AFTER the Firestore commit with no rollback, so a batch number the backend would 400 on
  // (MAX_MANUFACTURE_BATCH_NUMBER_LENGTH) is refused here, before anything is written.
  const batchNumber = typeof info.batchNumber === 'string' ? info.batchNumber.trim() : ''
  if (batchNumber.length > MAX_MANUFACTURE_BATCH_NUMBER_LENGTH) {
    throw new Error(`batchNumber must be at most ${MAX_MANUFACTURE_BATCH_NUMBER_LENGTH} characters`)
  }
  const manufactureSource = info.manufactureSource === 'import' ? 'import' : undefined
  const importBatchId =
    typeof info.importBatchId === 'string' && info.importBatchId !== '' ? info.importBatchId : undefined
  const batchFields: { batchNumber?: string; manufactureSource?: 'import'; importBatchId?: string } = {
    ...(batchNumber ? { batchNumber } : {}),
    ...(manufactureSource ? { manufactureSource } : {}),
    ...(importBatchId ? { importBatchId } : {}),
  }
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)
  const newRef = db.collection('manufactures').doc()
  // Sanitize info to convert Timestamps to Dates (fixes serialization issues)
  const {
    batchNumber: _rawBatchNumber,
    manufactureSource: _rawManufactureSource,
    importBatchId: _rawImportBatchId,
    ...infoWithoutBatchFields
  } = info
  const sanitizedInfo: Record<string, unknown> = {
    ...(sanitizeForFirestoreDeep(infoWithoutBatchFields) as Record<string, unknown>),
    ...batchFields,
  }
  // Use sanitized date for all batch operations to avoid Timestamp serialization errors
  const sanitizedDate = sanitizedInfo.date
  batch.set(newRef, sanitizedInfo)
  // update stocks for manufactured product
  const storeStockField = 'storeStock.' + info.store
  // Create proper DocumentReferences from IDs (spread operator may have converted refs to plain objects)
  const productRef = db.collection('products').doc(info.product.id)
  batch.update(productRef, { [storeStockField]: fieldValue.increment(info.quantity) })
  batch.set(productRef.collection('movement').doc(), {
    productID: info.product.id,
    type: 'manufacture',
    previousInStock: allProductsDict[info.product.id]?.storeStock?.[info.store] || 0,
    increment: info.quantity,
    date: fieldValue.serverTimestamp(),
    linkedDocId: newRef.id,
    linkedDocDate: sanitizedDate,
    clientSaveDate: new Date(),
    store: info.store,
    organizationId: info.organizationId
  })
  // update stocks for ingredients
  for (const [idx, ingredient] of info.ingredients.entries()) {
    if (!allProductsDict[ingredient.id]) {
      const error = new Error('INGREDIENT_NOT_FOUND') as any
      error.ingredientName = ingredient.name
      error.productName = info.product.name
      throw error
    }
    const increment = 0 - (info.quantity * ingredient.quantity)
    // Create proper DocumentReference from ingredient ID
    const ingredientRef = db.collection('products').doc(ingredient.id)
    batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
    batch.set(ingredientRef.collection('movement').doc(), {
      productID: ingredient.id,
      type: 'manufacture',
      previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
      increment,
      date: fieldValue.serverTimestamp(),
      linkedDocId: newRef.id,
      linkedDocDate: sanitizedDate,
      clientSaveDate: new Date(),
      store: info.store,
      organizationId: info.organizationId
    })
  }
  // update daily stats
  const statFields = {
    manufacturesAmount: fieldValue.increment(info.totalSum),
    manufacturesQuantity: fieldValue.increment(info.quantity),
  }
  batch.set(db.collection('dailyStats').doc(info.dailyStatID), {...statFields,
    date: sanitizedDate,
    store: info.store,
    organizationId: info.organizationId
  }, {merge: true})
  await commitGuardedCreate('manufactures.create', db, newRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  const apiResult = await bestEffortDualWrite('manufactures.create', () => manufacturesApi.createManufacture({
    firestoreId: newRef.id,
    storeId: info.store,
    productId: info.product.id,
    quantity: info.quantity,
    totalSum: info.totalSum,
    // ATL-1831: each line with its historical cost, as the Firestore line records it.
    ingredients: info.ingredients.map(manufactureIngredientPayload),
    manufactureDate: info.date?.toDate ? info.date.toDate().toISOString() : (info.date instanceof Date ? info.date.toISOString() : info.date),
    ...batchFields,
    dailyStatId: info.dailyStatID,
  } as any));

  // Expose the Supabase UUID on the returned ref so callers (and e2e tests) can
  // query product_movements by linked_doc_id. The /product-movements backend
  // cannot resolve a manufacture Firestore id → UUID (no 'manufacture' entry in
  // its linkedDoc tableMap), so the UUID is required for manufacture movement lookups.
  const supabaseId = (apiResult as any)?.supabaseId || (apiResult as any)?.id
  if (supabaseId) {
    try { (newRef as any).supabaseId = supabaseId } catch { /* ref may be frozen */ }
  }

  return newRef
}

export const deleteManufacture = async (db: FirestoreDb, ref: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteManufacture')
  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)

  // A manufacture opened from the operations list under Supabase mode carries
  // only flat ids: { productId (UUID), storeId, ingredients:[{name,quantity,
  // productId(UUID)}], date(string), totalSum, quantity } — it has NO `product`
  // object, no `store`, no `id` on ingredients, and `date` is a string. The
  // original implementation read info.product.id / info.store / ingredient.id /
  // info.date.toDate() directly, throwing BEFORE the Supabase cleanup runs and
  // leaving the manufacture un-reversed. Re-hydrate the shape from the live
  // productsDict (indexed by every id spelling so a UUID maps back to the full
  // product) and normalise store/date so the batch can be built safely.
  const productByAnyId: Record<string, any> = {}
  for (const p of Object.values(allProductsDict as Record<string, any>)) {
    for (const key of [p?.id, p?.firestoreId, p?.firestore_id, p?.supabaseId, p?.supabase_id]) {
      if (key) productByAnyId[key as string] = p
    }
  }
  const resolveProduct = (raw: any): any => {
    if (raw == null) return raw
    const pid = raw.id || raw.firestoreId || raw.firestore_id || raw.productId || raw.supabaseId
    const full = pid ? productByAnyId[pid] : undefined
    return full ? { ...full, ...raw, id: full.id, ref: full.ref } : raw
  }
  const product = resolveProduct(info.product || (info.productId ? { productId: info.productId } : null))
  const ingredients = (info.ingredients || []).map(resolveProduct)
  const store = info.store || info.storeId || info.store_id
  const organizationId = info.organizationId || info.organization_id

  const firestoreRef = getFirestoreRef(ref, 'manufactures')
  batch.delete(firestoreRef)
  // update stocks for manufactured product
  const storeStockField = 'storeStock.' + store
  if (product && product.id && allProductsDict[product.id]) { // check if product is not deleted
    const productRef = getFirestoreRef(product, 'products')
    batch.update(productRef, { [storeStockField]: fieldValue.increment(-info.quantity) })
    batch.set(productRef.collection('movement').doc(), {
      productID: product.id,
      type: 'deleteManufacture',
      previousInStock: allProductsDict[product.id]?.storeStock?.[store] || 0,
      increment: -info.quantity,
      date: fieldValue.serverTimestamp(),
      linkedDocId: ref.id,
      linkedDocDate: fieldValue.serverTimestamp(),
      clientSaveDate: new Date(),
      store,
      organizationId
    })
  }
  // update stocks for ingredients
  for (const [idx, ingredient] of ingredients.entries()) {
    if (ingredient.id && allProductsDict[ingredient.id]) { // check if product is not deleted
      const increment = (info.quantity * ingredient.quantity)
      const ingredientRef = getFirestoreRef(ingredient, 'products')
      batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
      batch.set(ingredientRef.collection('movement').doc(), {
        productID: ingredient.id,
        type: 'deleteManufacture',
        previousInStock: allProductsDict[ingredient.id]?.storeStock?.[store] || 0,
        increment,
        date: fieldValue.serverTimestamp(),
        linkedDocId: ref.id,
        linkedDocDate: fieldValue.serverTimestamp(),
        clientSaveDate: new Date(),
        store,
        organizationId
      })
    }
  }
  // update daily stats — only when a valid doc id is resolvable (a list-item
  // manufacture may lack dailyStatID; derive it from date + store). The Supabase
  // delete RPC recomputes stats, so skipping a missing-id write is safe.
  // ATL-1013: business day, not the device's calendar day — see deleteSale. The null-skip
  // when no store is resolvable is deliberate and preserved (see the comment above).
  // ATL-1013: the RAW `info.date`, never a `new Date()` default — a manufacture whose date did
  // not survive the read must NOT reverse today's doc, and this site writes with
  // `set(..., {merge:true})`, which would CREATE that doc carrying negative
  // manufacturesAmount/Quantity rather than fail.
  //
  // But the guard is a SKIP here, not a throw — this site is deliberately degrade-not-abort
  // (the Supabase delete RPC recomputes stats, so a missing stats write is recoverable, while a
  // failed delete leaves stock unreversed). Hence the OrNull variant rather than a truthiness
  // pre-check: `info.date` can be TRUTHY and still unparseable (a serialized
  // {seconds,nanoseconds}, a non-ISO string), which a truthiness check waves through into the
  // refusal — reintroducing the abort this comment exists to prevent.
  const statDocName = dailyStatIDForRecordOrNull(info.dailyStatID, store, info.date as DateLike)
  if (statDocName) {
    const statFields = {
      manufacturesAmount: fieldValue.increment(-(info.totalSum || 0)),
      manufacturesQuantity: fieldValue.increment(-(info.quantity || 0)),
    }
    batch.set(db.collection('dailyStats').doc(statDocName), statFields, { merge: true })
  }
  await commitBatchTolerant(batch, 'deleteManufacture')

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('manufactures.delete', () => manufacturesApi.deleteManufacture(ref.id), { firestoreId: ref.id });
}

export const getComplexSalesOnDateRange = async (db: FirestoreDb, range: any, flat = false, store: any, clientID: string | null = null, active = false) => {
  if (shouldUseSupabase()) {
    try {
      const params: Record<string, any> = {}

      if (store) {
        params.storeId = store
      }
      if (clientID) {
        params.clientId = clientID
      }
      if (range) {
        params.startDate = range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate
        params.endDate = range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate
      }

      // ATL-1477: every complex sale matching the filters, not the newest 50.
      let sales = await getWholeList('/complex-sales', params, newestFirst('createdDate'))

      // Filter for active sales if needed
      if (active) {
        sales = sales.filter((sale: any) => sale.isActive !== false)
      }

      if (flat) {
        return sales
      } else {
        const docs: Record<string, any[]> = {} // date key - array of sales
        sales.forEach(function (sale: any) {
          const saleDate = sale.createdDate?.toDate ? sale.createdDate.toDate() : new Date(sale.createdDate)
          const date = format(getLocalStoreDate(sale.store || sale.storeId, saleDate), 'd/M/y EEEE')
          const newArr = docs[date] ? [...docs[date]] : []
          newArr.push(sale)
          docs[date] = [...newArr]
        })
        const docsArr: { title: string; data: unknown[] }[] = []
        for (const key in docs) {
          const newData = {
            title: key,
            data: docs[key]
          }
          docsArr.push(newData)
        }
        return docsArr
      }
    } catch (error: unknown) {
      console.error('Error fetching complex sales from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'complexSales' },
        extra: {
          range,
          store,
          clientID,
          active,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  // Firestore implementation
  let query: any = db.collection('complexSales')
  if (store) {
    query = query.where('store', '==', store)
  }
  if (clientID) {
    query = query.where('clientId', '==', clientID)
  }
  if (active) {
    query = query.where('isActive', '==', true)
  }
  if (range) {
    query = query
      .where('createdDate', '>=', range.startDate)
      .where('createdDate', '<=', range.endDate)
  }
  const querySnapshot = await query.orderBy('createdDate', 'desc').get()
  if (flat) {
    const docs: any[] = []
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      docs.push(snap)
    })
    return docs
  } else {
    const docs: Record<string, any[]> = {} // date key - array of sales
    querySnapshot.forEach(function (doc: any) {
      const snap = doc.data()
      snap.id = doc.id
      snap.ref = doc.ref
      const date = format(getLocalStoreDate(snap.store, snap.createdDate.toDate()), 'd/M/y EEEE')
      const newArr = docs[date] ? [...docs[date]] : []
      newArr.push(snap)
      docs[date] = [...newArr]
    })
    const docsArrFirestore: { title: string; data: unknown[] }[] = []
    for (const key in docs) {
      const newData = {
        title: key,
        data: docs[key]
      }
      docsArrFirestore.push(newData)
    }
    return docsArrFirestore
  }
}

export const saveNewComplexSale = async (db: FirestoreDb, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewComplexSale')
  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)
  const ref = db.collection('complexSales').doc()

  // The 'Empty template' option leaves info.items undefined (not []); default it so
  // the loop below — and the downstream dual-write — don't throw 'info.items is not iterable'.
  if (!Array.isArray(info.items)) info.items = []

  // update inStock of product
  const storeStockField = 'storeStock.' + info.store
  for (const value of info.items) {
    if (value.isComposite && !value.manufacturedBeforeSale) {
      for (const ingredient of value.ingredients) {
        if (!allProductsDict[ingredient.id]) {
          const error = new Error('INGREDIENT_NOT_FOUND') as any
          error.ingredientName = ingredient.name
          error.productName = value.name
          throw error
        }
        const increment = 0 - (value.quantity * ingredient.quantity)
        const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
        batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(ingredientRef.collection('movement').doc(), {
          productID: ingredient.id,
          type: 'complexSale',
          previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: ref.id,
          linkedDocDate: info.createdDate,
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    } else {
      if (!value.id.startsWith('custom')) {
        const increment = 0 - value.quantity
        const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
        batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(valueRef.collection('movement').doc(), {
          productID: value.id,
          type: 'complexSale',
          previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: ref.id,
          linkedDocDate: info.createdDate,
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    }
  }

  // update client
  if (info.client) {
    batch.update(db.collection('clients').doc(info.client.id), {
      totalPurchasedValue: fieldValue.increment(info.finalPrice),
      lastPurchaseDate: info.createdDate,
      lastPurchaseSource: info.store,
    })
  }
    
  /**
   * ATL-1249 — the money legs, recorded on the payment AND held for the dual-write.
   *
   * A complex sale commits one `transactions` document per payment inside this batch. The id of
   * each was previously discarded the moment it was minted, and two separate harms followed from
   * that one discard (both measured read-only on production 2026-09-07):
   *
   *   A. Supabase never received the leg. 13 documents in Firestore and in no form in Supabase
   *      (Justore 12, UNDERWOOD DECOR 1), most recent 2026-09-04 — invisible to the cutover gate,
   *      whose `setdiff` clause excuses every missing transaction id on both organizations
   *      (ATL-821).
   *
   *   B. Firestore could never DELETE the leg. `deleteComplexSale` and `deleteComplexSalePayment`
   *      both remove it `if (payment.transactionId)` — the field this path never wrote — so the
   *      delete was a silent no-op. Justore holds 155 payments of which 3 carry the field and 27
   *      complexSale legs whose parent is gone; UNDERWOOD DECOR 194 / 123 and 5. All 32 orphans
   *      have a parent named in a `deleteComplexSale` update, so they are that no-op, and they
   *      still count toward the wallet balance the customer reads today.
   *
   * `addComplexSalePayment` — the path that adds a payment to an EXISTING sale — has always
   * recorded the id, which is why the 3 and the 123 exist. This is the create-time path catching
   * up with it.
   *
   * The wallet id is forwarded verbatim rather than re-derived on the server:
   * `destinationWalletForStore[store].id` is a client-side lookup, and a second spelling of it in
   * the backend would drift the first time either changed (the ATL-1245 argument).
   */
  const linkedTransactions: { firestoreId: string; value: number; destinationWallet: string }[] = []
  if (info.payments) {
    const paymentsWithLegs: any[] = []
    for (const payment of info.payments) {
      if (!payment.paymentMethod) {
        // No payment method, no wallet, no leg — and so no `transactionId` to record. Stamping
        // one here would give Supabase an id that no Firestore document carries.
        paymentsWithLegs.push(payment)
        continue
      }
      const walletId = payment.paymentMethod.destinationWalletForStore?.[info.store]?.id
      const transactionRef = db.collection('transactions').doc()
      batch.set(transactionRef, {
        linkedDocId: ref.id,
        linkedDocType: 'complexSale',
        type: 'income',
        destinationWallet: walletId || '',
        value: payment.amount,
        date: info.createdDate,
        store: info.store,
        organizationId: info.organizationId,
        createdDate: new Date(),
      })
      // A NEW payment object rather than a mutation: `info.payments` is the screen's React state
      // array, and `saveNewComplexSale` must not write through it.
      paymentsWithLegs.push({ ...payment, transactionId: transactionRef.id })
      if (Number.isFinite(payment.amount)) {
        linkedTransactions.push({
          firestoreId: transactionRef.id,
          value: payment.amount,
          destinationWallet: walletId || '',
        })
      }
      // else: the leg is written and recorded in Firestore, but WITHHELD from the request.
      // `@IsNumber()` refuses NaN, and a 400 is classified client-side as a deterministic defect
      // that the outbox never queues (`classifyDualWriteDefect`) — so one unusable leg would
      // destroy the whole complex sale's dual-write. Withholding it degrades to the pre-ATL-1249
      // behaviour for that leg alone and never sends a value the Firestore document lacks.
      // `addPayment` guards `parseFloat(paymentAmount) > 0`, so this is a second layer.
    }
    // Assigning to `info` (a fresh object literal built by the screen) rather than to the state
    // array it borrowed: the document written below, the payload sent, and the item the screen
    // caches all read this one updated array, so the id is recorded in all three or in none.
    info.payments = paymentsWithLegs
  }
  
  // update dailyStats
  const statFields = dailyStatIncrementsForComplexSales([info], true, false)
  batch.set(db.collection('dailyStats').doc(info.dailyStatID), {...statFields,
    store: info.store,
    date: info.createdDate,
    organizationId: info.organizationId
  }, {merge: true})

  const sanitizedInfo = sanitizeForFirestoreDeep(info) as Record<string, unknown>
  batch.set(ref, sanitizedInfo)

  await commitGuardedCreate('complexSales.create', db, ref, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.create', () => complexSalesApi.createComplexSale({
    firestoreId: ref.id,
    organizationId: info.organizationId,
    storeId: info.store,
    name: info.name,
    // ATL-1024: `info` has NO `templateName` key — the name lives at `info.template.templateName`
    // (994/994 prod documents). Sending the flat spelling alone dropped it silently, which is why
    // all 55 live dual-written rows have `complex_sales.template_name` NULL. This is the same
    // wrong-field-name defect as the migration transform, on the live path, one field wide.
    // `totalPrice` below is NOT the same case and is deliberately left as-is: `info` has no such
    // key either, but there is nothing to send — `finalPrice` is the collection's only price, and
    // `complex_sales.total_price`'s single reader is a COALESCE fallback. See the ATL-1024
    // backfill script's docblock.
    templateName: info.templateName || info.template?.templateName,
    items: info.items,
    finalPrice: info.finalPrice,
    finalExpensesPrice: info.finalExpensesPrice,
    totalPrice: info.totalPrice,
    deposit: info.deposit,
    status: info.status,
    clientId: info.client?.id,
    createdBy: info.createdBy,
    comment: info.comment,
    createdDate: info.createdDate?.toDate ? info.createdDate.toDate().toISOString() : (info.createdDate instanceof Date ? info.createdDate.toISOString() : info.createdDate),
    dailyStatID: info.dailyStatID,
    ...(info.transactionId && { transactionId: info.transactionId }),
    ...(info.secondTransactionId && { secondTransactionId: info.secondTransactionId }),
    ...(info.payments && { payments: info.payments }),
    // ATL-1249 — the money legs minted above, so the backend writes them in THIS request rather
    // than never. One request means one outbox entry: the sale and its money land together or
    // neither does.
    ...(linkedTransactions.length > 0 && { transactions: linkedTransactions }),
    ...(info.comments && { comments: info.comments }),
    ...(info.updates && { updates: info.updates }),
  } as any));

  return ref
}

export const deleteComplexSale = async (db: FirestoreDb, info: any, uid: string) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteComplexSale')
  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)
  const complexSaleRef = getFirestoreRef(info, 'complexSales')
  batch.delete(complexSaleRef)

  // update inStock of product
  const storeStockField = 'storeStock.' + info.store
  for (const [index, value] of info.items.entries()) {
    if (value.isComposite && !value.manufacturedBeforeSale) {
      for (const [_idx, ingredient] of value.ingredients.entries()) {
        if (allProductsDict[ingredient.id]) { // check if product is not deleted
          const increment = (value.quantity * ingredient.quantity)
          const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
          batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
          batch.set(ingredientRef.collection('movement').doc(), {
            productID: ingredient.id,
            type: 'deleteComplexSale',
            previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: complexSaleRef.id,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: info.store,
            organizationId: info.organizationId
          })
        }
      }
    } else {
      if (!value.id.startsWith('custom') && allProductsDict[value.id]) {
        const increment = value.quantity
        const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
        batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(valueRef.collection('movement').doc(), {
          productID: value.id,
          type: 'deleteComplexSale',
          previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: complexSaleRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    }
  }
  
  // update client
  if (info.client) {
    batch.update(db.collection('clients').doc(info.client.id), {
      totalPurchasedValue: fieldValue.increment(-info.finalPrice),
    })
  }
    
  // Delete all payment transactions
  if (info.payments) {
    for (const payment of info.payments) {
      if (payment.transactionId) {
        batch.delete(db.collection('transactions').doc(payment.transactionId))
      }
    }
  }
  if (info.transactionId) {
    batch.delete(db.collection('transactions').doc(info.transactionId))
  }
  if (info.secondTransactionId) {
    batch.delete(db.collection('transactions').doc(info.secondTransactionId))
  }
  
  // update dailyStats
  // ATL-1013: the last reversal site keying a bare, unguarded `info.dailyStatID`. A complex
  // sale opened from the operations list (or read back from Supabase) carries none, and
  // `doc(undefined)` throws before the batch is ever committed — so the sale could not be
  // deleted at all. `saveComplexSale` keys this doc with `getDailyStatID(store, createdDate)`
  // (ComplexSaleDetailScreen), which is exactly what the helper recomputes.
  const statFields = dailyStatIncrementsForComplexSales([info], true, true)
  const statDocName = dailyStatIDForRecord(info.dailyStatID, info.store, info.createdDate as DateLike)
  batch.update(db.collection('dailyStats').doc(statDocName), statFields)

  // write to updates
  // ATL-911: bind the ref BEFORE building the record, so the document's generated id is
  // part of it. An anonymous `.doc()` mints an id and discards it, which is why the
  // Supabase mirror landed with firestore_id NULL and its Firestore twin read as missing.
  const deleteComplexSaleUpdateRef = db.collection('updates').doc()
  const deleteComplexSaleUpdate = {
    firestoreId: (deleteComplexSaleUpdateRef as { id: string }).id,
    date: new Date(),
    cashierUID: uid,
    type: 'deleteComplexSale',
    dataBefore: filterUndefined(info as Record<string, unknown>)
  }
  batch.set(deleteComplexSaleUpdateRef, deleteComplexSaleUpdate)

  await batch.commit()

  // Mirror the audit-log entry to Supabase (best-effort)
  await dualWriteUpdate(deleteComplexSaleUpdate)

  // Dual write to Supabase via NestJS backend. ATL-1188: durable — a lost delete here is the
  // worst shape this system has, because the sale reappears in the customer's books at the read
  // cutover. A 404 stays free (record predates this org's dual-write).
  await bestEffortDualWriteDelete(
    'complexSales.delete',
    () => complexSalesApi.deleteComplexSale(complexSaleRef.id),
    { firestoreId: complexSaleRef.id },
  )
  // Also write to audit_log
  try {
    await auditLogApi.createAuditLog({
      tableName: 'complex_sales',
      recordId: complexSaleRef.id,
      operation: 'DELETE',
      oldValues: info,
      metadata: { cashierUID: uid, type: 'deleteComplexSale' },
    } as any);
  } catch (_) {
    // non-critical
  }
}

export const saveUpdatedItemsForComplexSale = async (db: FirestoreDb, info: any, updates: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveUpdatedItemsForComplexSale')
  const batch = rootDb.batch()
  const jotaiStore =  getDefaultStore()
  const allProductsDict = jotaiStore.get(productsDictAtom)
  const complexSaleRef = getFirestoreRef(info, 'complexSales')
  batch.update(complexSaleRef, sanitizeForFirestoreDeep(updates) as Record<string, unknown>)

  // update stocks for old items
  const storeStockField = 'storeStock.' + info.store
  for (const [index, value] of info.items.entries()) {
    if (value.isComposite && !value.manufacturedBeforeSale) {
      for (const [_idx, ingredient] of value.ingredients.entries()) {
        if (allProductsDict[ingredient.id]) {
          const increment = (value.quantity * ingredient.quantity)
          const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
          batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
          batch.set(ingredientRef.collection('movement').doc(), {
            productID: ingredient.id,
            type: 'editComplexSale',
            previousInStock: allProductsDict[ingredient.id]?.storeStock?.[info.store] || 0,
            increment,
            date: fieldValue.serverTimestamp(),
            linkedDocId: complexSaleRef.id,
            linkedDocDate: fieldValue.serverTimestamp(),
            clientSaveDate: new Date(),
            store: info.store,
            organizationId: info.organizationId
          })
        }
      }
    } else {
      if (!value.id.startsWith('custom') && allProductsDict[value.id]) {
        const increment = value.quantity
        const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
        batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(valueRef.collection('movement').doc(), {
          productID: value.id,
          type: 'editComplexSale',
          previousInStock: allProductsDict[value.id]?.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: complexSaleRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    }
  }

  // update stocks for new item
  for (const [index, value] of updates.items.entries()) {
    if (value.isComposite && !value.manufacturedBeforeSale) {
      for (const [_idx, ingredient] of value.ingredients.entries()) {
        const increment = 0 - (value.quantity * ingredient.quantity)
        const ingredientRef = getFirestoreRef(allProductsDict[ingredient.id] || ingredient, 'products')
        batch.update(ingredientRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(ingredientRef.collection('movement').doc(), {
          productID: ingredient.id,
          type: 'editComplexSale',
          previousInStock: ingredient.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: complexSaleRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    } else {
      if (!value.id.startsWith('custom')) {
        const increment = 0 - value.quantity
        const valueRef = getFirestoreRef(allProductsDict[value.id] || value, 'products')
        batch.update(valueRef, { [storeStockField]: fieldValue.increment(increment) })
        batch.set(valueRef.collection('movement').doc(), {
          productID: value.id,
          type: 'editComplexSale',
          previousInStock: value.storeStock?.[info.store] || 0,
          increment,
          date: fieldValue.serverTimestamp(),
          linkedDocId: complexSaleRef.id,
          linkedDocDate: fieldValue.serverTimestamp(),
          clientSaveDate: new Date(),
          store: info.store,
          organizationId: info.organizationId
        })
      }
    }
  }

  // update dailyStats
  const change = updates.finalExpensesPrice - info.finalExpensesPrice
  const statFields = {
    salesPurchaseAmount: fieldValue.increment(change), 
  }

  // ATL-1013: `ComplexSaleDetailScreen` hands this the SAME `detail` object it hands
  // `deleteComplexSale`, which this PR guarded — so leaving this bare made a complex sale
  // without a `dailyStatID` deletable but NOT editable: `doc(undefined)` throws before the
  // batch commits and the edit is lost. Same amount-delta class as `updateSaleWithStats`,
  // which this PR did convert. Keyed on `createdDate`, matching the screen's own
  // `getDailyStatID(store, createdDate)`. The saveNew* siblings stay bare on purpose —
  // they CREATE the doc from an id the screen just computed.
  batch.set(db.collection('dailyStats').doc(dailyStatIDForRecord(info.dailyStatID, info.store, info.createdDate as DateLike)), {...statFields,
    store: info.store,
    date: info.createdDate,
    organizationId: info.organizationId
  }, {merge: true})

  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(complexSaleRef.id, {
    items: updates.items,
    finalExpensesPrice: updates.finalExpensesPrice,
  }));
}

export const saveUpdatedFinalPriceForComplexSale = async (db: FirestoreDb, info: any, updatedData: any, uid: string) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveUpdatedFinalPriceForComplexSale')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(info, 'complexSales')
  batch.update(firestoreRef, sanitizeForFirestoreDeep(updatedData) as Record<string, unknown>)

  // update dailyStats
  const change = updatedData.finalPrice - info.finalPrice
  const statFields = {
    saleAmount: fieldValue.increment(change),
    [info.createdBy + 'SalesAmount']: fieldValue.increment(change),
  }

  // ATL-1013: as saveUpdatedItemsForComplexSale — deletable but not re-priceable.
  batch.set(db.collection('dailyStats').doc(dailyStatIDForRecord(info.dailyStatID, info.store, info.createdDate as DateLike)), {...statFields,
    store: info.store,
    date: info.createdDate,
    organizationId: info.organizationId
  }, {merge: true})

  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(firestoreRef.id, {
    finalPrice: updatedData.finalPrice,
  }));
}

export const updateComplexSale = async (ref: any, info: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateComplexSale')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'complexSales')
  batch.update(firestoreRef, sanitizeForFirestoreDeep(info) as Record<string, unknown>)
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('complexSales.update', () => complexSalesApi.updateComplexSale(firestoreRef.id, {
    status: info.status,
    comment: info.comment,
    ...(info.updates && { statusUpdates: info.updates }),
  }));
}

export const saveNewComplexSaleTemplate = async (db: any, info: any) => {
  const batch = rootDb.batch()
  const newRef = db.collection('constants').doc('complexSaleTemplates').collection('data').doc()
  batch.set(newRef, sanitizeForFirestoreDeep(info) as Record<string, unknown>)
  await batch.commit()

  // Dual write to Supabase via NestJS backend. The detail form builds `info` with
  // `templateName` (there is no `info.name`), so fall back to it — otherwise the
  // Supabase row is stored with name='' while Firestore keeps the real label.
  await bestEffortDualWrite('complexSaleTemplates.create', () => complexSaleTemplatesApi.createComplexSaleTemplate({
    firestoreId: newRef.id,
    name: info.name || info.templateName,
    items: info.items,
  } as any));

  return newRef
}

export const deleteComplexSaleTemplate = async (ref: any) => {
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const organization = jotaiStore.get(organizationAtom) as Organization & { firestoreId?: string }
  const orgId = organization?.firestoreId || organization?.id
  const docId = ref?.firestoreId || ref?.id
  const firestoreRef = rootDb.collection('organizations').doc(orgId).collection('constants').doc('complexSaleTemplates').collection('data').doc(docId)
  batch.delete(firestoreRef)
  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('complexSaleTemplates.delete', () => complexSaleTemplatesApi.deleteComplexSaleTemplate(ref.id), { firestoreId: ref.id });
}

export const updateComplexSaleTemplate = async (ref: any, info: any) => {
  const batch = rootDb.batch()
  const jotaiStore = getDefaultStore()
  const organization = jotaiStore.get(organizationAtom) as Organization & { firestoreId?: string }
  const orgId = organization?.firestoreId || organization?.id
  const docId = ref?.firestoreId || ref?.id
  const firestoreRef = rootDb.collection('organizations').doc(orgId).collection('constants').doc('complexSaleTemplates').collection('data').doc(docId)
  batch.update(firestoreRef, sanitizeForFirestoreDeep(info) as Record<string, unknown>)
  await batch.commit()

  // Dual write to Supabase via NestJS backend (see createComplexSaleTemplate: the
  // form provides `templateName`, not `name`).
  await bestEffortDualWrite('complexSaleTemplates.update', () => complexSaleTemplatesApi.updateComplexSaleTemplate(ref.id, {
    name: info.name || info.templateName,
    items: info.items,
  }));
}

export const getProductStockSnapshot = async (db: FirestoreDb, id: string) => {
  // Stock snapshots live ONLY in Firestore (`productStockSnapshots` collection,
  // written by utils/validationFunctions.js): there is no Supabase home, no
  // dual-write, and no migration for them. So this read is mode-independent —
  // always go to Firestore regardless of shouldUseSupabase(). (The old Supabase
  // branch hit a backend endpoint querying the non-existent
  // `product_stock_snapshots` table, which 404'd; swallowing that 404 also
  // masked real DB errors and silently zeroed ProfitStats' stock math.)
  const snap = await db.collection('productStockSnapshots').doc(id).get()
  return {...snap.data(), id: snap.id, ref: snap.ref}
}

export const updateUserLocale = async (ref: any, locale: string) => {
  const firestoreRef = getFirestoreRef(ref, 'users')
  await firestoreRef.update({locale})

  // Dual write to Supabase
  await bestEffortDualWrite('users.update', () => usersApi.updateUser(ref.id, { locale }))
}

// ATL-1966: each line is sent as Firestore stores it — the ordered product's snapshot plus
// `orderQuantity` — so Supabase holds the line SupplyOrderDetailScreen reads (`storeStock`,
// `productUnit`, `stockThreshold`, …) and writes back. It used to be reduced to
// {productId, productName, quantity, price}: the line the stock column crashed on, and the line a
// save then dropped (`toSavedOrderItems` keeps only lines carrying an `orderQuantity`). Each line goes
// through the Firestore write's own sanitizer (Timestamps → Date; `ref`, functions and undefined
// dropped), so both stores receive the same keys; a Date travels as its ISO instant, the encoding
// the read path returns.
const toSupplyOrderItems = (items: unknown): Record<string, unknown>[] =>
  (Array.isArray(items) ? items : []).map((line) => sanitizeForFirestoreDeep(line) as Record<string, unknown>)

// ATL-1962: the order's own date for `supply_orders.order_date`. `undefined` when the payload names
// none (an update that does not touch it). A date that is present but unreadable is refused BEFORE
// either write: sending null would erase the Supabase copy, and skipping it would leave the two
// stores disagreeing. SupplyOrderDetailScreen already refuses such a save (`dateUnreadable`).
const toSupplyOrderDate = (raw: unknown): string | undefined => {
  if (raw === undefined) return undefined
  const date = readSupplyOrderDate(raw)
  if (!date) throw new Error('Supply order date is not a readable instant')
  return date.toISOString()
}

// ATL-1103: the screen's `status` is the Firestore MAP ({id, isCompleted, text}) and the column
// is now `jsonb`, so it is sent WHOLE. This used to coerce it to `status.id` because
// `supply_orders.status` was `text` — the coercion that flattened 9 supply orders and, more
// importantly, taught the Supabase read path to hand a scalar back to a screen that writes it home.
//
// A bare id still passes through as a bare id: some rows never had a map, and wrapping one would
// invent a `text` nobody wrote.
const toSupplyOrderStatus = (status: any): Record<string, unknown> | string => {
  if (status && typeof status === 'object' && !Array.isArray(status)) return status
  return (typeof status === 'string' && status) || 'pending'
}

export const saveNewSupplyOrder = async (db: FirestoreDb, data: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#saveNewSupplyOrder')
  const orderDate = toSupplyOrderDate(data.date)
  const batch = rootDb.batch()

  // Create new supply order document. The raw items are full product objects with
  // function-valued fields, which Firestore's batch.set() rejects ('Unsupported field
  // value: a function') — sanitize first so the write (and thus the Supabase dual-write
  // below) actually runs. Keep all serializable fields so the detail view still has
  // orderQuantity/purchasePrice/etc.
  const newRef = db.collection('supplyOrders').doc()
  batch.set(newRef, {
    ...(sanitizeForFirestoreDeep(data) as Record<string, unknown>),
    createdAt: fieldValue.serverTimestamp(),
    createdBy: data.cashierUID,
  })

  await batch.commit()

  // Dual write to Supabase via NestJS backend: the lines and the date as Firestore holds them.
  await bestEffortDualWrite('supplyOrders.create', () => supplyOrdersApi.createSupplyOrder({
    firestoreId: newRef.id,
    storeId: data.store,
    supplierId: data.supplierID,
    items: toSupplyOrderItems(data.items),
    date: orderDate,
    status: toSupplyOrderStatus(data.status),
    // ATL-1103: the completion flag travels with the status. It was never sent at all, so a supply
    // order created as completed landed in Supabase as `is_completed = false`.
    isCompleted: supplyOrderIsCompleted(data.status) ?? data.isCompleted ?? false,
    expectedDate: data.expectedDate,
  }));

  return newRef
}

/**
 * @param options.seededFrom the order the caller's form was seeded from (SupplyOrderDetailScreen's
 *   `detail`). ATL-1041: the Firestore write then names only the fields the save changed, so a field
 *   the read transform lost or rendered lossily is never written over the Firestore order — the
 *   rollback copy. Absent, every field is written (`buildWriteBackDelta`'s unknown-baseline rule).
 */
export const updateSupplyOrder = async (
  db: FirestoreDb,
  ref: any,
  data: any,
  options: { seededFrom?: Record<string, unknown> | null } = {},
) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#updateSupplyOrder')
  const orderDate = toSupplyOrderDate(data.date)
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'supplyOrders')

  // Update supply order (sanitize: items can be full product objects with functions). The baseline
  // is sanitized the same way, so a Timestamp read back compares equal to the Date the form holds.
  const seededFrom = options.seededFrom
    ? (sanitizeForFirestoreDeep(options.seededFrom) as Record<string, unknown>)
    : null
  const supplyOrderDelta = buildWriteBackDelta(
    {
      ...(sanitizeForFirestoreDeep(data) as Record<string, unknown>),
      lastModifiedAt: fieldValue.serverTimestamp(),
    },
    seededFrom,
    { alwaysWrite: new Set(['lastModifiedAt']) },
  )
  batch.update(firestoreRef, supplyOrderDelta.update)

  await batch.commit()

  // Dual write to Supabase via NestJS backend: the lines, supplier and date as Firestore holds them.
  await bestEffortDualWrite('supplyOrders.update', () => supplyOrdersApi.updateSupplyOrder(firestoreRef.id, {
    items: data.items ? toSupplyOrderItems(data.items) : undefined,
    // ATL-1956: the supplier and the date never reached Supabase on an update. The supplier is
    // sent only when this save writes it to Firestore: the backend answers a supplier it cannot
    // resolve with a 404, and a 404 on an UNCHANGED supplier would fail the whole update — lines,
    // date, status — for a field the merchant never touched.
    supplierId: 'supplierID' in supplyOrderDelta.update ? data.supplierID : undefined,
    date: orderDate,
    status: data.status !== undefined ? toSupplyOrderStatus(data.status) : undefined,
    // ATL-1103: `is_completed` was never written by the update path, so a supply order completed
    // in the app stayed `false` in Supabase for ever while Firestore said true — the exact state
    // the flattened-status write-back destroys. Both halves have to move together.
    isCompleted:
      typeof data.isCompleted === 'boolean'
        ? data.isCompleted
        : (data.status !== undefined ? (supplyOrderIsCompleted(data.status) ?? undefined) : undefined),
    expectedDate: data.expectedDate,
    supplyId: data.supplyId,
  }));
}

export const deleteSupplyOrder = async (db: FirestoreDb, ref: any, data: any) => {
  assertWWriterAvailable('global/Firebase/firebase.ts#deleteSupplyOrder')
  const batch = rootDb.batch()
  const firestoreRef = getFirestoreRef(ref, 'supplyOrders')

  // Delete supply order
  batch.delete(firestoreRef)

  // Update connected supply
  if (data.supplyId) {
    batch.update(db.collection('supplies').doc(data.supplyId), {
      supplyOrderId: null
    })
  }

  await batch.commit()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('supplyOrders.delete', () => supplyOrdersApi.deleteSupplyOrder(firestoreRef.id), { firestoreId: firestoreRef.id });
}

// Not a supplier id in either key space (Firestore ids are 20 alphanumerics, Supabase ids UUIDs),
// so no ProductStocksScreen supplier row can read the Supabase orders grouped under it.
export const SUPABASE_UNDATED_SUPPLY_ORDERS_KEY = 'supabase:undated'

export const getActiveSupplyOrders = async (db: FirestoreDb, store: any) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      const params: { storeId?: string } = {}
      if (store) {
        params.storeId = store as unknown as string
      }

      // Backend returns ready-to-use Firestore-format orders (camelCase, dual IDs,
      // ref, resolved supplier lookup object). Pass through without manual mapping.
      const response = await supplyOrdersApi.getActiveSupplyOrders(params)

      // Grouped by `supplierID`, as the Firestore branch below groups — the key a supplier row reads
      // (`activeSupplyOrders[supplier.id]`: the supplier's Firestore id, or its UUID when it has no
      // Firestore twin, which is also what the read emits). The read emits it as `supplierKey`, and
      // `withSupplyOrderSupplierKey` puts it under `supplierID` for this bundle (ATL-1966).
      // ProductStocksScreen renders each order with `order.date.toDate()`, so an order whose date
      // did not reach Supabase (`date: null` until the ATL-1962 backfill) is kept under one key no
      // supplier row reads: calculateItemsOnTheWay still counts it, and no supplier row crashes on
      // it (ATL-1955).
      const grouped: Record<string, ReturnType<typeof withSupplyOrderSupplierKey<SupplyOrderResponse>>[]> = {}
      for (const order of response.map(withSupplyOrderSupplierKey)) {
        // `date` is a TimestampLike once the typed client has deserialised it; its schema type is the
        // wire string, so it is checked here rather than assumed.
        const date: unknown = order.date
        const supplierID = order.supplierID
        const key =
          typeof (date as { toDate?: unknown } | null)?.toDate === 'function' &&
          typeof supplierID === 'string' &&
          supplierID
            ? supplierID
            : SUPABASE_UNDATED_SUPPLY_ORDERS_KEY
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(order)
      }
      return grouped
    } catch (error: unknown) {
      console.error('Error fetching active supply orders from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'supplyOrders' },
        extra: {
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  try {
    const supplyOrdersRef = db.collection('supplyOrders')
    const snapshot = await supplyOrdersRef.where('isCompleted', '==', false).where('store', '==', store).get()
    const orders: Record<string, any[]> = {}

    snapshot.forEach((doc: any) => {
      const data = doc.data()
      if (!orders[data.supplierID]) {
        orders[data.supplierID] = []
      }
      orders[data.supplierID].push({
        ...data,
        id: doc.id,
        ref: doc.ref
      })
    })
    
    return orders
  } catch (error: unknown) {
    console.error('Error fetching supply orders:', error)
    Sentry.captureException(error)
    return {}
  }
}

export const savePrintTemplate = async (template: any) => {
  const db = getDefaultStore().get(dbAtom)
  const userId = getDefaultStore().get(userIdAtom)
  const organizationId = (getDefaultStore().get(organizationAtom) as Organization).id

  const templateData = {
    ...template,
    organizationId,
    updatedAt: fieldValue.serverTimestamp(),
    updatedBy: userId
  }

  const isNew = !template.id
  if (isNew) {
    // Create new template
    templateData.id = db.collection('printTemplates').doc().id
    templateData.createdAt = fieldValue.serverTimestamp()
    templateData.createdBy = userId
  }

  await db.collection('printTemplates').doc(templateData.id).set(templateData, {merge: true})

  // Dual write to Supabase via NestJS backend
  // Construct API payload explicitly - templateData has Firestore-specific fields (FieldValues, organizationId)
  // that the backend DTO doesn't accept (organizationId comes from auth guard, timestamps are server-generated)
  // Map Firestore field names to API field names: settings→content, store→stores
  const apiPayload = {
    name: templateData.name,
    type: templateData.type,
    content: templateData.settings || templateData.content,
    stores: templateData.stores || (templateData.store ? [templateData.store] : undefined),
    isDefault: templateData.isDefault,
  }
  if (isNew) {
    await bestEffortDualWrite('printTemplates.create', () => printTemplatesApi.createPrintTemplate({
      ...apiPayload,
      firestoreId: templateData.id,
    } as any));
  } else {
    await bestEffortDualWrite('printTemplates.update', () => printTemplatesApi.updatePrintTemplate(templateData.id, apiPayload as any));
  }

  return templateData
}

export const deletePrintTemplate = async (templateId: string) => {
  const db = getDefaultStore().get(dbAtom)
  await db.collection('printTemplates').doc(templateId).delete()

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('printTemplates.delete', () => printTemplatesApi.deletePrintTemplate(templateId), { firestoreId: templateId });
}

export const getPrintTemplates = async (type = null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      // Backend returns ready-to-use Firestore-format templates (camelCase, dual IDs,
      // ref). Use the typed client and pass through without manual transformApiItem.
      return await printTemplatesApi.getPrintTemplates(type ? { type } : undefined)
    } catch (error: unknown) {
      console.error('Error fetching print templates from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'printTemplates' },
        extra: {
          type,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  const db = getDefaultStore().get(dbAtom)
  const organizationId = (getDefaultStore().get(organizationAtom) as Organization).id
  
  let query = db.collection('printTemplates')
  
  if (type) {
    query = query.where('type', '==', type)
  }
  
  const snapshot = await query
    .orderBy('createdAt', 'desc')
    .get()
  
  return snapshot.docs.map((doc: any) => doc.data())
}

export const getPrintTemplatesByTypeAndStore = async (type: string, store: any = null) => {
  if (shouldUseSupabase()) {
    try {
      const jotaiStore = getDefaultStore()
      const organization = jotaiStore.get(organizationAtom) as Organization
      const organizationId = organization?.id

      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      // Typed client + pass-through (backend already returns Firestore-format).
      return await printTemplatesApi.getPrintTemplatesByTypeAndStore(
        store ? { type, storeId: store as string } : { type }
      )
    } catch (error: unknown) {
      console.error('Error fetching print templates by type and store from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'printTemplates' },
        extra: {
          type,
          store,
          errorMessage: (error as Error).message,
        },
      })
      throw error
    }
  }

  const db = getDefaultStore().get(dbAtom)
  
  const query = db.collection('printTemplates')
    .where('type', '==', type)
  
  const snapshot = await query
    .orderBy('createdAt', 'desc')
    .get()
  
  const templates = snapshot.docs.map((doc: any) => doc.data())

  // Filter by store: return global templates + store-specific templates
  if (store) {
    return templates.filter((template: any) =>
      !template.store || template.store === store
    )
  }
  
  return templates
}

// ============= Price Type Management =============

/**
 * Save price types configuration to organization constants (Firestore only)
 * Note: Supabase dual-write is handled by the individual add/update/delete functions
 * @param {Array} priceTypes - Array of price type configurations
 */
export const savePriceTypes = async (priceTypes: any[]) => {
  const db = getDefaultStore().get(dbAtom)
  const organizationId = (getDefaultStore().get(organizationAtom) as Organization).id

  await db.collection('constants').doc('priceTypes').set({
    data: priceTypes,
    organizationId,
    updatedAt: fieldValue.serverTimestamp()
  }, { merge: true })
}

/**
 * Add a new price type to organization
 * @param {Object} priceType - Price type configuration
 */
export const addPriceType = async (priceType: any) => {
  const constants = getDefaultStore().get(constantsAtom) as any
  const currentPriceTypes = constants?.priceTypes?.data || []

  // Ensure unique ID
  if (currentPriceTypes.some((pt: any) => pt.id === priceType.id)) {
    throw new Error('Price type with this ID already exists')
  }

  const updatedPriceTypes = [...currentPriceTypes, priceType]
  await savePriceTypes(updatedPriceTypes)

  // Dual write to Supabase via NestJS backend
  // ATL-1890: appended at the end of the array, so its position — the order — is the old length.
  await bestEffortDualWrite('priceTypes.create', () => priceTypesApi.createPriceType({
    ...priceType,
    firestoreId: priceType.id,
    displayOrder: currentPriceTypes.length,
  } as any));
}

/**
 * Update an existing price type
 * @param {string} priceTypeId - ID of price type to update
 * @param {Object} updates - Updates to apply
 */
export const updatePriceType = async (priceTypeId: any, updates: any) => {
  const constants = getDefaultStore().get(constantsAtom) as any
  const currentPriceTypes = constants?.priceTypes?.data || []

  const updatedPriceTypes = currentPriceTypes.map((pt: any) =>
    pt.id === priceTypeId ? { ...pt, ...updates } : pt
  )

  await savePriceTypes(updatedPriceTypes)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWrite('priceTypes.update', () => priceTypesApi.updatePriceType(priceTypeId, updates as any));
}

/**
 * Delete a price type from organization
 * @param {string} priceTypeId - ID of price type to delete
 */
export const deletePriceType = async (priceTypeId: any) => {
  const constants = getDefaultStore().get(constantsAtom) as any
  const currentPriceTypes = constants?.priceTypes?.data || []

  const updatedPriceTypes = currentPriceTypes.filter((pt: any) => pt.id !== priceTypeId)
  await savePriceTypes(updatedPriceTypes)

  // Dual write to Supabase via NestJS backend
  await bestEffortDualWriteDelete('priceTypes.delete', () => priceTypesApi.deletePriceType(priceTypeId), { firestoreId: priceTypeId, deletedFrom: constantsItemLocation('priceTypes') });
}
